// ...existing code...
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';
import { TypeData , Entity } from '../../../model/entity.model';
import { Page } from '../../../model/page.model';
import { FunctionListService } from '../../../services/function-list.service';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { PageService } from '../../../services/page.service';
import { VariableRegistryService } from './variable-registry.service';
import { EntityService } from '../../../services/entity.service';
import { BehaviorNavigationService } from '../../../services/behavior-navigation.service';
import { ToastService } from '../../../services/toast.service';
import { IconService } from '../../../services/icon.service';
import { StorageUrlCacheService } from '../../../services/storage-url-cache.service';
import { atlasIconEntries } from '../../../services/icon-library';

import prettier from "prettier";
import prettierPluginTypescript from "prettier/plugins/typescript";
import parserMarkdown from "prettier/plugins/markdown"; 
import prettierPluginEstree from "prettier/plugins/estree";

import { QueryFns } from './helpers/query-helper';
import { EntityVariableFns } from './helpers/entity-variable-healper';
import { AtlasFieldBridge } from './helpers/atlas-field-bridge';
import { PeopleBlockBridge, entitiesReferencingPeople } from './helpers/people-block-bridge';
import { pushFieldsOf } from './helpers/push-fields.util';
import { blockedGlobalNames, globalEntityRootName } from './helpers/global-import-gate';
import { registerNullConnectionChecker } from './helpers/null-connection-checker';
import { alignSavedVariableTypes, declaredEntityOf, pickerVariableType } from './helpers/variable-types.util';
import { CONDITIONAL_EXCLUDED_BLOCK_TYPES, CONDITIONAL_EXCLUDED_CATEGORIES } from './helpers/conditional-allowed-blocks';
import { SpecialFunctionsFns } from './helpers/special-functions-helper';
import { TimeStampFns } from './helpers/time-stamp-healper';
import { ConditionalFns } from './helpers/conditional-function-helper';
import { applyReturnValueCheck } from './helpers/value-function-helper';
import { LOCALFUNCTIONS } from '../../../model/page.model';
import { ForgePlan, ForgePhase, ForgeStep, PlanBlockStep, PlanBlockSegment, SegmentRefKind, PageContext, PlanCardMode, ForgeMessage, AtlasContext, ForgeContextRequest } from './helpers/forge.types';
import { computeVariableUsage, formatUsageSummary, VariableUsage } from './helpers/atlas-usage';
import { extractVariablesFromSteps, extractEntitiesFromSteps, swapVariableInSteps, summarizeForgeSteps, formatPlanAsText, swapInSteps, buildListItemsForRef } from './helpers/forge-plan-helpers';
import { createToolboxXml, createSandboxedToolboxXml } from './helpers/toolbox-xml';
import { registerAllMiscBlocks } from './helpers/misc-block-definitions';
import { registerConditionBlocks } from './helpers/condition-blocks';
import * as CoreVariableBlocks from './helpers/core-variable-blocks';
import { registerProcedureBlock } from './helpers/procedure-block';
import { registerApiFunctionBlocks } from './helpers/api-function-blocks';
import { fillFunctionSlots } from './helpers/function-toolbox';
import { FunctionInterface } from '../../../model/function.model';
import { registerAllBlocks } from './helpers/register-all-blocks';
import { enumerateConditionTargets, enumerateTargetFields, VALUE_CHECK_FOR_TYPE, FieldOption, conditionEffectSentence, selfIdFieldOption } from './helpers/condition-targets';
import { loadStepsToWorkspace, LoadResult } from './helpers/steps-to-blockly';
import { decompileWorkspace } from './helpers/blockly-to-steps';
import { buildPageContext } from './helpers/page-context-builder';
import { ForgeService } from '../../../services/forge.service';
import { AtlasAgentService } from '../../../services/atlas-agent.service';
import { AtlasComponent } from '../../../shared/components/atlas/atlas.component';
import { AtlasData, AtlasSelection, AtlasListItem, AtlasAddVariableEvent, AtlasImportEntityEvent, AtlasUploadIconEvent, AtlasIconEntry } from '../../../shared/components/atlas/atlas.types';
import { planVariableCreate, withEntityFields } from '../../../shared/components/atlas/variable-create.util';
import { prettifyKey, isSystemVariableKey as isSystemVariableKeyShared, SYSTEM_SET_VISIBLE_BLOCKS } from '../../../shared/components/atlas/atlas.utils';
import { isReferenceOrphaned } from './reference-crumb';
import { ReconOption, buildReconTree, reconTreeToAtlasData, enumerateAtlasVarsForRecon } from '../../../shared/components/atlas/atlas-reconstruct.util';
import { isGeometry } from '../../../shared/geometry/geometry.util';
import { isSystemEntityRef } from '../../../services/system-entity.util';
import { followsReference } from '../../../services/reference-path.util';
import { enterReference } from '../../../shared/entity/entity-shape.util';
import { resolvedShapeOf, withResolvedShapes } from '../../../shared/entity/resolved-entity-list.util';

/**
 * The scalar types the unified `set_variable` / `get_variable` picker offers, in AOB spelling.
 */
export const SCALAR_PICKER_TYPES = ['STRING', 'NUMBER', 'BOOLEAN', 'TIMESTAMP', 'GEOMETRY'];

/**
 * The same set in BLOCKLY spelling — the `variable.type` a workspace variable actually carries.
 *
 * A `get_variable` plugged into a `set_variable` narrows its picker to the set's type. A type
 * missing from this list does not narrow, so the get silently offers EVERY scalar: you could put a
 * String into a geometry set and nothing would stop you. GEOMETRY was missing until 2026-09-10.
 *
 * These two lists must stay in step under toUpperCase — asserted in
 * blockly-scalar-types.spec.ts, because the failure is silent and permissive rather than loud.
 */
export const NARROWABLE_BLOCKLY_TYPES = ['String', 'Number', 'Boolean', 'TimeStamp', 'GEOMETRY'];



@Component({
    selector: 'app-blockly-interface',
    imports: [CommonModule, FormsModule, AtlasComponent],
    templateUrl: './blockly-interface.component.html',
    styles: [`
      @keyframes trace { 0% { stroke-dashoffset: 400; } 100% { stroke-dashoffset: -400; } }
      @keyframes shimmer { 0%,100% { opacity: 0.3; } 50% { opacity: 0.6; } }
      @keyframes code-slide { 0% { transform: translateX(-8px); opacity: 0; } 30% { opacity: 0.5; } 70% { opacity: 0.5; } 100% { transform: translateX(8px); opacity: 0; } }
      .trace-line { stroke-dasharray: 30 370; animation: trace linear infinite; }
      .code-bar { animation: code-slide ease-in-out infinite; }
      :host ::ng-deep .overflow-y-auto::-webkit-scrollbar,
      :host ::ng-deep .overflow-auto::-webkit-scrollbar { width: 6px; }
      :host ::ng-deep .overflow-y-auto::-webkit-scrollbar-track,
      :host ::ng-deep .overflow-auto::-webkit-scrollbar-track { background: transparent; }
      :host ::ng-deep .overflow-y-auto::-webkit-scrollbar-thumb,
      :host ::ng-deep .overflow-auto::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }
      :host ::ng-deep .overflow-y-auto::-webkit-scrollbar-thumb:hover,
      :host ::ng-deep .overflow-auto::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }
    `]
})
export class BlocklyInterfaceComponent {
  createToolboxXml(): string {
    return createToolboxXml();
  }

  pageData: Page = {
    type: 'web',
    name: 'functions',
    id: 'functions',
    sections: [],
    entites: [],
    localVariable: {
      defaultFlag: false,
      defaultString: '',
      defaultNumber: 0,
      defaultTimeStamp: '2000-01-01T00:00:00.000Z',
    },
    functions: [],
    conditionalFunctions: [],
  };
  /**
   * Which lane this workspace is editing.
   *   'function'    — page.functions[]            (async, output handlers)
   *   'conditional' — page.conditionalFunctions[] (sync, boolean)
   *   'value'       — page.valueFunctions[]       (sync, typed scalar for an input binding)
   */
  editorMode: 'function' | 'conditional' | 'value' = 'function';

  /** Kept so the existing `isConditionalMode` reads and the template stay untouched. */
  get isConditionalMode(): boolean { return this.editorMode === 'conditional'; }
  set isConditionalMode(v: boolean) { this.editorMode = v ? 'conditional' : 'function'; }

  get isValueMode(): boolean { return this.editorMode === 'value'; }

  /** allowedType of the binding this value workspace was opened for; drives the return socket. */
  valueAllowedType: string | undefined;
  fullToolboxXml = '';
  conditionalToolboxXml = '';
  valueToolboxXml = '';
  entityList : Entity[] = [];
  pageList: Page[] = [];
  @ViewChild('blocklyDiv') blocklyDiv: ElementRef | undefined;
  @ViewChild('codeEditor', { static: false }) codeEditor!: ElementRef;
  code: string = '';
  workspace!: Blockly.WorkspaceSvg;

  showCode = true;
  showBlockly = true;

  constructor(
    private router: Router,
    public pageService: PageService,
    public entityService: EntityService,
    public globalVariablesService: GlobalVariablesService,
    public functionsService: FunctionListService,
    private forgeService: ForgeService,
    // v3: Forge takes an atlasContext, so a Forge run now BEGINS with an Atlas selection. See
    // resolveAtlasContext() below and docs/plans/forge-atlas-ask-back-loop.md.
    private atlasAgentService: AtlasAgentService,
    public behaviorNav: BehaviorNavigationService,
    private toastService: ToastService,
    private variableRegistryService: VariableRegistryService,
    private iconService: IconService,
    private storageUrlCache: StorageUrlCacheService,
  ) {
    this.pageData = this.pageService.selectedPage;
    if (!this.pageData.conditionalFunctions) {
      this.pageData.conditionalFunctions = [];
    }
    this.pageService.$values.subscribe((value: any) => {
      this.pageList = value;
    });
        this.entityService.$values.subscribe((value: any) => {
      this.entityList = value;
        // Which entities a Transfer block can offer (people-block-bridge.ts).
        PeopleBlockBridge.referencingEntities = entitiesReferencingPeople(value || []);
    });


  }
  entityIdForGetEntity = '';
  /**
   * What the LAST `enumerateVariablesForRecon` call withheld because of the import gate.
   *
   * Carried so `openAtlasForBlock` can say WHY a panel is empty. Hiding these is correct — the
   * workspace has no token for them — but an empty picker with no explanation is the failure mode this
   * component already met once and answered with a toast rather than silence (the `allowedType === 'NAN'`
   * branch). The remedy is one click away in the same overlay: Atlas can import an entity
   * (`onAtlasImportEntity`), which is why naming the entity is worth the line.
   */
  reconWithheldByImport: { count: number; entityIds: Set<string> } = { count: 0, entityIds: new Set() };
  standaloneMode = false;

  QueryFns = QueryFns;
  EntityVariableFns = EntityVariableFns;
  SpecialFunctionsFns = SpecialFunctionsFns;
  TimeStampFns = TimeStampFns;

  ngAfterViewInit() {


    /* → moved to helpers/misc-block-definitions.ts (registerAllMiscBlocks) */
    // Clicking a VAR dropdown on any block opens Atlas via the SAME action as the side-panel "swap
    // variable" button (openAtlasForBlock, gated on selectedBlockFlag). Deferred so the workspace
    // SELECTED handler — which sets the per-block Atlas context — has run first.
    AtlasFieldBridge.open = (_sourceBlock: any) => {
      setTimeout(() => { if (this.selectedBlockFlag) this.openAtlasForBlock(); }, 0);
    };
    // On-attach same-entity guard (2026-07-10): the entity setter blocks call this from their
    // setOnChange (on FINISHED_LOADING + a live connect). See docs/blockly/blocks/_VERIFICATION.md.
    AtlasFieldBridge.validateEntityChild = (setBlock: any) => this.validateEntityChildMatch(setBlock);
    // The guard INSTALLER also lives here (not in a helper): block inits call it via the bridge, so the
    // guard logic is centralized in this component and no helper owns it / imports another helper.
    AtlasFieldBridge.attachEntityGuard = (block: any) => this.installEntityGuard(block);
    this.newBlocklyFunction();
    // ── THE BLOCK SET ────────────────────────────────────────────────────────────────────────────
    // ONE SOURCE OF TRUTH. The ~80 registration calls that used to be inlined here now live in
    // helpers/register-all-blocks.ts, in the SAME order, so the editor and the headless generation
    // pipeline register an identical block set from identical code. Order is load-bearing: push-V2
    // must follow the helper it overrides — see that file.
    //
    // The three things it deliberately does NOT do stay here: the AtlasFieldBridge wiring (UI handlers,
    // above), newBlocklyFunction (injects into a DOM div, above), and createDefinitionsForFunctions
    // (async + service-driven, below). See docs/plans/background-generation-pipeline.md.
    registerAllBlocks(this);
    this.createDefinitionsForFunctions();

    if (this.behaviorNav.pending) {
      this.standaloneMode = true;
      const pending = this.behaviorNav.pending;
      if (pending.mode === 'value') {
        this.editorMode = 'value';
        this.valueAllowedType = pending.allowedType;
      } else if (pending.mode === 'conditional' || pending.isConditional) {
        this.editorMode = 'conditional';
      }
      const waitForWorkspace = () => {
        if (this.workspace) {
          // Switch to restricted toolbox AFTER workspace is injected
          if (this.editorMode === 'conditional' && this.conditionalToolboxXml) {
            (this.workspace as any).updateToolbox(this.conditionalToolboxXml);
          } else if (this.editorMode === 'value' && this.valueToolboxXml) {
            (this.workspace as any).updateToolbox(this.valueToolboxXml);
          }
          this.selectFunction(pending.functionName);
          // AFTER selectFunction, so the restored return_value block exists to be re-checked.
          if (this.editorMode === 'value') {
            applyReturnValueCheck(this.workspace, this.valueAllowedType);
          }
        } else {
          setTimeout(waitForWorkspace, 100);
        }
      };
      setTimeout(waitForWorkspace, 300);
    }
  }

  clearWorkSpace() {
    if (this.workspace) {
      Blockly.Events.disable();
      try {
        const allBlocks = this.workspace.getAllBlocks();
        for (const block of allBlocks) {
          if (!block.disposed) {
            block.dispose(true, true);
          }
        }
      } finally {
        Blockly.Events.enable();
      }
    }
  }


  resolveEntityIdByEntityAndVariable(entityName: string ) {
    var entitySplit : any = [];

      entitySplit = entityName.split('%');
      var entityId = '';
      var entitySplitStartVariable = 1;
    if(entityName.includes('local_variable')) {
        entityId = this.pageData.localVariable[entitySplit[1]].entityId;
        entitySplitStartVariable = 2;
    } else if(entityName.includes('global_variable')) {

        entityId = this.globalVariablesService.globalVariable[entitySplit[1]].entityId;
        entitySplitStartVariable = 2;
    } else {
      for(let i=0 ; i < this.pageService.newEntityListForSelected.length; i++) {
        const regex = new RegExp(`(?:(?<=[$%])|^)${this.pageService.newEntityListForSelected[i].id}(?:(?=[$%])|$)`);

        const found = regex.test(entityName);
        // if(entityName.includes(this.pageService.newEntityListForSelected[i].id) ){
        if(found){
          entityId = this.pageService.newEntityListForSelected[i].id;
          break;
        }
      }
    }
    for(let j=0 ; j < this.entityList.length ; j++ ) {
      if(this.entityList[j].id == entityId) {
        let fields: TypeData[] = this.entityList[j].data || [];
      for(let k = entitySplitStartVariable ; k< entitySplit.length ; k++) {
        for(let l = 0 ; l < fields.length ; l++) {
          if(fields[l].name == entitySplit[k]) {
            entityId = fields[l].referenceEntityName;
            // Was (2026-10-29): entity = entity.data[l];  (the next hop read a reference's stored copy)
            fields = this.fieldsBelow(fields[l]);
            break;
          } 
        }
      }
        break;
      }
    }
    return entityId;
}

  /**
   * The fields one step down a field path: a reference's target entity as it is now, a list's own row fields. A copy
   * of the target's fields stored inside the reference may be empty or stale, so it is never read.
   */
  private fieldsBelow(field: TypeData): TypeData[] {
    if (field?.type === 'ENTITY_REFERENCE') {
      return this.entityList.find((e) => e.id === field.referenceEntityName)?.data || [];
    }
    return field?.data || [];
  }

  /**
   * The entity an entity_id_conditions block's parent names, '<field>$<entity id>%<hop>%…' or an entity id alone;
   * null when the path names nothing, which leaves the picker as it was.
   */
  entityIdForConditionParent(parentValue: string): { id: string } | null {
    let named: { id: string } | null = null;
    const splitOnParentValueVariable = parentValue.split('$')[0];
    const splitOnParentValueEntity = parentValue.split('$')[1] ?? '';
    const splitOnParentValueEntityByPrecentage = splitOnParentValueEntity.split('%');
    let entityFoundFlag = false;
    let entityData: TypeData[] = [];
    for(let x = 0 ; x < splitOnParentValueEntityByPrecentage.length ; x++) {
      if(entityFoundFlag) {
        for(let y =0 ; y < entityData.length ; y++) {
          const splitOnParentValueEntityByPrecentageByUnderscore = splitOnParentValueEntityByPrecentage[x].split('_');
            if(splitOnParentValueEntityByPrecentageByUnderscore[splitOnParentValueEntityByPrecentageByUnderscore.length - 1] == entityData[y].name) {
              // Was (2026-10-29): entityData = entityData[y].data;  (a reference's stored copy)
              entityData = this.fieldsBelow(entityData[y]);
              break;
            }
        }
      } else if( x !==0 ) {
        console.error('parent entity not found  in entity list');
      } else {
        for(let y =0 ; y < this.entityList.length ; y++) {
          if(splitOnParentValueEntityByPrecentage[x] == this.entityList[y].id) {
            entityFoundFlag = true;
            entityData = this.entityList[y].data;
          }
        }
      }
    }
    for(let y =0 ; y < entityData.length ; y++) {
      if(entityData[y].name == splitOnParentValueVariable) {
        named = { id: entityData[y].referenceEntityName };
        break;
      }
    }
    if(splitOnParentValueEntity == '') {
      for(let y =0 ; y < this.entityList.length ; y++) {
        if(splitOnParentValueVariable == this.entityList[y].id) {
          named = { id: this.entityList[y].id };
          break;
        }
      }
    }
    return named;
  }
  //Start initialize the Blockly Interface
  eventSelected = false;
  selectedBlockFlag = false;
  selectedBlock: any;
  presentBlockVariable ='';
  topEntitySelectionOnlyFlag = false;
  entityReferenceSelectionListForCRUD : any[] = [];
  functionList : any = [];
  async newBlocklyFunction() {
    var toolbox = this.createToolboxXml();
    // 500, not query()'s default page of 15: a project with more functions and flows than that would lose blocks.
    this.functionList = await this.functionsService.query(500);
    toolbox = fillFunctionSlots(toolbox, this.functionList ?? []);
    this.fullToolboxXml = toolbox;
    this.conditionalToolboxXml = this.buildConditionalToolboxXml(toolbox);
    this.valueToolboxXml = this.buildValueToolboxXml(toolbox);
    // Sandboxed landing page (Track C): restrict the palette to values + localVariables + the Auth
    // blocks. Setting it here means every later updateToolbox(fullToolboxXml/conditional) inherits it.
    if (this.isCurrentPageSandboxed()) {
      toolbox = createSandboxedToolboxXml();
      this.fullToolboxXml = toolbox;
      // FIX (2026-08-22): previously `conditionalToolboxXml = toolbox` — the RAW sandboxed palette,
      // which has NO return block (so a conditional could never answer) and DOES offer auth_login /
      // auth_signup_found / async_function_wrapper / resolve_promise_by_group, all of which emit
      // `await`, which getConditionalFn refuses by caching a permanent `false`. Skipping the strip
      // here defeated the entire point of the exclusion list on exactly the pages that need it most.
      // Both restricted lanes now go through the same strip on sandboxed pages too.
      // FOLLOW-UP (2026-09-02): routing through the strip was necessary but not sufficient — a strip
      // only removes what its lists NAME, and auth_login / auth_signup_found were in neither list, so
      // the Auth category survived into both restricted palettes anyway. They are now excluded by
      // block type AND by category in conditional-allowed-blocks.ts, which is what makes the sentence
      // above actually true.
      this.conditionalToolboxXml = this.buildConditionalToolboxXml(toolbox);
      this.valueToolboxXml = this.buildValueToolboxXml(toolbox);
    }
    registerNullConnectionChecker();
    this.workspace = Blockly.inject(this.blocklyDiv?.nativeElement, {
      readOnly: false,
      plugins: { connectionChecker: 'zavoNullChecker' },
      // ── 0-BASED, deliberately (2026-08-08) ────────────────────────────────────────────────────────────
      // Blockly defaults `oneBasedIndex` to TRUE, which made this platform index two different ways at
      // once. Our OWN blocks were always 0-based — `set_entity_variable_by_index_of_entity_list` emits a
      // raw `value[i]` (entity-variable-healper.ts:214), and so do the AOB cursor, delete-from-list and
      // push-at-index. Only the three stock Blockly text blocks in the toolbox — text_charAt,
      // text_getSubstring, text_indexOf — went through `getAdjusted`, which subtracts 1 when this flag is
      // on and emits `charAt(i - 1)`. Same workspace, two bases, decided by which block you happened to
      // pick.
      //
      // Forge found it the way a person would: it wrote a 0-based character loop (`from 0 to length - 1`)
      // against a 1-based charAt, which reads "" first and silently drops the last character. Nothing
      // errors. The alternative was to document the split and ask every author — human or model — to
      // classify each call by block family before choosing a base; a per-call judgement with two plausible
      // answers is a coin flip, so the platform gets one base instead.
      //
      // EXISTING FUNCTIONS KEEP WORKING, because generated JS is STORED on the function (`code`) and frozen
      // at save time — a 1-based function runs its 1-based `code` and never re-reads this flag. The eleven
      // functions estate-wide that use these three blocks were DELIBERATELY NOT converted (2026-08-08):
      // re-authoring live client logic from inferred intent is a bigger risk than the one it removes, and
      // those pages are being reworked separately. The residual hazard is therefore real and accepted —
      // re-saving one of those eleven regenerates its `code` under this flag and shifts it by one, silently.
      // They are listed in docs/blockly/zero-based-index.md so the next person meets the list, not the bug.
      oneBasedIndex: false,
      toolbox,
      grid: {
        spacing: 20,
        length: 3,
        colour: '#ccc',
        snap: true,
      },
      trashcan: true,
      zoom: {
        controls: true,
        wheel: true,
        startScale: 1,
        maxScale: 3,
        minScale: 0.3,
        scaleSpeed: 1.2,
      },
    });
    var returnMsg = 'Return To Dashboard';
    Blockly.Msg['RENAME_VARIABLE'] = returnMsg;
    Blockly.Msg['DELETE_VARIABLE'] = returnMsg;
    Blockly.Msg['VARIABLES_DEFAULT_NAME'] = '_0_';

    javascriptGenerator.init(this.workspace);
    // Optional: Disable adding new procedures_defnoreturn blocks
    // Blockly.Blocks['procedures_defnoreturn'].disable();

    // Optional: Add event handlers or additional configuration here
    this.workspace.addChangeListener(
      async (event: Blockly.Events.Abstract | any) => {
        if (
          event.type == Blockly.Events.VAR_RENAME ||
          event.type == Blockly.Events.VAR_DELETE
        ) {
          this.router.navigate(['/dashboard/pages']);
          return;
        }
        if (
          event.type === Blockly.Events.BLOCK_CHANGE ||
          event.type === Blockly.Events.CREATE ||
          event.type === Blockly.Events.MOVE ||
          event.type === Blockly.Events.CHANGE ||
          event.type === Blockly.Events.DELETE
        ) {
          this.updateCode();
        }
        if (event.type === Blockly.Events.SELECTED) {
          this.selectedBlockFlag = false;
          this.entityReferenceSelectionListForCRUD = [];
          this.topEntitySelectionOnlyFlag = false;
          this.eventSelected = true;
          this.variableToBeAdded.allowedType = '';

          var block = this.workspace.getBlockById(event['newElementId']);
          if (block) {
            this.presentBlockVariable = '';
            if(block.getFieldValue('VAR'))
              this.presentBlockVariable = block.getFieldValue('VAR');
            // for ABO parent entity
              this.AOBToBeAddedEntity = [] ;
              this.AOBToBeAddedAllowedType = '';
              // for AOB parent entity
            // Get the tag name (type) of the selected block
            this.selectedBlock = block;
            // Get the ID of the selected block
            // var blockId = block.id;
            // console.log(block.type);
            if (block.type == 'set_boolean_variable' || block.type == 'get_boolean_variable') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'BOOLEAN';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'get_string_variable' || block.type == 'set_string_variable') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'STRING';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'get_time_stamp_variable' || block.type == 'set_time_stamp_variable') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'TIMESTAMP';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'get_number_variable' || block.type == 'set_number_variable') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'NUMBER';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'set_variable' || block.type == 'get_variable') {
              // unified type-driven blocks: Atlas picker lists ALL scalar vars;
              // the block adopts the picked variable's type (colour/check follow).
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'SCALAR';
            } else if (block.type == 'set_aob_variable' || block.type == 'get_aob_variable') {
              // unified AOB blocks: reuse the typed-AOB two-step Atlas flow, but with a
              // SCALAR field filter (step-2 lists every typed field; type follows the pick).
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = 'SCALAR';
              const parts = this.convertBackToVaribleFromFielValue(
                block.getFieldValue('VAR')
              )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
            }   else if (block.type == 'get_reference_entity_variable_for_AOB_variable_string' || block.type == 'set_reference_entity_variable_for_AOB_variable_string') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = 'STRING';
              const parts = this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
                
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
   
            } else if (block.type == 'get_reference_entity_variable_for_AOB_variable_time_stamp' || block.type == 'set_reference_entity_variable_for_AOB_variable_time_stamp') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = 'TIMESTAMP';
              const parts = this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
                
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
   
            }   else if (block.type == 'get_reference_entity_variable_for_AOB_variable_number' || block.type == 'set_reference_entity_variable_for_AOB_variable_number') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = 'NUMBER';
              const parts = this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
                
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
   
            }  else if (block.type == 'get_reference_entity_variable_for_AOB_variable_boolean' || block.type == 'set_reference_entity_variable_for_AOB_variable_boolean') {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = 'BOOLEAN';
              const parts = this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
                
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
   
            } else if (block.type == 'get_reference_entity_variable_for_AOB_reference_AOB' ) {
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
                this.AOBToBeAddedAllowedType  = 'ENTITY_LIST_VARIABLE';
              const parts = this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0].split('$');
              this.variableToBeAdded.variable = parts[0];
              this.AOBToBeAddedVariable = parts[1] || '';
                
              this.variableToBeAdded.entity = 'entity_reference_for_AOB';
                if(block.getParent()?.type == 'set_reference_entity_variable_for_AOB' || block.getParent()?.type == 'push_new_element_to_array') {

                   const value = block.getParent()?.getFieldValue('VAR');

              for(let p = 0 ; p < this.pageService.entityListReferenceEntity.length ; p++) {
                for(let key of this.getKeys(this.pageService.entityListReferenceEntity[p].data)) {
                  if(this.getType(this.pageService.entityListReferenceEntity[p].data[key]) == 'ENTITY_LIST_VARIABLE') {
                    if(block.getParent()?.type == 'push_new_element_to_array' || (value == 'entity_reference_for_AOB$' + this.pageService.entityListReferenceEntity[p].id + '%' + key)) {
                      this.AOBToBeAddedEntity.push({id: this.pageService.entityListReferenceEntity[p].id + '$$' + key, 
                        name: this.pageService.entityListReferenceEntity[p].name + '--' + key
                      });
                    }
                  }
                }
              //   for(let q =0 ; q < this.pageService.entityListReferenceEntity[p].data.length; q++) {
              //     if(this.pageService.entityListReferenceEntity[p].data[q].type == "ARRAY_OF_OBJECT") {
              //       console.log(this.pageService.entityListReferenceEntity[p].data[q]);
              //       for(let r = 0 ; r < this.entityList[p].data[q].data.length; r++) {

              // if(block.getParent()?.type == 'push_new_element_to_array') {
              //    if(this.entityList[p].data[q].data[r].type == 'ENTITY_REFERENCE') {
              //     for(let s = 0 ; s < this.entityList[p].data[q].data[r].data.length ; s++) {
              //       if(this.entityList[p].data[q].data[r].data[s].type == "ARRAY_OF_OBJECT") {
              //                       this.AOBToBeAddedEntity.push({id: this.entityList[p].id + '%' + this.entityList[p].data[q].name + '$.' + this.entityList[p].data[q].data[r].name + '.' + this.entityList[p].data[q].data[r].data[s].name,
              //   name: this.entityList[p].id + ' -- ' + this.entityList[p].data[q].name + ' -- ' + this.entityList[p].data[q].data[r].name + ' -- ' + this.entityList[p].data[q].data[r].data[s].name
              //                 });
              //       }
              //     }
              //   }
              // } else {
              //  const regex = new RegExp(`(?:(?<=[$%])|^)${this.entityList[p].data[q].data[r].referenceEntityName}(?:(?=[$%])|$)`);

              //  const found = regex.test(value);
              //                 if(found && this.entityList[p].data[q].data[r].name !== "parent_id") {

              // var splitByPercentile = value.split('%');

              // this.AOBToBeAddedEntity.push({id: this.entityList[p].id + '%' + this.entityList[p].data[q].name + '$.' + this.entityList[p].data[q].data[r].name + '.' + splitByPercentile[1],
              //   name: this.entityList[p].id + ' -- ' + this.entityList[p].data[q].name + ' -- ' + this.entityList[p].data[q].data[r].name + ' -- ' + splitByPercentile[1]
              //                 });
              //  }
              // }
              //       }
              //      }

              //   }


              }
              if(this.AOBToBeAddedEntity.length == 0) {
                this.variableToBeAdded.allowedType = 'NAN';
              // Was: alert('No variable present for The AOB in set reference'); — a blocking browser alert that fired
              // during onchange (every block-change event), halting the whole editor. Removed 2026-07-13; the
              // "no compatible AOB" feedback now surfaces as a subtle toast at click time (the 'NAN' guard in
              // openAtlasForBlock), not as a modal that stops everything.
              }
                }
   
            }  else if (block.type == 'get_entity_variable') {
              this.entityIdForGetEntity = '';
              if (
                (block.getParent()?.type == 'set_entity_variable' &&
                  block.getParent()?.getFieldValue('VAR') !== 'NAN') ||
                (block.getParent()?.type == 'update_entity' || block.getParent()?.type.includes('_api_functions'))
              ) {
                if (block.getParent()?.type == 'set_entity_variable') {
                  const value = block.getParent()?.getFieldValue('VAR');
                  if (value.includes('localVariable$')) {
                    for (let key of this.getKeys(this.pageData.localVariable)) {
                      if (
                        this.getType(this.pageData.localVariable[key]) ==
                        'ENTITY_VARIABLE'
                      ) {
                        if (key == value.replace('localVariable$', '')) {
                          this.entityIdForGetEntity =
                            this.pageData.localVariable[key].entityId;
                          this.variableToBeAdded.entity = 'local_variable';
                        }
                      }
                    }
                  } else if (value.includes('globalVariable$')) {
                    for (let key of this.getKeys(
                      this.globalVariablesService.globalVariable
                    )) {
                      if (
                        this.getType(
                          this.globalVariablesService.globalVariable[key]
                        ) == 'ENTITY_VARIABLE'
                      ) {
                        if (key == value.replace('globalVariable$', '')) {
                          this.entityIdForGetEntity =
                            this.globalVariablesService.globalVariable[
                              key
                            ].entityId;
                          this.variableToBeAdded.entity = 'global_variable';
                        }
                      }
                    }
                  } else { 
                    if(value.includes('$$')) {
                      this.variableToBeAdded.entity = value.split('$$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$$')[0] + '%' + value.split('$$')[1]);
                    } else {
                      this.variableToBeAdded.entity = value.split('$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$')[0] + '%' +value.split('$')[1]);
                    }
                  }
                } else if (block.getParent()?.type.includes('_api_functions')) {
                 const parentBlock = block.getParent();
                  const targetConn =
                    block.outputConnection?.targetConnection ||
                    block.previousConnection?.targetConnection;

                  let inputName: string | undefined;
                  if (parentBlock && targetConn) {
                    const input = parentBlock.inputList.find(
                      i => i.connection === targetConn
                    );

                    inputName = input?.name;
                    console.log(inputName);
                  }
                  // i want replace _api_functions with '' to get the function name
                  const functionName = block
                    .getParent()
                    ?.type.replace('_api_functions', '');
                  const selectedFunction = this.functionList?.find(
                    (ele: any) => ele.name === functionName
                  );
                //   inputs or outputs of parent block
                  const input = selectedFunction?.inputs.find(
                    (ele: any) => ele.name === inputName
                  ) || selectedFunction?.outputs.find(
                    (ele: any) => ele.name === inputName
                  );
                  this.entityIdForGetEntity = input?.entityReference || '';

               } else {
                  this.entityIdForGetEntity = '$any$';
                  this.variableToBeAdded.entity =
                    this.convertBackToVaribleFromFielValue(
                      block.getFieldValue('VAR')
                    )[1];
                    if (block.getParent()?.type == 'update_entity') { 
                      this.topEntitySelectionOnlyFlag = true;
                    }
                }
                this.selectedBlockFlag = true;
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
              } else if (block.getParent()?.type == 'meta_page_subscription') {
                this.selectedBlockFlag = true;
                // Facebook page leads block (B4): only records of the Meta pages entity make sense here.
                this.entityIdForGetEntity = 'systemOotbMetaPages';
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(block.getFieldValue('VAR'))[1];
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(block.getFieldValue('VAR'))[0];
              } else if (block.getParent()?.type == 'get_current_user' || block.getParent()?.type == 'edit_user') {
                          this.selectedBlockFlag = true;
                // user-management migration (feat/user-management): user entity is now systemOotbUser
                // (Postgres-backed, single roleId), superseding the legacy user_global definition.
                this.entityIdForGetEntity = 'systemOotbUser';
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];

              } else if (block.getParent()?.type == 'entity_reference_selection') {
                          this.selectedBlockFlag = true;
                          const value = block.getParent()?.getFieldValue('VAR');
                          const cleaned = value.replace('_for_CRUD', '');
                  this.entityIdForGetEntity = cleaned.slice(cleaned.lastIndexOf('%') + 1);
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];

              } else if (block.getParent()?.type == 'create_entity') {

                      this.topEntitySelectionOnlyFlag = true;
                          this.selectedBlockFlag = true;
                  this.entityIdForGetEntity = block.getParent()?.getFieldValue('VAR');
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];

              } else if (block.getParent()?.type == 'entity_id_conditions') {
                  if(block.getParent()?.getParent()?.type == 'entity_set_id_in_conditions') {
                      // this.topEntitySelectionOnlyFlag = true;
                          this.selectedBlockFlag = true;
                 let parentValue  = block.getParent()?.getParent()?.getFieldValue('VAR');
                 if(parentValue.startsWith('$')) {
                  parentValue = parentValue.slice(1);
                 }
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
                  // Was (2026-10-29): entityIdForConditionParent's walk ran inline here, its hops through a reference's stored copy.
                  const named = this.entityIdForConditionParent(parentValue);
                  if (named) this.entityIdForGetEntity = named.id;

                }
              } else if (block.getParent()) {
                const type = block.getParent()?.type;
                // functionList is the full list Blockly loaded; a fresh query() would return one page of 15 and re-publish it to every subscriber.
                const loadedFunctions: FunctionInterface[] | undefined = this.functionList;
                const selectedFunction = loadedFunctions?.find((ele) => ele.name === type);
                const connection = block
                  .getParent()
                  ?.getConnections_(true)
                  .find((ele) => ele.targetBlock() == block);
                const parentInputName = connection?.getParentInput()?.name;
                const input = selectedFunction?.inputs.find(
                  (ele) => ele.name === parentInputName
                );
                var entitiyRef = '';
                if (input) {
                  entitiyRef = input.entityReference || '';
                } else {
                  const output = selectedFunction?.outputs.find(
                    (ele) => ele.name === parentInputName
                  );
                  entitiyRef = output?.entityReference || '';
                }
                console.log(entitiyRef);
                this.entityIdForGetEntity = entitiyRef;
                this.variableToBeAdded.entity = 'local_variable';
                this.selectedBlockFlag = true;
                this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
              }
            } else if (block.type == 'set_entity_variable') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITY_VARIABLE';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'set_reference_entity_variable_for_AOB') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
              this.AOBToBeAddedAllowedType = '';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                'entity_reference_for_AOB';
            } else if (block.type == 'set_key_value_in_AOB_reference') {
              // AAOB key/value upsert — pick entity + AAOB field via the two-step AAOB picker
              // (same selection as aaob_aggregate_by_label). blockAtlasField resets to VAR so a fresh
              // click starts at the entity step and cascades to the AAOB field.
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'AAOB_UPSERT';
              const curUpsertEntity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = (curUpsertEntity && curUpsertEntity !== 'NANENTITY') ? curUpsertEntity : '';
              const curUpsertAaob = block.getFieldValue('AAOB_FIELD');
              this.aaobUpsertField = (curUpsertAaob && curUpsertAaob !== 'NANAAOB_FIELD') ? curUpsertAaob : '';
              this.blockAtlasField = 'VAR';
            } else if (block.type == 'get_entity_list_variable' ) {
              this.entityIdForGetEntity = '';
              if (
                (block.getParent()?.type == 'set_entity_list_variable' &&
                  block.getParent()?.getFieldValue('VAR') !== 'NAN') ||
                block.getParent()?.type ==
                  'set_entity_variable_by_index_of_entity_list'
              ) {
                if (block.getParent()?.type == 'set_entity_list_variable') {
                  const value = block.getParent()?.getFieldValue('VAR');
                  if (value.includes('localVariable$')) {
                    for (let key of this.getKeys(this.pageData.localVariable)) {
                      if (
                        this.getType(this.pageData.localVariable[key]) ==
                        'ENTITY_LIST_VARIABLE'
                      ) {
                        if (
                          key ==
                          value
                            .replace('localVariable$', '')
                            .replace('.data', '')
                        ) {
                          this.entityIdForGetEntity =
                            this.pageData.localVariable[key].entityId;
                          this.variableToBeAdded.entity = 'local_variable';
                        }
                      }
                    }
                  } else if (value.includes('globalVariable$')) {
                    for (let key of this.getKeys(
                      this.globalVariablesService.globalVariable
                    )) {
                      if (
                        this.getType(
                          this.globalVariablesService.globalVariable[key]
                        ) == 'ENTITY_LIST_VARIABLE'
                      ) {
                        if (
                          key ==
                          value
                            .replace('globalVariable$', '')
                            .replace('.data', '')
                        ) {
                          this.entityIdForGetEntity =
                            this.globalVariablesService.globalVariable[
                              key
                            ].entityId;
                          this.variableToBeAdded.entity = 'global_variable';
                        }
                      }
                    }
                  }  else { 
                    if(value.includes('$$')) {
                      this.variableToBeAdded.entity = value.split('$$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$$')[0] + '%' + value.split('$$')[1]);
                    } else {
                      this.variableToBeAdded.entity = value.split('$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$')[0] + '%' +value.split('$')[1]);
                    }
                  }
                } else if (
                  block.getParent()?.type ==
                    'set_entity_variable_by_index_of_entity_list' &&
                  (block.getParent()?.getParent()?.type == 'set_entity_variable' || block.getParent()?.getParent()?.type == 'set_reference_entity_variable_for_AOB')
                ) {
                  const value = block
                    .getParent()
                    ?.getParent()
                    ?.getFieldValue('VAR');
                  if (value.includes('localVariable$')) {
                    for (let key of this.getKeys(this.pageData.localVariable)) {
                      if (
                        this.getType(this.pageData.localVariable[key]) ==
                        'ENTITY_VARIABLE'
                      ) {
                        if (key == value.replace('localVariable$', '')) {
                          this.entityIdForGetEntity =
                            this.pageData.localVariable[key].entityId;
                          this.variableToBeAdded.entity = 'local_variable';
                        }
                      }
                    }
                  } else if (value.includes('globalVariable$')) {
                    for (let key of this.getKeys(
                      this.globalVariablesService.globalVariable
                    )) {
                      if (
                        this.getType(
                          this.globalVariablesService.globalVariable[key]
                        ) == 'ENTITY_VARIABLE'
                      ) {
                        if (key == value.replace('globalVariable$', '')) {
                          this.entityIdForGetEntity =
                            this.globalVariablesService.globalVariable[
                              key
                            ].entityId;
                          this.variableToBeAdded.entity = 'global_variable';
                        }
                      }
                    }
                  } else {
                      if(value.includes('$$')) {
                      this.variableToBeAdded.entity = value.split('$$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$$')[0] + '%' + value.split('$$')[1]);
                    } else {
                      this.variableToBeAdded.entity = value.split('$')[0];
                      this.entityIdForGetEntity = this.resolveEntityIdByEntityAndVariable(value.split('$')[0] + '%' +value.split('$')[1]);
                    }
                  }
                } else {
                  console.log('tnetative');
                  //! this sinario might never occor - maybe
                  this.entityIdForGetEntity = '';
                  this.variableToBeAdded.entity =
                    this.convertBackToVaribleFromFielValue(
                      block.getFieldValue('VAR')
                    )[1];
                }
                this.selectedBlockFlag = true;
                this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
              } else if (block.getParent()?.type == 'push_new_element_to_array') {
                // Push target: the Atlas reconstruction handles this via `isPushGetList` in openAtlasForBlock
                // (same ENTITY_LIST_VARIABLE enumerator, no same-entity narrowing → entityIdForGetEntity '$any$').
                // Faithful to main, which had no push restriction. (The old PUSHABLE_ENTITY_LIST bucket picker
                // was removed 2026-07-12 — see docs/archive/legacy-atlas.md.)
                this.selectedBlockFlag = true;
                this.entityIdForGetEntity = '$any$';
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
              } else if (block.getParent()?.type == 'delete_from_entity_list' || block.getParent()?.type == 'set_function_entity_list_at_index') {
                this.selectedBlockFlag = true;
                this.entityIdForGetEntity = '$any$';
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
              }else if (block.getParent()?.type == 'get_users_list' || block.getParent()?.type == 'get_roles_list'){
                // user-management migration: users -> systemOotbUser (Postgres). roles are NOT a
                // Postgres entity anymore (they live in zavoConfig) — role_global is kept ONLY as a
                // local Blockly "role" type for get_roles_list output until a replacement type lands.
                const entityName = block.getParent()?.type == 'get_users_list' ? 'systemOotbUser' : 'role_global';
                  this.selectedBlockFlag = true;
                this.entityIdForGetEntity = entityName;
                this.variableToBeAdded.entity =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[1];
                this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
                this.variableToBeAdded.variable =
                  this.convertBackToVaribleFromFielValue(
                    block.getFieldValue('VAR')
                  )[0];
              } else {

                console.log('in get_entity_variable. this parent block has not been cosnidered for. ');
              }
              // this.selectedBlockFlag = true;
              // this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
              // this.variableToBeAdded.variable = this.convertBackToVaribleFromFielValue(block.getFieldValue('VAR'))[0];
              // this.variableToBeAdded.entity = this.convertBackToVaribleFromFielValue(block.getFieldValue('VAR'))[1];
            } else if (block.type == 'set_entity_list_variable') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            } else if (block.type == 'push_to_entity_list') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            }  else if (block.type == 'get_entity_length') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            }  else if (block.type == 'set_variable_entity') {
              this.entityIdForGetEntity = '$any$';
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITY_LIST_VARIABLE';
              this.variableToBeAdded.variable =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[0];
              this.variableToBeAdded.entity =
                this.convertBackToVaribleFromFielValue(
                  block.getFieldValue('VAR')
                )[1];
            }  else if (block.type == 'get_query') {
              var selectedblock: any = block;
              var value = selectedblock.getFieldValue('VAR');
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'set_entity_list_variable'
                ) {
                  this.getChildrenEntiesofSelectedEntity(
                    selectedblock.getFieldValue('VAR')
                  );
                  break;
                } else if (selectedblock.getParent()?.type == 'get_query') {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                }

                selectedblock = selectedblock.getParent();
              }

              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }

              var flag = false;
              for (let variable of this.childrenEntiesofSelectedEntity) {
                if (variable.id == value) {
                  flag = true;
                  break;
                }
              }
              if (block.getParent()?.type !== 'get_query') flag = true;
              if (!flag || !block.getParent())
                block.setFieldValue('NANENTITY', 'VAR');
              if (block.getParent()?.type == 'get_query') {
                this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              } else if (
                block.getParent()?.type == 'set_entity_list_variable'
              ) {
                if (
                  selectedblock
                    .getParent()
                    ?.getFieldValue('VAR')
                    .includes('globalVariable')
                )
                  block.setFieldValue(
                    this.globalVariablesService.globalVariable[
                      selectedblock
                        .getParent()
                        ?.getFieldValue('VAR')
                        .replace(/\.data/g, '')
                        .replace(/globalVariable\$/g, '')
                    ].entityId,
                    'VAR'
                  );
                else
                  block.setFieldValue(
                    this.pageData.localVariable[
                      selectedblock
                        .getParent()
                        ?.getFieldValue('VAR')
                        .replace(/\.data/g, '')
                        .replace(/localVariable\$/g, '')
                    ].entityId,
                    'VAR'
                  );
                this.variableToBeAdded.allowedType = '';
              } else {
                this.variableToBeAdded.allowedType = 'ENTITYNAME';
              }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.entity =
                this.getParenEntityFromEntityNameFlatWithParent(
                  block.getFieldValue('VAR')
                );
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'entity_conditions') {
              var selectedblock: any = block;
              // while(selectedblock.getPreviousBlock() && selectedblock.getPreviousBlock()?.type) {
              //   if( selectedblock.getPreviousBlock()?.type == 'get_query') {
              //     this.getChildrenEntiesofSelectedEntity(selectedblock.getPreviousBlock()?.getFieldValue('VAR'));
              //     break;
              //   }

              //   selectedblock = selectedblock.getPreviousBlock();
              // }
              var selectedblock: any = block;
              var value = selectedblock.getFieldValue('VAR');
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (selectedblock.getParent()?.type == 'get_query' || selectedblock.getParent()?.type == 'entity_aggregate_query' || selectedblock.getParent()?.type == 'aaob_aggregate_by_label') {
                  this.getChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );

                  break;
                } else if (
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                }

                selectedblock = selectedblock.getParent();
              }

              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }

              var flag = false;
              for (let variable of this.childrenEntiesofSelectedEntity) {
                if (variable.id == value) {
                  flag = true;
                  break;
                }
              }
              if (!flag || !block.getParent())
                block.setFieldValue('NANENTITY', 'VAR');

              selectedblock = selectedblock.getParent();
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              this.variableToBeAdded.entity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            }  else if (block.type == 'entity_set_string_in_conditions') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              var orBlockNotOccuredFlag = true;
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'get_query' ||
                  selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                  selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  this.variableToBeAdded.entity = selectedblock
                    .getParent()
                    ?.getFieldValue('VAR');
                  await this.getVariableChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR'),
                    'STRING' , this.entityListForFieldPickers()
                  );
                  break;
                } else if (
                  selectedblock.getParent()?.type ==
                    'entity_set_string_in_conditions' &&
                  orBlockNotOccuredFlag
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                } else if (
                  selectedblock.getParent()?.type == 'entity_or_conditions'
                ) {
                  orBlockNotOccuredFlag = false;
                }
                selectedblock = selectedblock.getParent();
              }
              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'entity_set_time_stamp_in_conditions') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              var orBlockNotOccuredFlag = true;
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'get_query' ||
                  selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                  selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  this.variableToBeAdded.entity = selectedblock
                    .getParent()
                    ?.getFieldValue('VAR');
                  await this.getVariableChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR'),
                    'TIMESTAMP', this.entityListForFieldPickers()
                  );
                  break;
                } else if (
                  selectedblock.getParent()?.type ==
                    'entity_set_time_stamp_in_conditions' &&
                  orBlockNotOccuredFlag
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                } else if (
                  selectedblock.getParent()?.type == 'entity_or_conditions'
                ) {
                  orBlockNotOccuredFlag = false;
                }
                selectedblock = selectedblock.getParent();
              }
              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'entity_set_number_in_conditions') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              var orBlockNotOccuredFlag = true;
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'get_query' ||
                  selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                  selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  this.variableToBeAdded.entity = selectedblock
                    .getParent()
                    ?.getFieldValue('VAR');
                  await this.getVariableChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR'),
                    'NUMBER', this.entityListForFieldPickers()
                  );
                  break;
                } else if (
                  selectedblock.getParent()?.type ==
                    'entity_set_number_in_conditions' &&
                  orBlockNotOccuredFlag
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                } else if (
                  selectedblock.getParent()?.type == 'entity_or_conditions'
                ) {
                  orBlockNotOccuredFlag = false;
                }
                selectedblock = selectedblock.getParent();
              }
              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'entity_set_boolean_in_conditions') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              var orBlockNotOccuredFlag = true;
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'get_query' ||
                  selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                  selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  this.variableToBeAdded.entity = selectedblock
                    .getParent()
                    ?.getFieldValue('VAR');
                  await this.getVariableChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR'),
                    'BOOLEAN' , this.entityListForFieldPickers()
                  );
                  break;
                } else if (
                  selectedblock.getParent()?.type ==
                    'entity_set_boolean_in_conditions' &&
                  orBlockNotOccuredFlag
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                } else if (
                  selectedblock.getParent()?.type == 'entity_or_conditions'
                ) {
                  orBlockNotOccuredFlag = false;
                }
                selectedblock = selectedblock.getParent();
              }
              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              // this.variableToBeAdded.entity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'entity_set_id_in_conditions') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              var parentVariablesForSelectedEntity = [];
              var orBlockNotOccuredFlag = true;
              while (
                selectedblock.getParent() &&
                selectedblock.getParent()?.type
              ) {
                if (
                  selectedblock.getParent()?.type == 'get_query' ||
                  selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                  selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                  selectedblock.getParent()?.type == 'entity_conditions'
                ) {
                  this.variableToBeAdded.entity = selectedblock
                    .getParent()
                    ?.getFieldValue('VAR');
                  await this.getVariableChildrenEntiesofSelectedEntity(
                    selectedblock.getParent()?.getFieldValue('VAR'),
                    'ENTITY_REFERENCE' , this.entityListForFieldPickers()
                  );
                  break;
                } else if (
                  selectedblock.getParent()?.type ==
                    'entity_set_id_in_conditions' &&
                  orBlockNotOccuredFlag
                ) {
                  parentVariablesForSelectedEntity.push(
                    selectedblock.getParent()?.getFieldValue('VAR')
                  );
                } else if (
                  selectedblock.getParent()?.type == 'entity_or_conditions'
                ) {
                  orBlockNotOccuredFlag = false;
                }
                selectedblock = selectedblock.getParent();
              }
              for (
                let i = 0;
                i < this.childrenEntiesofSelectedEntity.length;
                i++
              ) {
                for (let parentVariables of parentVariablesForSelectedEntity) {
                  if (
                    parentVariables == this.childrenEntiesofSelectedEntity[i].id
                  ) {
                    this.childrenEntiesofSelectedEntity.splice(i, 1);
                    i = i - 1;
                    break;
                  }
                }
              }
              this.childrenEntiesofSelectedEntity.push({ name: 'Self' , id: '' , parent:''});
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              // this.variableToBeAdded.entity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'get_set_order_variable') {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];

              if (
                selectedblock.getParent()?.type == 'get_query' ||
                selectedblock.getParent()?.type == 'entity_aggregate_query' ||
                selectedblock.getParent()?.type == 'aaob_aggregate_by_label' ||
                selectedblock.getParent()?.type == 'entity_conditions'
              ) {
                const parentVar = selectedblock.getParent()?.getFieldValue('VAR');
                const entityList = this.entityListForFieldPickers();
                const collected: any[] = [];
                for (const type of ['STRING', 'NUMBER', 'TIMESTAMP', 'BOOLEAN', 'GEOMETRY']) {
                  await this.getVariableChildrenEntiesofSelectedEntity(parentVar, type, entityList);
                  collected.push(...this.childrenEntiesofSelectedEntity);
                }
                this.childrenEntiesofSelectedEntity = collected;
              }
              // var parentVariablesForSelectedEntity = [];
              // while(selectedblock.getParent() && selectedblock.getParent()?.type) {
              //   if( selectedblock.getParent()?.type == 'get_query' || selectedblock.getParent()?.type == 'entity_conditions') {
              //     this.getVariableChildrenEntiesofSelectedEntity(selectedblock.getParent()?.getFieldValue('VAR'), 'STRING');
              //     break;
              //   } else if(selectedblock.getParent()?.type == 'entity_set_string_in_conditions'){
              //     parentVariablesForSelectedEntity.push(selectedblock.getParent()?.getFieldValue('VAR'));
              //   }
              //   selectedblock = selectedblock.getParent();
              // }
              // for(let i=0; i < this.childrenEntiesofSelectedEntity.length; i++) {
              //   for(let parentVariables of parentVariablesForSelectedEntity) {
              //     if(parentVariables == this.childrenEntiesofSelectedEntity[i].id) {
              //       this.childrenEntiesofSelectedEntity.splice(i,1);
              //         i=i-1;
              //         break;
              //     }
              //   }
              // }
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYCHILDREN';
              this.variableToBeAdded.entity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type === 'entity_reference_selection') {
              var blockParent = block as Blockly.Block | null;
                while(blockParent && blockParent.getParent() !== null &&blockParent.getParent()?.type === 'entity_reference_selection') {
                  blockParent = blockParent.getParent();
                }
              if (blockParent && (blockParent.getParent()?.type === 'create_entity' || blockParent.getParent()?.type === 'update_entity')) {
                var entity = null;
                if(blockParent.getParent()?.type === 'create_entity') {
                                   entity = blockParent.getParent()?.getFieldValue('VAR');
                  } else {
                   const parent = blockParent.getParent();
                  const valueBlock = parent
                    ?.getInput('VALUE')
                    ?.connection
                    ?.targetBlock();

                  if (valueBlock) {
                    entity = valueBlock.getFieldValue('VAR');
                  }
                  }

                // getting the enityId
                if(entity) {
                var entityName  = entity;
        var entityById : any= {data:[], id:'sample'};
        if(entityName.includes('localVariable$') || entityName.includes('globalVariable$')) {
                // Resolve a local/global entity-variable token to its entityId. TWO fixes (2026-09-08,
                // nexgen harvestDetailsPageMobile "localVariable$SetCycleEntity is not defined"):
                //  1. the second replaceAll must chain off entityNameTemp, not re-read the original
                //     entityName — otherwise the local→store substitution is thrown away and the raw
                //     `localVariable$…` token reaches eval() and throws ReferenceError.
                //  2. local vars live at this.pageData.localVariable (same store used at ~L258/L619),
                //     NOT this.pageService.localVariables (which does not exist).
                var entityNameTemp = entityName.replaceAll(
                  'localVariable$',
                  'this.pageData.localVariable.'
                );

                entityNameTemp = entityNameTemp.replaceAll(
                  'globalVariable$',
                  'this.globalVariablesService.globalVariable.'
                );
                // this works because we only allow to update top level entity in update block
                entityName = eval(entityNameTemp + '.entityId');
                 entityById = this.pageService.getEntityById(entityName);

              } else {

                 entityById = this.pageService.getEntityById(entityName);
              }
                // The same resolved walk as the registry's refLink lane, so the two mint the same tokens.
                this.entityReferenceSelectionListForCRUD = this.variableRegistryService.entityReferenceCrudBaseNames(
                  resolvedShapeOf(entityName, this.pageService.entityListForShapes, this.pageService.newEntityListForSelected) as any[], entityName + '$');
                for(let t =0 ; t < this.entityReferenceSelectionListForCRUD.length; t++) {
                  this.entityReferenceSelectionListForCRUD[t] = this.entityReferenceSelectionListForCRUD[t] + '_for_CRUD';
      this.checkVariableandCreate(this.entityReferenceSelectionListForCRUD[t], 'ENTITYREFERENCEFORCRUD');
                }
                this.selectedBlockFlag = true;
                this.variableToBeAdded.allowedType = 'ENTITYREFERENCEFORCRUD';
                this.variableToBeAdded.entity = entity;
                this.variableToBeAdded.variable =
                  block.getFieldValue('VAR');
              }
            }
            } else if (block.type == 'create_entity' ) {
              var selectedblock: any = block;
              var selectedblock: any = block;
              this.childrenEntiesofSelectedEntity = [];
              this.entityNameFlatWithParent = [];
              for (let entity of this.pageService.newEntityListForSelected) {
                // Exclude LOCAL (unsynced) entities from the create_entity picker: the runtime createEntity
                // (ui-editor.component.ts) hard-blocks entity.status === 'LOCAL' with a "sync it first" alert
                // and never inserts, so listing them here only leads to a dead-end pick. Mirrors that guard.
                if ((entity as any).status === 'LOCAL') continue;
                this.entityNameFlatWithParent.push({
                  name: entity.name,
                  id: entity.id,
                  innerEntites: [],
                });
              }

              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'ENTITYNAME';
              this.variableToBeAdded.entity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = block.getFieldValue('VAR');
            } else if (block.type == 'aaob_filter_by_label' || block.type == 'aaob_label_exists' || block.type == 'aaob_sort_by_label') {
              // AAOB-CACHE-INVARIANT: filter-cache query blocks. Selection
              // happens in the HTML panel — populated from
              // pageService.newEntityListForSelected, filtered to AAOB fields
              // (ARRAY_OF_OBJECT with extendedAttributes:true) on the parent
              // query's target entity.
              //
              // Two valid parent scopes:
              //   - get_query.CONDITIONS slot → root entity (get_query.VAR)
              //   - entity_conditions.CONDITIONS slot → joined child entity
              //     (entity_conditions.VAR), itself nested inside a get_query
              //
              // Walk up through condition/sibling/OR blocks to whichever
              // scope-defining ancestor comes first. entity_conditions wins
              // when present because Hasura where-clause scoping is innermost.
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'AAOB_FILTER';
              const current = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = current && current !== 'NANAAOB_FIELD' ? current : '';
              this.variableToBeAdded.entity = '';
              this.aaobParentEntityId = '';
              let cursor = block.getParent();
              while (cursor) {
                if (cursor.type === 'entity_conditions' || cursor.type === 'get_query') {
                  this.aaobParentEntityId = cursor.getFieldValue('VAR') || '';
                  break;
                }
                cursor = cursor.getParent();
              }
            } else if (block.type == 'entity_aggregate_query') {
              // Phase 2 query-capabilities: entity_aggregate_query block.
              // Selection happens in the AGGREGATE_ENTITY HTML panel — entity
              // dropdown + (entity-scoped) Number-column dropdown. See
              // docs/plans/query-capabilities-phase-2.md §4.4.
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'AGGREGATE_ENTITY';
              const currentEntity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = (currentEntity && currentEntity !== 'NANENTITY') ? currentEntity : '';
              this.variableToBeAdded.entity = '';
              const currentColumn = (block.getFieldValue('COLUMN') || '').replace('__AGG_NUM', '');
              this.aggregateColumnVar = (currentColumn && currentColumn !== 'NANENTITYNUMBER') ? currentColumn : '';
            } else if (block.type == 'aaob_aggregate_by_label') {
              // Phase 2 query-capabilities (Phase D): AAOB-label aggregate.
              // Selection happens in AAOB_AGGREGATE HTML panel — entity
              // dropdown + AAOB field dropdown scoped to that entity.
              // systemKey comes from a connected text block (free-text).
              this.selectedBlockFlag = true;
              this.variableToBeAdded.allowedType = 'AAOB_AGGREGATE';
              const curEntity = block.getFieldValue('VAR');
              this.variableToBeAdded.variable = (curEntity && curEntity !== 'NANENTITY') ? curEntity : '';
              this.variableToBeAdded.entity = '';
              const curAaob = block.getFieldValue('AAOB_FIELD');
              this.aaobAggregateField = (curAaob && curAaob !== 'NANAAOB_FIELD') ? curAaob : '';
            } else {
              this.selectedBlockFlag = false;
            }
          }
        }
        // doing this as the selected is followed by one click event
        if (event.type === Blockly.Events.CLICK) {
          if (!this.eventSelected) {
            this.variableToBeAdded.entity = '';
            this.variableToBeAdded.variable = '';

            this.selectedBlockFlag = false;
          }
          this.eventSelected = false;
        }
      }
    );
    this.setBlocklyVariables();

    // Override the default 'procedures_defnoreturn' block — EXTRACTED (unchanged) to
    // helpers/procedure-block.ts so a headless runner can register the function wrapper without this
    // method, which otherwise injects into a DOM div. Delegation, not removal.
    registerProcedureBlock();
  }
  convertBackToVaribleFromFielValue(value: string) {
    // have to consider for the local_entity$ also
    var index = value.indexOf('$');
    var entity = '';
    if (value.includes('global_entity$')) {
      value = value.replace('global_entity$', '');
      index = value.indexOf('$');
      entity =
        'global_entity$' + (index !== -1 ? value.substring(0, index) : value);
    } else if (value.includes('local_entity$')) {
      value = value.replace('local_entity$', '');
      index = value.indexOf('$');
      entity =
        'local_entity$' + (index !== -1 ? value.substring(0, index) : value);
    } else if (
      (index !== -1 ? value.substring(0, index) : value) == 'globalVariable'
    ) {
      entity = 'global_variable';
    } else if (
      (index !== -1 ? value.substring(0, index) : value) == 'localVariable'
    ) {
      entity = 'local_variable';
    }else if(value.includes('entity_reference_for_AOB$')) {
      value = value.replace('entity_reference_for_AOB$' , '');
      index = -1;
      entity = 'entity_reference_for_AOB$';

    } else if (value.includes('global_variable') || value.includes('global_variable')) {
      index = index + 1;
      entity = index !== -1 ? value.substring(0, index) : value;
      value = value.substring(index );
      index = -1;
    } else {
      index = index + 1;
      entity = index !== -1 ? value.substring(0, index) : value;
    }
    return [index !== -1 ? value.substring(index + 1) : value, entity];
  }
  getFieldNamesAndValuesByBlockType(blockType: any) {
    var blocks = this.workspace.getAllBlocks(false);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.type === blockType) {
        var fieldNamesAndValues: any[] = [];
        block.inputList.forEach((input) => {
          input.fieldRow.forEach((field) => {
            fieldNamesAndValues.push({
              name: field.name,
              value: field.getValue(),
            });
          });
        });
        console.log(
          'Field names and values for block type "' + blockType + '":',
          fieldNamesAndValues
        );
        return fieldNamesAndValues;
      }
    }
    console.log('No block found with type:', blockType);
    return null;
  }

  //Start: adding varaibles with id to blockly interface
  setBlocklyVariables() {
        this.workspace
      .getVariableMap()
      .createVariable(
        'NANENTITY_LIST_VARIABLE',
        'ENTITY_LIST_VARIABLE',
        'NANENTITY_LIST_VARIABLE'
      );
    this.workspace
      .getVariableMap()
      .createVariable(
        'NAN_ENTITY_VARIABLE',
        'ENTITY_VARIABLE',
        'NAN_ENTITY_VARIABLE'
      );
    this.workspace
      .getVariableMap()
      .createVariable('NANENTITY', 'ENTITYNAME', 'NANENTITY');
          this.workspace
      .getVariableMap()
      .createVariable('NANENTITYSTRING', 'STRING', 'NANENTITYSTRING');
          this.workspace
      .getVariableMap()
      .createVariable('NANENTITYTIMESTAMP', 'TIMESTAMP', 'NANENTITYTIMESTAMP');
    this.workspace
      .getVariableMap()
      .createVariable('NANENTITYNUMBER', 'NUMBER', 'NANENTITYNUMBER');
    this.workspace
      .getVariableMap()
      .createVariable('NANENTITYBOOLEAN', 'BOOLEAN', 'NANENTITYBOOLEAN');

    // AAOB-CACHE-INVARIANT: register one workspace variable per unique AAOB
    // field name across all entities so the aaob_filter_by_label /
    // aaob_label_exists blocks have a valid VAR target. Selection is driven
    // by the HTML panel (allowedType === 'AAOB_FILTER') in
    // blockly-interface.component.html — see toBeAddedVaraibleChanged().
    this.workspace
      .getVariableMap()
      .createVariable('NANAAOB_FIELD', 'AAOB_FIELD', 'NANAAOB_FIELD');
    // MOVED 2026-07-16 -> VariableRegistryService.generateVariables (verbatim copy of the old body).
    // Now a thin blockly-generation-mode caller: hand it the workspace, and assign entityNameFlatWithParent
    // back so this component's create_entity / ENTITYNAME pickers (their own re-writer + readers) are unchanged.
    this.entityNameFlatWithParent = this.variableRegistryService.generateVariables(this.workspace);
  }
  // NOTE: isTimestamp() below stays (used across this component). getEntityNames() +
  // addVariableRecursivelyFromEntity() also below are now dead copies (the live path is the service) —
  // left in place for now; safe to comment once the service is verified in testing.
  isTimestamp(value: any): boolean {
  if (typeof value !== "string") return false;

  // Strict ISO 8601 date-time with timezone
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?([+-]\d{2}:\d{2}|Z)$/;

  if (!isoRegex.test(value)) return false;

  const time = Date.parse(value);
  return !isNaN(time);
}
  addVariableRecursivelyFromEntity(data: any, entityId: string) {
    for (const variables of data) {
      if (variables.type == 'UUID') variables.type = 'STRING';
      if (variables.type == 'AUTO_INCREMENT') variables.type = 'NUMBER';
      if (
        variables.type == 'NUMBER' ||
        variables.type == 'STRING' ||
        variables.type == 'BOOLEAN' || 
        variables.type == 'TIMESTAMP'
      ) {
        try {
          this.workspace
            .getVariableMap()
            .createVariable(
              variables.name + '$' + entityId,
              variables.type,
              variables.name + '$' + entityId
            );
        } catch (error) {
          const previousVariable = Blockly.Variables.getVariable(
            this.workspace,
            variables.name + '$' + entityId
          );
          if (previousVariable?.type == variables.type) {
          }
          //  else
            // console.log(
            //   'present variable with name:' +
            //     variables.name +
            //     ' and type: ' +
            //     variables.type +
            //     ' has already been used with a different type: ' +
            //     previousVariable?.type +
            //     'Hence this varialbe name has to be changed or its type to previous type,'
            // );
        }
      }
      if (variables.type == 'ARRAY_OF_OBJECT') {
        this.addVariableRecursivelyFromEntity(variables.data, variables.id);
      }
    }
  }
  entityNameFlatWithParent: {
    name: string;
    id: string;
    innerEntites: { name: string; id: string }[];
  }[] = [];
  getEntityNames(
    typeData: TypeData[],
    parentEntityFlag: boolean = true,
    parentId: string = '',
    parentName: string = ''
  ) {
    var data: any[] = [];
    var dataWithParentName: any = [];
    if (parentEntityFlag) {
      dataWithParentName.push({
        name: parentName,
        id: parentId,
        innerEntites: [],
      });
    }
    for (let i = 0; i < typeData.length; i++) {
      if (typeData[i].type == 'ARRAY_OF_OBJECT') {
        data.push(typeData[i].id);
        if (parentEntityFlag)
          dataWithParentName[dataWithParentName.length - 1].innerEntites.push({
            name: typeData[i].name,
            id: typeData[i].id,
          });
        else
          dataWithParentName.push({
            name: typeData[i].name,
            id: typeData[i].id,
          });

        var returnedData = this.getEntityNames(typeData[i].data, false);
        data = [...data, ...returnedData[0]];
        if (parentEntityFlag)
          dataWithParentName[dataWithParentName.length - 1].innerEntites = [
            ...dataWithParentName[dataWithParentName.length - 1].innerEntites,
            ...returnedData[1],
          ];
        else dataWithParentName = [...dataWithParentName, ...returnedData[1]];
      }
    }
    if (parentEntityFlag) {
      this.entityNameFlatWithParent = [
        ...this.entityNameFlatWithParent,
        ...dataWithParentName,
      ];
    }
    return [data, dataWithParentName];
  }
  // End adding variables with id to blockyl interface

  // didnt work
  // disableField(blockId: string, fieldName: string): void {
  //   const block = this.workspace.getBlockById(blockId);
  //   if (block) {
  //     const field = block.getField(fieldName);
  //     if (field) {
  //       (field as any).EDITABLE = false; // Disable editing
  //       (field as any).SERIALIZABLE = false; // Disable serialization
  //     }
  //   }
  // }

  // Start Fucntion to be saved and created at the same time to show code of the function Selected
  selectedFunctionName = '';
  functionSelected() {
    const list = this.editorMode === 'conditional'
      ? (this.pageData.conditionalFunctions ?? [])
      : this.editorMode === 'value'
        ? (this.pageData.valueFunctions ?? [])
        : this.pageData.functions;
    const functionSelected: any = list.find(
      (variable) => variable.name === this.selectedFunctionName
    );
    this.functionName = functionSelected?.name;
    this.restoreBlock(functionSelected?.xml);

    // Ensure an editable starter function block exists even when the saved function has no/empty
    // XML (e.g. a brand-new page's default function) — otherwise Blockly opens with nothing to edit.
    // Only the async lane is wrapped in a procedures_defnoreturn shell. A value function is a bare
    // statement list ending in return_value, so giving it a shell would add a block the value
    // palette does not offer and the gate would then refuse on the next regeneration.
    if (this.functionName && this.editorMode === 'function' && !this.hasFunctionDefBlock(this.functionName)) {
      this.restoreBlock(
        `<xml xmlns="https://developers.google.com/blockly/xml"><block type="procedures_defnoreturn"><field name="NAME">${this.functionName}</field><statement name="STACK"></statement></block></xml>`
      );
    }

    this.selectedBlockFlag = false;
  }

  /** True if the workspace already contains the function-definition block for `name`. */
  private hasFunctionDefBlock(name: string): boolean {
    if (!this.workspace) return false;
    return this.workspace
      .getTopBlocks(false)
      .some((b: any) => b.type === 'procedures_defnoreturn' && b.getFieldValue('NAME') === name);
  }

  // Conditional toolbox: strip blocks that generate await / side effects, then inject the return_boolean
  // category. THE LISTS THEMSELVES MOVED to helpers/conditional-allowed-blocks.ts (2026-08-14) so the
  // headless generation gate refuses exactly what this palette withholds — see that file's header. The
  // fields stay, so nothing else in this class changes.
  private readonly conditionalExcludedBlockTypes: string[] = CONDITIONAL_EXCLUDED_BLOCK_TYPES;
  private readonly conditionalExcludedCategories: string[] = CONDITIONAL_EXCLUDED_CATEGORIES;

  /**
   * Shared strip for BOTH restricted lanes. `returnCategory` is the only difference between a
   * conditional palette and a value palette — keeping one implementation is what stops the editor's
   * palette and the headless gate's allowed-set from drifting apart.
   */
  buildRestrictedToolboxXml(fullXml: string, returnCategory: string): string {
    let xml = fullXml;
    for (const cat of this.conditionalExcludedCategories) {
      const regex = new RegExp(
        `<category\\s+name="${cat}"[\\s\\S]*?<\\/category>`,
        'g'
      );
      xml = xml.replace(regex, '');
    }
    for (const type of this.conditionalExcludedBlockTypes) {
      const regex = new RegExp(
        `<block\\s+type="${type}"\\s*/?>(?:\\s*</block>)?`,
        'g'
      );
      xml = xml.replace(regex, '');
    }
    return xml.replace('</xml>', `${returnCategory}</xml>`);
  }

  buildConditionalToolboxXml(fullXml: string): string {
    return this.buildRestrictedToolboxXml(
      fullXml,
      `<category name="Conditional" colour="#5C81A6"><block type="return_boolean"/></category>`
    );
  }

  buildValueToolboxXml(fullXml: string): string {
    return this.buildRestrictedToolboxXml(
      fullXml,
      `<category name="Value" colour="#5C81A6"><block type="return_value"/></category>`
    );
  }

  private stripSideEffectCallsForConditional(code: string): string {
    return code
      .replace(/\s*await\s+this\.reinitializeComponents\(\);?/g, '')
      .replace(/\s*await\s+this\.reinitialize\s*\(\s*(['"])[^'"]*\1\s*\);?/g, '')
      .replace(/\s*this\.reinitializeComponentsNow\(\);?/g, '')
      .replace(/\s*this\.reinitializeComponantsFlag\s*=\s*true\s*;?/g, '');
  }

  /** True when the page currently being edited is the sandboxed landing page. */
  private isCurrentPageSandboxed(): boolean {
    return !!(this.pageService?.selectedPage as any)?.isSandboxed;
  }

  startNewFunction() {
    this.isConditionalMode = false;
    if (this.workspace && this.fullToolboxXml) {
      (this.workspace as any).updateToolbox(this.fullToolboxXml);
    }
    this.selectedFunctionName = '';
    this.clearWorkSpace();
    this.functionName = '';
    this.planCardMode = 'empty';
    this.forgePlan = null;
    this.forgeMessages = [];
    this.forgeError = '';
    this.forgeChatState = 'ready';
  }

  startNewConditionalFunction() {
    this.isConditionalMode = true;
    if (this.workspace && this.conditionalToolboxXml) {
      (this.workspace as any).updateToolbox(this.conditionalToolboxXml);
    }
    this.selectedFunctionName = '';
    this.clearWorkSpace();
    this.functionName = '';
  }

  startNewValueFunction(allowedType?: string) {
    this.editorMode = 'value';
    this.valueAllowedType = allowedType;
    if (this.workspace && this.valueToolboxXml) {
      (this.workspace as any).updateToolbox(this.valueToolboxXml);
    }
    applyReturnValueCheck(this.workspace, allowedType);
    this.selectedFunctionName = '';
    this.clearWorkSpace();
    this.functionName = '';
  }
  restoreBlock(xmlText: any) {
    if (xmlText) {
      const xmlDom = this.alignToDeclaredTypes(Blockly.utils.xml.textToDom(xmlText));
      Blockly.Xml.domToWorkspace(xmlDom, this.workspace);
    }
  }

  /**
   * A saved block carrying an older variable type takes the DECLARED type (the registered workspace
   * variable's) before it is loaded — blockly-tradais throws on the mismatch inside domToWorkspace, so the
   * function would not open at all. Quiet by design: a builder cannot change a type. See
   * `alignSavedVariableTypes`.
   */
  private alignToDeclaredTypes(dom: Element): Element {
    const ws: any = this.workspace;
    if (!ws) return dom;
    return alignSavedVariableTypes(dom, (id, name) =>
      (id ? ws.getVariableById(id)?.type : undefined)
        ?? (name ? ws.getAllVariables().find((v: any) => v.name === name)?.type : undefined));
  }
  addFunctinoToPage(description?: string) {
    if (this.editorMode === 'conditional' && !this.pageData.conditionalFunctions) {
      this.pageData.conditionalFunctions = [];
    }
    if (this.editorMode === 'value' && !this.pageData.valueFunctions) {
      this.pageData.valueFunctions = [];
    }
    // Value functions are stripped for the same reason conditionals are: they run SYNCHRONOUSLY
    // against the shared sandbox, so a stray `await this.reinitializeComponents()` would make
    // getValueFn refuse the whole body and the input would silently show its static value.
    // ONE read, both artefacts — see serializeFunction. `this.code` is deliberately not consulted here.
    const serialized = this.serializeFunction();
    const codeToSave = this.editorMode === 'function'
      ? serialized.code
      : this.stripSideEffectCallsForConditional(serialized.code);
    const targetList: LOCALFUNCTIONS[] = this.editorMode === 'conditional'
      ? (this.pageData.conditionalFunctions as LOCALFUNCTIONS[])
      : this.editorMode === 'value'
        ? (this.pageData.valueFunctions as LOCALFUNCTIONS[])
        : this.pageData.functions;
    const steps = decompileWorkspace(this.workspace, this.functionName);
    const pageContext = this.buildForgePageContext();
    const autoDescription = formatPlanAsText(steps, pageContext);
    const existingIdx = targetList.findIndex((f) => f.name === this.functionName);
    if (existingIdx !== -1) {
      targetList[existingIdx] = {
        name: this.functionName,
        xml: serialized.xml,
        code: codeToSave,
        steps,
        description: description || autoDescription,
      };
    } else {
      // Checked against BOTH other lanes, not just one. Three lanes now share a name space, and a
      // collision is not cosmetic: the lanes have different return contracts, so a name resolving
      // into the wrong array yields a silent `false` or a silent static-value fallback.
      const lanes: { mode: string; label: string; list: LOCALFUNCTIONS[] }[] = [
        { mode: 'function', label: 'Function', list: this.pageData.functions ?? [] },
        { mode: 'conditional', label: 'Conditional Function', list: this.pageData.conditionalFunctions ?? [] },
        { mode: 'value', label: 'Value Function', list: this.pageData.valueFunctions ?? [] },
      ];
      const clash = lanes
        .filter((o) => o.mode !== this.editorMode)
        .find((o) => o.list.some((f) => f.name === this.functionName));
      if (clash) {
        alert(
          `Name "${this.functionName}" is already used by a ${clash.label}. Please choose a different name.`
        );
        return;
      }
      targetList.push({
        name: this.functionName,
        xml: serialized.xml,
        code: codeToSave,
        steps,
        description: description || autoDescription,
      });
    }
    this.functionName = '';
    this.codeEditor.nativeElement.textContent = '';
    this.clearWorkSpace();
  }
  /**
   * `code` and `xml` from ONE workspace read, so they cannot describe different workspaces.
   *
   * ⚠ WHY THIS EXISTS. `addFinctionToPage` used to save `code: this.code` — a cached field assigned only
   * inside `if (this.codeEditor)` in updateCode() — beside `xml: this.storeBlock('')`, read live. Two
   * sources, no consistency check. NTk1Nz/homePage is the proof: its xml holds a text block "FARMS"
   * while its code is `return '';`, and CODE IS WHAT RUNS, so that button renders no label at all
   * (main-button hides it on `displayData !== ''`).
   *
   * `javascriptGenerator.init` is required before statementToCode outside the editor's own render path;
   * without it statementToCode returns '' with no error at all.
   */
  private serializeFunction(): { code: string; xml: string } {
    const block: any = this.workspace?.getTopBlocks(true)
      .find((b: any) => b.getFieldValue('NAME') === this.functionName);
    if (!block) return { code: '', xml: '' };
    (javascriptGenerator as any).init(this.workspace);
    const code = (javascriptGenerator as any).statementToCode(block, 'STACK').trim();
    const dom = (Blockly as any).Xml.blockToDom(block);
    const xml = `<xml xmlns="https://developers.google.com/blockly/xml"> ${(Blockly as any).Xml.domToText(dom)} </xml>`;
    return { code, xml };
  }

  storeBlock(blockId: string) {
    const allBlocks = this.workspace.getTopBlocks(true);

    const block: any = allBlocks.find(
      (block: any) => block.getFieldValue('NAME') === this.functionName
    );
    // const block = this.workspace.getBlockById(blockId);
    if (block) {
      const blockXml = Blockly.Xml.blockToDom(block);
      return `<xml xmlns="https://developers.google.com/blockly/xml"> ${Blockly.Xml.domToText(
        blockXml
      )} </xml>`;
    }
    return '';
  }
  //save the changes
  SavePageDataChanges() {
    this.pageService.create(this.pageData);
    this.router.navigate(['/dashboard/pages']);
  }

  // End Fucntion to be saved and created at the same time to show code of the function Selected
  // Start of add new functions
  newFunctionName = '';
  addNewFunction() {
    if (
      this.newFunctionName !== '' &&
      !this.checkFunctionNameAlreadyExists(this.newFunctionName)
    ) {
      this.functionName = this.newFunctionName;
      this.selectedFunctionName = '';
      // this.clearWorkSpace();
      this.createNewFunction();
    } else {
      alert('funciton name is already used or empty please change');
    }
  }
  checkFunctionNameAlreadyExists(name: string) {
    // All THREE lanes share one name space. Omitting valueFunctions let a new function take a name a
    // value function already held, and the lanes have different return contracts — so the collision
    // surfaces as a silent `false` or a silent static-value fallback, never as an error.
    const functions = this.pageData.functions ?? [];
    const conditionalFunctions = this.pageData.conditionalFunctions ?? [];
    const valueFunctions = this.pageData.valueFunctions ?? [];
    return (
      functions.some((variable) => variable.name === name) ||
      conditionalFunctions.some((variable) => variable.name === name) ||
      valueFunctions.some((variable) => variable.name === name)
    );
  }
  // End of Adding new functions
  //Start Blocks check of Blockly to remove excess functions and for letting only function stay at all time -- included child blocks
  updateCode() {
    const allBlocks = this.workspace.getTopBlocks(true);

    const functionBlock: any = allBlocks.find(
      (block: any) => block.getFieldValue('NAME') === this.functionName
    );
    // Filter remaining blocks (excluding the first matching one)
    const matchingProcedures = allBlocks.filter((block) => {
      return (
        block.type === 'procedures_defnoreturn' &&
        block.getFieldValue('NAME') !== this.functionName
      );
    });
    for (const block of matchingProcedures) {
      block.dispose(true, true);
    }
    if (functionBlock) {
      // const code =javascriptGenerator.workspaceToCode(this.workspace);
      // const code =javascriptGenerator.blockToCode(functionBlock, true);
      const statements = (javascriptGenerator as any).statementToCode(
        functionBlock,
        'STACK'
      );
      if (this.codeEditor) {
        this.code = statements.trim();
           prettier.format(this.code, {
            parser: "markdown",
            plugins: [prettierPluginEstree, prettierPluginTypescript, parserMarkdown],
            printWidth: 70,   // ← max characters per line
            tabWidth: 2,
            semi: true,
            singleQuote: false,
          }).then((formatedCode)=> {

        this.codeEditor.nativeElement.textContent = formatedCode;
          });





      }
    } else {
      if (this.functionName !== '') {
        this.createNewFunction();
      }
    }
  }
  functionName = '';
  createNewFunction() {
    const defaultFunctionName = this.functionName;
    const xmlText = `
    <xml>
      <block type="procedures_defnoreturn" id="myFunction">
        <field name="NAME">${defaultFunctionName}</field>
        <statement name="STACK">

        </statement>
      </block>
    </xml>
  `;
    const xmlDom = this.alignToDeclaredTypes(Blockly.utils.xml.textToDom(xmlText));
    Blockly.Xml.domToWorkspace(xmlDom, this.workspace);
  }

  deleteChildBlock(parentBlock: any, blockName: string) {
    // Get the first connected block (child block) in the statement input
    if (parentBlock.getInput(blockName)) {
      var inputConnection = parentBlock.getInput(blockName).connection;
      var childBlock = inputConnection && inputConnection.targetBlock();

      if (childBlock) {
        // Disconnect the child block from its parent
        childBlock.unplug(true);

        // Dispose of the child block
        childBlock.dispose(true);
      }
    }
  }

  //End Blocks check of Blockly to remove excess functions and for letting only function stay at all time

  // Start Variables adding Start set and get
  // for type check custom blocks
  // number
  // === Unified type-driven variable blocks (Reconciliation Phase 1) ===
  // One set/get pair replaces the 8 typed set_/get_*_variable blocks. The value
  // check, output type, colour and code template all follow the SELECTED variable's
  // declared type. Old typed blocks stay registered (hidden) for backward-compat.
  customBlockForSetVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetVariable(this);
  }

  // ── Value-carrying push (2026-06-28) ──────────────────────────────────────────
  // Re-registers push_new_element_to_array as a MUTATOR: ARRAY_INPUT + N dynamic typed
  // field rows. Selecting a field in Atlas auto-adds a row; ✕ deletes it. Save/reload:
  // rows serialize via mutationToDom/domToMutation (verified headless round-trip);
  // sockets are permissive (setCheck null) on load, real checks re-applied on
  // FINISHED_LOADING (never during deserialization). Generator captures the new row from
  // getArrayElement and sets each provided field; no rows ⇒ identical to the legacy push.
  // Overrides the helper registration (entity-variable-healper.ts) — called after it.
  customBlockForPushNewElementToArrayV2() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForPushNewElementToArrayV2(this);
  }

  // Resolve the AOB-element / entity id behind the block plugged into ARRAY_INPUT,
  // so the field picker can list that element's scalar (+ ref-scalar) fields.
  resolvePushTargetEntityId(v: string): string {
    if (!v) return '';
    if (v.startsWith('entity_reference_for_AOB$')) {
      return v.replace('entity_reference_for_AOB$', '').replace(/\[.*?\]/g, '').replace(/\$+$/, '');
    }
    if (v.includes('$$')) { return v.split('$$')[0] + '%' + v.split('$$')[1]; }
    if (v.startsWith('localVariable$') || v.startsWith('globalVariable$')) {
      const key = v.replace('localVariable$', '').replace('globalVariable$', '').replace('.data', '');
      const map: any = v.startsWith('localVariable$') ? (this as any).pageData?.localVariable : this.globalVariablesService?.globalVariable;
      return (map && map[key] && map[key].entityId) || '';
    }
    return v;
  }

  openPushFieldPicker(block: any) {
    this.selectedBlock = block;
    this.selectedBlockFlag = true;
    this.variableToBeAdded = this.variableToBeAdded || {};
    const child = block.getInputTargetBlock && block.getInputTargetBlock('ARRAY_INPUT');
    const childVar = child && child.getFieldValue && child.getFieldValue('VAR');
    this.variableToBeAdded.variable = this.resolvePushTargetEntityId(childVar);
    this.variableToBeAdded.allowedType = 'PUSH_FIELD';
    this.blockAtlasField = 'PUSH_FIELD';
    this.openAtlasForBlock();
  }

  deletePushRow(block: any, key: string) {
    if (block && block.removeFieldRow_) { block.removeFieldRow_(key); if (block.rendered) block.render(); }
  }

  customBlockForGetVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetVariable(this);
  }

  // === Unified AOB-reference variable blocks (Reconciliation Phase 2) ===
  // set_aob_variable / get_aob_variable replace the 8 typed
  // set_/get_reference_entity_variable_for_AOB_variable_* blocks. Same type-driven
  // engine as set_variable/get_variable — the AOB-reference variable carries the leaf
  // field's type, so check/output/colour/codegen all follow it. Selection reuses the
  // existing two-step AOB Atlas list flow (allowedType REFERENCE_ENTITY_VARIABLE_AOB).
  customBlockForSetAobVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetAobVariable(this);
  }

  customBlockForGetAobVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetAobVariable(this);
  }

  // ╔══ DEPRECATED — legacy typed variable block DEFINITIONS begin here.
  //    8 methods (through customBlockForGetBooleanVariable below) superseded by
  //    set_variable / get_variable. Safe to delete together with the 8 registration
  //    calls (marked above) once corpus-confirmed unused. See END marker after
  //    customBlockForGetBooleanVariable.
  customBlockForSetNumberVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetNumberVariable(this);
  }
  customBlockForGetNumberVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetNumberVariable(this);
  }

  // string
  // for type check custom blocks

  customBlockForSetStringVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetStringVariable(this);
  }
  customBlockForGetStringVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetStringVariable(this);
  }

    // timestamp
  // for type check custom blocks

  customBlockForSetTimeStampVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetTimeStampVariable(this);
  }
  customBlockForGetTimeStampVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetTimeStampVariable(this);
  }
  // boolean
  // for type check custom blocks

  customBlockForSetBooleanVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForSetBooleanVariable(this);
  }
  customBlockForGetBooleanVariable() {
    // Body EXTRACTED (unchanged) to helpers/core-variable-blocks.ts so the headless generation
    // pipeline can register these blocks with no Angular component. Delegation, not removal —
    // the same pattern isSystemVariableKey uses for atlas.utils. ONE definition, two callers.
    CoreVariableBlocks.customBlockForGetBooleanVariable(this);
  }

  // ╚══ END DEPRECATED legacy typed variable block DEFINITIONS
  //     (customBlockForSetNumberVariable … customBlockForGetBooleanVariable above)

  // add varaible with js from outside blockly
  variableToBeAdded = {
    entity: '',
    variable: 'aa',
    allowedType: '',
  };

  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  trackByValue = (_: number, item: string) => item;
  trackByFnName = (_: number, fn: { name: string }) => fn.name;

  // Delegates to the shared implementation in atlas.utils so the Atlas builder and this component use
  // ONE definition (the old inline copy is superseded, kept only via this delegation). See fix-plan A1.
  isSystemVariableKey(key: string): boolean {
    return isSystemVariableKeyShared(key);
  }

  hideSystemVariablesForSelectedAobSetBlock(): boolean {
    const blockType = this.selectedBlock?.type ?? '';
    return blockType.startsWith('set_reference_entity_variable_for_AOB_variable_');
  }

  hideSystemVariablesForSelectedPrimitiveSetBlock(): boolean {
    const blockType = this.selectedBlock?.type ?? '';
    return (
      blockType === 'set_boolean_variable' ||
      blockType === 'set_string_variable' ||
      blockType === 'set_number_variable' ||
      blockType === 'set_time_stamp_variable'
    );
  }

  /**
   * The ONLY blocks allowed to reach an AAOB (`extendedAttributes === true`). Every other block's picker
   * must EXCLUDE AAOB entities/fields — an AAOB holds only the runtime `systemKey`/`value` columns, which
   * may be touched exclusively through these dedicated blocks. See docs/blockly/aaob-model.md (A4).
   */
  isAaobBlock(type?: string): boolean {
    const t = type ?? this.selectedBlock?.type ?? '';
    return (
      t === 'set_key_value_in_AOB_reference' ||
      t === 'aaob_aggregate_by_label' ||
      t === 'aaob_filter_by_label' ||
      t === 'aaob_label_exists' ||
      t === 'aaob_sort_by_label'
    );
  }

  /**
   * True when the selected block WRITES the variable/field being picked (set_* / push_*). Its Atlas picker
   * must HIDE `"system"`-word variables (`isSystemVariableKey`), which are READ-ONLY — a GET/read block
   * shows them. (Distinct from `id`/`id_auto_increment`/`parent_id`, hidden in ALL pickers — atlas.utils.ts.)
   */
  isSetContextBlock(type?: string): boolean {
    const t = type ?? this.selectedBlock?.type ?? '';
    return t.startsWith('set_') || t.startsWith('push_');
  }

  /**
   * True when the write picker for this block must HIDE `"system"`-word fields (isSystemVariableKey).
   * Every set_ / push_ write block hides them EXCEPT the ones in SYSTEM_SET_VISIBLE_BLOCKS (the entity
   * setters + the reference-setter), which are allowed to show + set them because they only ever land in
   * an entity, and system-word fields are stripped from the create/update write. Get blocks never hide.
   */
  hideSystemForWritePicker(type?: string): boolean {
    const t = type ?? this.selectedBlock?.type ?? '';
    return this.isSetContextBlock(t) && !SYSTEM_SET_VISIBLE_BLOCKS.has(t);
  }

  /**
   * The configured reference chain around `block`: climb to the topmost entity_reference_selection, then
   * descend its NEXT sockets collecting each block's token (VAR) + mode (CONSIDER_ENTITY TRUE=link/FALSE=build).
   * Used by the ENTITYREFERENCEFORCRUD picker's orphan guard so a nested reference is only offered when its
   * parent reference is present AND set to Build inline. Best-effort — returns [] if the shape is unexpected.
   */
  readCrudReferenceChain(block: any): { token: string; mode: 'link' | 'build' }[] {
    try {
      let root = block;
      while (root?.getParent && root.getParent()?.type === 'entity_reference_selection') root = root.getParent();
      const chain: { token: string; mode: 'link' | 'build' }[] = [];
      let cur = root;
      while (cur && cur.type === 'entity_reference_selection') {
        const token = cur.getFieldValue && cur.getFieldValue('VAR');
        if (token && token !== 'NAN') {
          chain.push({ token, mode: cur.getFieldValue('CONSIDER_ENTITY') === 'TRUE' ? 'link' : 'build' });
        }
        cur = cur.getInputTargetBlock ? cur.getInputTargetBlock('NEXT') : null;
      }
      return chain;
    } catch { return []; }
  }
  getType(variable: any): string {
    if (variable === null) {
      return 'null';
    }
    var type = typeof variable;
    if (type == 'object') {
      // BEFORE the `variable.type` read. GeoJSON's discriminator is ALSO called `type`, and its
      // value is 'Polygon' / 'Point' / 'LineString' — so neither arm below matches, nothing
      // returns, control leaves this block entirely, and the function falls through to
      // `return type` = 'object'. The Atlas picker filters on that against
      // [STRING, NUMBER, BOOLEAN, TIMESTAMP, GEOMETRY] and drops every geometry variable.
      // Same predicate as runtime-helpers.getType and page.service.getType — one definition.
      if (isGeometry(variable)) return 'GEOMETRY';
      if(variable.type) {
      if (variable.type == 'ARRAY_OF_OBJECT') return 'ENTITY_LIST_VARIABLE';
      else if (variable.type == 'ENTITY') return 'ENTITY_VARIABLE';
      // An unrecognised `.type` used to fall out of this block and return typeof = 'object'.
      // Kept EXACTLY as it was, but said out loud. Returning 'ENTITY_VARIABLE' here would be more
      // defensible and would start surfacing unknown objects in entity pickers, which is a change
      // nobody has measured. Design §3.2.
      return 'object';
      } else {
        if (Array.isArray(variable)) {
          return 'ENTITY_LIST_VARIABLE';
          // return 'ARRAY_OF_OBJECT';
        } else {
          return 'ENTITY_VARIABLE'
        }
      }
    }
    if (type == 'boolean') return 'BOOLEAN';
    if (type == 'string')  {
      if(this.isTimestamp(variable)) {
        return "TIMESTAMP"
      }
      else
      return 'STRING';

    }
    if (type == 'number') return 'NUMBER';
    return type;
  }

  AOBToBeAddedVariable = '';
  AOBToBeAddedAllowedType = '';
  AOBToBeAddedEntity : {id: string , name : string}[] = [];
  pushFieldTypes: any = {}; // value-carrying push: fieldKey -> scalar block type (String/Number/Boolean/TimeStamp)
  // AAOB-CACHE-INVARIANT: entity ID resolved by walking up the selected
  // block's parents to the nearest entity_conditions block. Empty if the
  // block is floating / not yet inside an entity_conditions chain.
  // Drives the filter on the HTML AAOB picker.
  aaobParentEntityId = '';
  // Phase 2 entity_aggregate_query — workspace-variable name for the picked
  // Number column (shape: `<entity.id>$$<colName>`). Driven by the
  // AGGREGATE_ENTITY HTML panel; written back to the block's COLUMN field
  // by aggregateBlockChanged(). See docs/plans/query-capabilities-phase-2.md §6.1.
  aggregateColumnVar = '';
  // Phase 2 (Phase D) aaob_aggregate_by_label — picked AAOB field name.
  // Driven by AAOB_AGGREGATE HTML panel; written back to the block's
  // AAOB_FIELD by aaobAggregateBlockChanged().
  aaobAggregateField = '';
  // Transient holder for the AAOB key/value upsert block's selected AAOB field (allowedType AAOB_UPSERT).
  aaobUpsertField = '';
  /**
   * Pure encoder — extracted VERBATIM from toBeAddedVaraibleChanged so BOTH the live pick handler AND the
   * Atlas reconstruction enumerator share ONE encoding (the enumerator needs the flat string with no
   * side-effects to build its tree). Given the block + the picked (entity, variable)[+ AOB sub-var], returns
   * the flat VAR string. The aaob_filter/label/sort early-return stays in toBeAddedVaraibleChanged (it also
   * writes the field), so it is intentionally not represented here. See docs/blockly/atlas-reconstruction.md.
   */
  encodeReconVarName(blockType: string, parentType: string | undefined, entity: string, variable: string, aobVar: string): string {
    let varName = '';
    if (blockType.includes('_reference_entity_variable_for_AOB_variable_') || blockType == 'set_aob_variable' || blockType == 'get_aob_variable') {
      varName = entity + '$' + variable + '$$' + aobVar;
    } else if (blockType.includes('get_reference_entity_variable_for_AOB_reference_AOB')) {
      if (parentType == 'set_reference_entity_variable_for_AOB') {
        varName = entity + '$' + variable;
      } else {
        // Mirror the push_new_element_to_array code-gen — the runtime source of truth for the trailing `$`
        // (blockly-interface.component.ts :2593-2595 / entity-variable-healper.ts :1166-1170). A single trailing
        // `$` is the marker for a BARE AOB reference (no sub-field): append it ONLY when the token carries no inner
        // `$` after the `entity_reference_for_AOB$` prefix. A field-carrying token (…$$field) already resolves and
        // must NOT get a redundant trailing `$` — that stray `$` orphaned the token in the Atlas map (2 saved
        // instances fixed 2026-07-21; see docs/plans/atlas-v2.md §Connection model). Content-aware, not a blind append.
        const base = (entity + '$' + variable).replace(/\$$/, '');
        const dollarInside = base.replace('entity_reference_for_AOB$', '').includes('$');
        varName = dollarInside ? base : base + '$';
      }
    } else if (blockType == 'get_entity_variable') {
      if (entity == 'global_variable') varName = 'globalVariable$' + variable;
      else if (entity == 'local_variable') varName = 'localVariable$' + variable;
      else {
        if (entity.includes('%')) varName = entity + '' + variable;
        else varName = entity + '$' + variable;
      }
    } else {
      if (entity == 'global_variable') varName = 'globalVariable$' + variable;
      else if (entity == 'local_variable') varName = 'localVariable$' + variable;
      else {
        if (entity.includes('%')) varName = entity + '' + variable;
        else varName = entity + '$' + variable;
      }
    }
    return varName;
  }

  /**
   * Atlas reconstruction enumerator — a faithful transcription of main's `ENTITY_LIST_VARIABLE` /
   * `ENTITY_VARIABLE` HTML panels using ONLY existing helpers/data (getKeys/getType + the pageService
   * arrays + the entityIdForGetEntity narrowing), NOT new logic. `typeFilter` selects the panel
   * (ENTITY_LIST_VARIABLE | ENTITY_VARIABLE); `listSuffix` mirrors main's `key + '.data'` for local/global
   * LIST vars vs plain `key` for entity vars. Produces the flat option list (each `value` via the shared
   * encodeReconVarName); buildReconTree() turns it into the drill-down tree. Reference-descended entries
   * appear because newEntityTransformedListForSelected carries every %-path entity. See
   * docs/blockly/atlas-reconstruction.md §3-4.
   *
   * WHERE IT FITS: this REPLACES main's old HTML `*ngFor` variable dropdowns. On each block click the
   * workspace SELECTED handler (`addChangeListener`, ~L420) sets `allowedType` + `selectedBlock` (and decodes
   * the block's current VAR); THIS then lists the candidates matching that filter — same input (the pageService
   * lists), same job, moved from the template into TS. So: the click sets the FILTER, this builds the LIST.
   */
  // excludeAaobFields (zavo_oct_06): drop Advanced (AAOB) list fields WITHOUT push's LOCAL-only restriction —
  // for the other list WRITE parents (delete from entity list / set at index), which must not write into an AAOB.
  enumerateVariablesForRecon(opts: { types: string[]; listSuffix: boolean; narrow: boolean; hideSystem: boolean; excludeAaob?: boolean; excludeAaobFields?: boolean; excludeSystemEntityVariables?: boolean; excludeSystemEntityFields?: boolean }): ReconOption[] {
    const { types, listSuffix, narrow, hideSystem, excludeAaob, excludeAaobFields, excludeSystemEntityVariables, excludeSystemEntityFields } = opts;
    const options: ReconOption[] = [];
    const blockType = this.selectedBlock?.type || '';
    const parentType = this.selectedBlock?.getParent && this.selectedBlock.getParent() ? this.selectedBlock.getParent().type : undefined;
    const eid = this.entityIdForGetEntity;                 // same-entity narrowing target
    // The import gate, shared with VariableRegistry (helpers/global-import-gate.ts) so this picker and
    // the workspace can never disagree about what exists.
    const blockedGlobals = blockedGlobalNames(this.globalVariablesService?.globalVariable, this.pageData?.entites);
    const withheld = { count: 0, entityIds: new Set<string>() };
    this.reconWithheldByImport = withheld;
    // ⚠ TO-BE-TESTED-AND-CONFIRMED (2026-07-13, audit Finding 2): we treat eid==='' as SHOW-ALL. Main's
    // literal gate is `eid == v.entityId || eid == '$any$'`, so main treats '' as NARROW-to-empty-entityId
    // (≈ nothing for entity/list vars). eid is reachable as '' (init + several branches). Impact depends on
    // whether '' survives to pick-time or is always overridden first — CONFIRM via live test before locking in.
    const doNarrow = narrow && !!eid && eid !== '$any$';   // '' / '$any$' → show all (deliberate; see note above)
    const suffix = listSuffix ? '.data' : '';
    const isSysField = (k: string) => k === 'id' || k === 'id_auto_increment' || k === 'parent_id'; // A1: always hidden
    const hideSysWord = (k: string) => hideSystem && !!this.isSystemVariableKey && this.isSystemVariableKey(k); // set-block read-only rule
    const add = (entity: string, variable: string, label: string, type: string, empty?: boolean) => {
      const value = this.encodeReconVarName(blockType, parentType, entity, variable, this.AOBToBeAddedVariable || '');
      options.push({ value, entity, variable, label, type, ...(empty ? { empty: true } : {}) });
    };
    // Push-target restriction (restores the LOCAL-only filter the old PUSHABLE_ENTITY_LIST bucket
    // enforced before reconstruction): top-level entity-list vars may only target LOCAL entities —
    // pushing to a synced entity's top-level list creates a client-only row that never persists.
    // Inner AOB fields (entitySources loop below) stay unrestricted regardless of sync status.
    const localOnlyIds = excludeAaob
      ? new Set((this.pageService.newEntityListForSelected || []).filter((e: any) => e.status === 'LOCAL').map((e: any) => e.id))
      : null;
    // Local / global top-level variables. A variable's type is its DECLARATION (the Atlas maps), `getType`
    // only when nothing is declared — `getType(null)` is 'null', which is how an emptied variable vanished
    // from this picker (zavo_Sep_06). An emptied ENTITY variable has no entityId to narrow on, so it is
    // offered without the same-entity gate rather than silently dropped by it.
    const pageMap = (this.pageData as any)?.atlasVariableMap;
    const globalMap = (this.globalVariablesService?.globalVariable as any)?.['atlasMeta']?.map;
    const getTypeFn = (x: any) => this.getType(x);
    for (const key of this.getKeys(this.pageData?.localVariable || {})) {
      const v = this.pageData.localVariable[key];
      const picked = pickerVariableType(key, v, pageMap, 'local', getTypeFn);
      if (!picked || !types.includes(picked.type)) continue;
      const t = picked.type;
      if (hideSysWord(key)) continue;
      // update: never a built-in target; an emptied variable has no entityId, so read its declaration
      if (excludeSystemEntityVariables && isSystemEntityRef(v?.entityId || declaredEntityOf(pageMap, 'local', key, v))) continue;
      if (doNarrow && !picked.empty && eid !== v?.entityId) continue;
      if (localOnlyIds && v?.entityId && !localOnlyIds.has(v.entityId)) continue; // push: LOCAL entities only
      add('local_variable', key + suffix, key, t, picked.empty);
    }
    for (const key of this.getKeys((this.globalVariablesService?.globalVariable) || {})) {
      const v = this.globalVariablesService.globalVariable[key];
      const picked = pickerVariableType(key, v, globalMap, 'global', getTypeFn);
      if (!picked || !types.includes(picked.type)) continue;
      const t = picked.type;
      if (hideSysWord(key)) continue;
      if (excludeSystemEntityVariables && isSystemEntityRef(v?.entityId || declaredEntityOf(globalMap, 'global', key, v))) continue;
      // IMPORT GATE — the registry does not mint a token for this global, so the workspace cannot bind
      // it and the Atlas map has tombstoned it. Offering it here is what fired the [lazy-create] alert.
      if (blockedGlobals.has(key)) {
        if (!doNarrow || eid === v?.entityId) {
          withheld.count++;
          if (v?.entityId) withheld.entityIds.add(v.entityId);
        }
        continue;
      }
      if (doNarrow && !picked.empty && eid !== v?.entityId) continue;
      if (localOnlyIds && v?.entityId && !localOnlyIds.has(v.entityId)) continue; // push: LOCAL entities only
      add('global_variable', key + suffix, key, t, picked.empty);
    }
    // Database entities (incl. reference-descended, %-path ids) + local/global page-entity lists.
    const entitySources: any[] = [
      ...(this.pageService.newEntityTransformedListForSelected || []),
      ...(this.pageService.localEntityList || []),
      ...(this.pageService.globalEntityList || []),
    ];
    for (const ent of entitySources) {
      if (!ent?.id) continue;
      if (excludeSystemEntityFields) {
        let owner = '';
        try { owner = this.resolveEntityIdByEntityAndVariable(ent.id) || ''; } catch { owner = ''; }
        if (isSystemEntityRef(owner) || isSystemEntityRef(ent.id)) continue; // no field of a built-in record is a write target
      }
      // Same gate, second path: globalEntityList projects the FIELDS of a global's record, and the
      // registry skips those by root name too. `local_variable%…` / entity ids return null and pass.
      // COUNTED like the holder above, because a narrowed panel can be emptied by THIS skip alone —
      // e.g. narrowing to `territory` when the only match is a field inside a withheld global.
      const rootGlobal = globalEntityRootName(ent.id);
      if (rootGlobal && blockedGlobals.has(rootGlobal)) {
        withheld.count++;
        const rootEntityId = this.globalVariablesService?.globalVariable?.[rootGlobal]?.entityId;
        if (rootEntityId) withheld.entityIds.add(rootEntityId);
        continue;
      }
      for (const key of this.getKeys(ent.data || {})) {
        if (isSysField(key)) continue;
        let t = this.getType(ent.data[key]);
        if (t === 'null' && ent.fieldTypes?.[key]) t = ent.fieldTypes[key];
        if (!types.includes(t)) continue;
        if (hideSysWord(key)) continue;
        if (excludeSystemEntityVariables) {
          // update: a reference FIELD that points at a built-in record is never a write target either
          let target = '';
          try { target = this.resolveEntityIdByEntityAndVariable(ent.id + '%' + key) || ''; } catch { target = ''; }
          if (isSystemEntityRef(target)) continue;
        }
        if (doNarrow && eid !== this.resolveEntityIdByEntityAndVariable(ent.id + '%' + key)) continue;
        // AAOB (extendedAttributes) list fields must NOT be push/write targets — pushing into an AAOB bypasses
        // the (systemKey,systemType) upsert (docs/blockly/aaob-model.md; restores the A4 exclusion dropped when
        // the PUSHABLE_ENTITY_LIST bucket was removed). extendedAttributes is stripped by createEntityData, so we
        // detect the AAOB by its injected systemKey column. Scoped to `excludeAaob` (push) so get_* reads that may
        // legitimately read an AAOB list stay unchanged (that read path is a separate, deferred gap).
        // Was: if (excludeAaob && this.pageService.isAaobValue((ent.data as any)[key])) continue;
        if ((excludeAaob || excludeAaobFields) && this.pageService.isAaobValue((ent.data as any)[key])) continue;
        add(ent.id + '$', key, key, t);
      }
    }
    return options;
  }

  /**
   * Atlas reconstruction enumerator for the AOB SCALAR pickers (set_aob_variable / get_aob_variable) — a
   * transcription of main's REFERENCE_ENTITY_VARIABLE_AOB two-step panel: step 1 = an AOB reference from
   * entityListReferenceEntity, step 2 = a scalar field inside it (+ one level into ENTITY_VARIABLE sub-
   * fields). Flattened to full `entity_reference_for_AOB$<refId>$$<field>` strings so the tree renders the
   * two steps as drill levels. Cleared rules (2026-07-12): writers (set_aob_variable) EXCLUDE AAOB refs,
   * readers (get_aob_variable) show them; system-word fields hidden on set. docs/blockly/atlas-reconstruction.md.
   */
  enumerateAobScalarForRecon(types: string[]): ReconOption[] {
    const options: ReconOption[] = [];
    const blockType = this.selectedBlock?.type || '';
    const parentType = this.selectedBlock?.getParent && this.selectedBlock.getParent() ? this.selectedBlock.getParent().type : undefined;
    const allowsAaob = blockType.startsWith('get_') || blockType === 'set_reference_entity_variable_for_AOB'; // AAOB read-only
    // set_aob_variable is in SYSTEM_SET_VISIBLE_BLOCKS → hideSystem false (may set system-word fields; stripped on write).
    const hideSystem = this.hideSystemForWritePicker(blockType);
    const isSysField = (k: string) => k === 'id' || k === 'id_auto_increment' || k === 'parent_id';
    const hideSysWord = (k: string) => hideSystem && !!this.isSystemVariableKey && this.isSystemVariableKey(k);
    for (const ref of this.pageService.entityListReferenceEntity || []) {
      if (!ref?.id) continue;
      if (this.isSetContextBlock(blockType)) {
        let owner = '';
        try { owner = this.resolveEntityIdByEntityAndVariable(ref.id) || ''; } catch { owner = ''; }
        if (isSystemEntityRef(owner) || isSystemEntityRef(ref.id)) continue; // a built-in record's list is never written
      }
      if (!allowsAaob && ref.extendedAttributes === true) continue; // AAOB write-restricted → hidden from set_aob_variable
      const add = (aobVar: string, label: string, type: string) => {
        const value = this.encodeReconVarName(blockType, parentType, 'entity_reference_for_AOB', ref.id, aobVar);
        options.push({ value, entity: 'entity_reference_for_AOB', variable: ref.id, aobVar, label, type });
      };
      for (const key of this.getKeys(ref.data || {})) {
        if (isSysField(key)) continue;
        let t = this.getType(ref.data[key]);
        if (t === 'null' && ref.fieldTypes?.[key]) t = ref.fieldTypes[key];
        if (types.includes(t) && !hideSysWord(key)) add(key, key, t);
        // one level into an ENTITY_VARIABLE sub-object (main's panel does this)
        if (t === 'ENTITY_VARIABLE' && ref.data[key] && typeof ref.data[key] === 'object') {
          for (const innerKey of this.getKeys(ref.data[key])) {
            // CLEARED DEVIATION (2026-07-13, audit Finding 1): main hid id/id_auto_increment only on the
            // OUTER key (html:582), so it SHOWED inner id/id_auto_increment. We hide system ids on the inner
            // sub-field too (isSysField(innerKey) = id/id_auto_increment/parent_id) — cleared by the product
            // owner as the sensible/consistent behaviour (never pick a system id as a scalar target).
            if (isSysField(innerKey)) continue;
            const it = this.getType(ref.data[key][innerKey]);
            if (types.includes(it) && !hideSysWord(key) && !hideSysWord(innerKey)) add(key + '.' + innerKey, key + ' -- ' + innerKey, it);
          }
        }
      }
    }
    return options;
  }

  /**
   * Render an existing list-mode picker's `AtlasListItem[]` as the reconstruction drill tree (REUSE
   * buildReconTree — the last step of retiring `list` mode). Enumeration is UNTOUCHED; this is display-only,
   * and `key` = the item's `value` VERBATIM so the pick handler + `encodedId` contract are unchanged. Modes:
   *  - `'default'`  — value = item.value as-is (single-segment → a leaf under "New Entry"; a `%`-encoded value
   *    nests under Local/Global Variable, e.g. AAOB_UPSERT's `local_variable%<var>` targets).
   *  - `'dotDrill'` — item.value is a dotted field path (`ref.inner`): render it as a drill (`ref → inner`)
   *    while the pick key stays the dotted string (the reference-AOB pick splits `encodedId` on '.').
   *  - `'flatLeaf'` — item.value is a compound pick id (e.g. `<entityId>$$<field>`) that must render as ONE
   *    leaf: display item.label, keep the full value as the pick key (AGGREGATE_ENTITY column step).
   */
  private reconFromListItems(items: AtlasListItem[], mode: 'default' | 'dotDrill' | 'flatLeaf' = 'default') {
    const recon: ReconOption[] = (items || []).map((it) => {
      const raw = String(it.value);
      if (mode === 'dotDrill') return { value: raw.split('.').join('$'), key: raw, label: (raw.split('.').pop() || it.label) };
      if (mode === 'flatLeaf') return { value: String(it.label), key: raw, label: it.label };
      return { value: raw, key: raw, label: it.label };
    });
    return reconTreeToAtlasData(buildReconTree(recon));
  }

  /**
   * Atlas reconstruction enumerator for the FORGE PLAN CARD (swap / browse) — the migration off `buildAtlasData`.
   * Walks the SAME `pageContext` buildAtlasData did (local + global variables and their entity fields; database
   * entities + entity-list references and their fields) and emits one ReconOption per encoded id. Because
   * `value` IS the pageContext `encodedId` (already `$`/`%`-encoded), `buildReconTree` re-nests it into the drill
   * and the pick hands `onForgeAtlasSwap`/`forgeSwapVariable` that exact id — UNCHANGED. `id`/`id_auto_increment`/
   * `parent_id` are always hidden (parity with `isSystemEntityFieldName`); system-word only when `hideSystem`
   * (the Forge card passes false, faithful to buildAtlasData's `isSetContextBlock()===false`). `allowedTypes`
   * (SWAP): keep only options of that type; a parent whose own type is filtered still appears as a drill-through
   * intermediate because its matching leaf carries the parent segment. `localOnly` (sandboxed page): locals only.
   */
  private enumerateForgeVarsForRecon(ctx: PageContext, opts?: { allowedTypes?: string[]; localOnly?: boolean; hideSystem?: boolean }): ReconOption[] {
    // Delegates to the SHARED enumerator (atlas-reconstruct.util) so the Forge card + Spotlight walk pageContext
    // with ONE implementation. See enumerateAtlasVarsForRecon.
    return enumerateAtlasVarsForRecon(ctx, opts);
  }

  toBeAddedVaraibleChanged() {
    var varName = '';
   // AAOB-CACHE-INVARIANT: filter-cache blocks store the AAOB field name
   // directly as the VAR value — no entity prefix, no separators. The
   // generator emits `<aaobFieldName>: { system_aaobCache: ... }`.
   if (this.selectedBlock.type == 'aaob_filter_by_label' || this.selectedBlock.type == 'aaob_label_exists' || this.selectedBlock.type == 'aaob_sort_by_label') {
    varName = this.variableToBeAdded.variable || 'NANAAOB_FIELD';
    this.checkVariableandCreate(varName, 'AAOB_FIELD');
    this.selectedBlock.setFieldValue(varName, 'VAR');
    return;
   }
   // Encoding extracted to encodeReconVarName() so the Atlas reconstruction enumerator shares it verbatim.
   varName = this.encodeReconVarName(
     this.selectedBlock.type,
     this.selectedBlock.getParent && this.selectedBlock.getParent() ? this.selectedBlock.getParent().type : undefined,
     this.variableToBeAdded.entity,
     this.variableToBeAdded.variable,
     this.AOBToBeAddedVariable
   );

if(this.AOBToBeAddedAllowedType == '' || this.AOBToBeAddedAllowedType == 'ENTITY_LIST_VARIABLE')
this.checkVariableandCreate(varName , this.variableToBeAdded.allowedType);
else if(this.AOBToBeAddedAllowedType == 'STRING')
this.checkVariableandCreate(varName , 'String');
else if(this.AOBToBeAddedAllowedType == 'BOOLEAN')
this.checkVariableandCreate(varName , 'Boolean');
else if(this.AOBToBeAddedAllowedType == 'NUMBER')
this.checkVariableandCreate(varName , 'Number');
else if(this.AOBToBeAddedAllowedType == 'TIMESTAMP')
this.checkVariableandCreate(varName , 'TimeStamp');
else if(this.AOBToBeAddedAllowedType == 'GEOMETRY')
this.checkVariableandCreate(varName , 'GEOMETRY');
  this.selectedBlock.setFieldValue(
    varName,
    'VAR'
  );
    if (this.selectedBlock.type == 'set_entity_list_variable' || this.selectedBlock.type == 'set_entity_variable' || this.selectedBlock.type == 'push_to_entity_list' || this.selectedBlock.type == 'set_variable_entity' ||  this.selectedBlock.type == 'get_entity_length')
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['VALUE']);
  }

  //End Variables adding Start set and get
  checkVariableandCreate(varName: string , allowedType: string, intentionalCreate = false)
  {
    const existingVar = this.workspace.getVariableById(varName);

    if (existingVar) {
      // The variable already exists.
    } else {
      /**
       * [LAZY-CREATE-ALERT] ⚠ THE BLOCKING alert() IS DELIBERATE AND STAYS. Do not "tidy" it into a
       * console.warn.
       *
       * This token was NOT pre-registered at init, so it is being lazily created here — which means
       * `generateVariables` should have emitted it and did not. `preExisted === false` ⇔ this fires.
       *
       * ⚠ IT IS LOUD ON PURPOSE (owner, 2026-09-08). A console.warn is scrolled past, and a bug
       * nobody sees is a bug nobody fixes. The whole value of this diagnostic is that it INTERRUPTS —
       * downgrading it to a silent warning does not make the alert less annoying, it makes the class
       * of bug invisible. It was tagged "TEMP DEBUG (remove before commit)" until that decision; the
       * tag was wrong and told the next reader to delete a deliberate instrument.
       *
       * ⚠ STILL NOT FOR PRODUCTION. `zavo` is the dev line and a modal is fine there. A real user must
       * never meet it, so this has to go — or become non-blocking — before any prod release. That
       * question is deferred, not answered.
       *
       * Skipped when the caller KNOWS the variable is new (e.g. Atlas add-variable).
       */
      if (!intentionalCreate) {
        alert(`[lazy-create] token NOT pre-registered at init:\n\n${varName}\n\ntype: ${allowedType}\nblock: ${this.selectedBlock?.type}`);
      }
      this.workspace
        .getVariableMap()
        .createVariable(varName, allowedType, varName);
    }
  }
  //Start query graph ql start

  getParenEntityFromEntityNameFlatWithParent(value: string) {
    for (let entity of this.entityNameFlatWithParent) {
      if (value == entity.id) {
        return value;
      }
      for (let variables of entity.innerEntites) {
        if (value == variables.id) {
          return entity.id;
        }
      }
    }
    return '';
  }
  childrenEntiesofSelectedEntity: {
    name: string;
    id: string;
    parent: string;
  }[] = [];
  getChildrenEntiesofSelectedEntity(entityId: string) {
    for (let entity of this.pageService.newEntityListForSelected) {
      if (entity.id == entityId) {
        this.childrenEntiesofSelectedEntity = this.getChildrenRecursively(
          entity.data,
          entityId,
          true
        );
        break;
      }
      this.childrenEntiesofSelectedEntity = this.getChildrenRecursively(
        entity.data,
        entityId
      );
    }
  }

  getChildrenRecursively(
    EntityData: TypeData[],
    entityId: string,
    startFlag: boolean = false
  ): { name: string; id: string; parent: string }[] {
    var arrayOfChildren: { name: string; id: string; parent: string }[] = [];
    for (let data of EntityData) {
      if (data.type == 'ARRAY_OF_OBJECT') {
        if (startFlag) {
          arrayOfChildren.push({
            name: data.name,
            id: data.id,
            parent: entityId,
          });
        }
        if (data.id == entityId) {
          arrayOfChildren = this.getChildrenRecursively(
            data.data,
            entityId,
            true
          );
        } else {
          arrayOfChildren = [
            ...arrayOfChildren,
            ...this.getChildrenRecursively(data.data, entityId, startFlag),
          ];
        }
      }
    }
    return arrayOfChildren;
  }
  /** The page's imported entities with their resolved shapes: what the query field pickers walk. */
  entityListForFieldPickers() {
    return withResolvedShapes(this.pageService.newEntityListForSelected, this.pageService.entityListForShapes);
  }
  async getVariableChildrenEntiesofSelectedEntity(entityId: string, type: string, data: any) {
    for (let entity of data) {
      if (entity.id == entityId) {
        this.childrenEntiesofSelectedEntity =
         await this.getVariableFromChildrenRecursively(
            entity.data,
            entityId,
            type,
            true
          );
          this.childrenEntitesAllowedType = type;
        return true;
      }
      for(let aob of entity.data) {
        if(aob.id == entityId) {
                 this.childrenEntiesofSelectedEntity =
         await this.getVariableFromChildrenRecursively(
            aob.data,
            entityId,
            type,
            true
          );
          this.childrenEntitesAllowedType = type;
        return true;
        } else if( aob.type == "ARRAY_OF_OBJECT") {
          const returnedFlag = await this.getVariableChildrenEntiesofSelectedEntity(entityId , type , aob.data);
          if(returnedFlag) {
            return true;
          }
        }
      }
    }

      return false;
  }
  childrenEntitesAllowedType = '';
  async getVariableFromChildrenRecursively(
    EntityData: TypeData[],
    entityId: string,
    type: string,
    startFlag: boolean = false,
    variableEntityName: string = '',
    // The resolver's loop rule, starting empty: ends the walk even on a tree that points back at itself.
    refPath: readonly string[] = []
  ): Promise<{ name: string; id: string; parent: string; }[]> {
    var arrayOfChildren: { name: string; id: string; parent: string }[] = [];
    for (let data of EntityData) {
              if (startFlag) {
      if (data.type == 'UUID' && type == 'STRING') {

          arrayOfChildren.push({
            name: variableEntityName + '$' + data.name,
            id: variableEntityName + data.name,
            parent: entityId,
          });
        
      }
      if (data.type == 'AUTO_INCREMENT' && type == 'NUMBER') {

          arrayOfChildren.push({
            name: variableEntityName + '$' + data.name,
            id: variableEntityName + '$'  + data.name,
            parent: entityId,
          });
        
      }
      if (data.type == type) {
          arrayOfChildren.push({
            name: variableEntityName + '$' + data.name,
            id: variableEntityName + '$'  + data.name,
            parent: entityId,
          });
        
      }
       // `EntityData` is a resolved shape: a reference is entered only where the resolver followed it, never into
       // a stored copy.
       if(data.type == 'ENTITY_REFERENCE' && (data as any).reach === 'followed' && followsReference(refPath, data.referenceEntityName)) {
        // console.log(data);
        // console.log(await this.getVariableFromChildrenRecursively(
        //       data.data,
        //       entityId,
        //       type,
        //       startFlag,
        //       variableEntityName + '%' + data.referenceEntityName+ '_' + data.name,
        //     ));
        // need to consider for inner variable of a entity reference but needs to be taken care of in the code also.
        const returnedData = await this.getVariableFromChildrenRecursively(
              data.data,
              entityId,
              type,
              startFlag,
              variableEntityName + '%' + data.referenceEntityName+ '_' +data.name,
              enterReference(refPath, data.referenceEntityName),
            );
          arrayOfChildren = [
            ...arrayOfChildren,
            ...returnedData,
          ];
      }
    } else if (data.type == 'ARRAY_OF_OBJECT') {
        if (data.id == entityId) {
          arrayOfChildren = await this.getVariableFromChildrenRecursively(
            data.data,
            entityId,
            type,
            true,
            '',
            refPath
          );
        } else {
              const returnedData = await this.getVariableFromChildrenRecursively(
              data.data,
              entityId,
              type,
              startFlag,
              '',
              refPath
            );
          arrayOfChildren = [
            ...arrayOfChildren,
            ...returnedData,
          ];
        }
      } 
    }
    return arrayOfChildren;
  }
  // Phase 2 query-capabilities: writeback for entity_aggregate_query.
  // Two-dropdown HTML panel — entity (variableToBeAdded.variable) and
  // Number column (aggregateColumnVar). Pass `entityChanged: true` from the
  // entity dropdown's change event so we cascade-clear stale state — column
  // and nested CONDITIONS/EC sockets may reference fields of the old entity.
  // Mirrors the existing toBeAddedEntityChanged() pattern below.
  // See docs/plans/query-capabilities-phase-2.md §4.4 / §6.1.
  aggregateBlockChanged(entityChanged: boolean = false) {
    if (!this.selectedBlock || this.selectedBlock.type !== 'entity_aggregate_query') return;

    if (this.variableToBeAdded.variable) {
      this.checkVariableandCreate(this.variableToBeAdded.variable, 'ENTITYNAME');
      this.selectedBlock.setFieldValue(this.variableToBeAdded.variable, 'VAR');
    }

    if (entityChanged) {
      this.aggregateColumnVar = '';
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['CONDITIONS', 'EC']);
    }

    if (this.aggregateColumnVar) {
      const taggedCol = this.aggregateColumnVar + '__AGG_NUM';
      this.checkVariableandCreate(taggedCol, 'NUMBER');
      this.selectedBlock.setFieldValue(taggedCol, 'COLUMN');
    }
  }

  // Phase 2 (Phase D) — writeback for aaob_aggregate_by_label.
  // VAR is the entity ID, AAOB_FIELD is the AAOB field name. systemKey is a
  // free-text input (separate text block), not handled here.
  // entityChanged=true cascades a clear of the AAOB field + child sockets,
  // since AAOBs are entity-scoped.
  aaobAggregateBlockChanged(entityChanged: boolean = false) {
    if (!this.selectedBlock || this.selectedBlock.type !== 'aaob_aggregate_by_label') return;

    if (this.variableToBeAdded.variable) {
      this.checkVariableandCreate(this.variableToBeAdded.variable, 'ENTITYNAME');
      this.selectedBlock.setFieldValue(this.variableToBeAdded.variable, 'VAR');
    }

    if (entityChanged) {
      this.aaobAggregateField = '';
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['CONDITIONS', 'EC', 'KEY']);
    }

    if (this.aaobAggregateField) {
      this.checkVariableandCreate(this.aaobAggregateField, 'AAOB_FIELD');
      this.selectedBlock.setFieldValue(this.aaobAggregateField, 'AAOB_FIELD');
    }
  }

  // for adding the selected varaible from the entity to the custom block whichi is selected
  toBeAddedEntityChanged() {
    this.variableToBeAdded.variable = this.variableToBeAdded.entity;
    this.selectedBlock.setFieldValue(this.variableToBeAdded.variable, 'VAR');
    this.setChildBlockToDefaultQuery(this.selectedBlock, [
      'EC',
      'E',
      'CONDITIONS',
      'SIBILING_CONDITIONS',
    ]);
  }
  toBeAddedEntityVariableChanged() {
    this.selectedBlock.setFieldValue(this.variableToBeAdded.variable, 'VAR');
    this.setChildBlockToDefaultQuery(this.selectedBlock, [
      'E',
      'CONDITIONS',
      'SIBILING_CONDITIONS',
    ]);
  }
  toBeAddedEntityVariableChangedForCondition() {
    if (this.variableToBeAdded.variable.includes("%")) {
    const [before, after] = this.variableToBeAdded.variable.split("$");
    this.variableToBeAdded.entity = this.variableToBeAdded.entity + before;
    this.variableToBeAdded.variable = after;
  } else {
    this.variableToBeAdded.variable = this.variableToBeAdded.variable.replace('$' , '');
  }
 
    if (
      this.selectedBlock.type == 'get_query' ||
      this.selectedBlock.type == 'entity_conditions' || this.selectedBlock.type == 'get_set_order_variable'
    ) {
      this.checkVariableandCreate(this.variableToBeAdded.variable, this.childrenEntitesAllowedType);
      this.selectedBlock.setFieldValue(this.variableToBeAdded.variable, 'VAR');
    }
    else {
      this.checkVariableandCreate(this.variableToBeAdded.variable + '$' + this.variableToBeAdded.entity , this.childrenEntitesAllowedType);
      this.selectedBlock.setFieldValue(
        this.variableToBeAdded.variable + '$' + this.variableToBeAdded.entity,
        'VAR'
      );
    }
    this.setChildBlockToDefaultQuery(this.selectedBlock, [
      'E',
      'CONDITIONS',
      'SIBILING_CONDITIONS',
    ]);
  }

  setChildBlockToDefaultQuery(parentBlock: any, blockNames: string[]) {
    // Get the first connected block (child block) in the statement input

    for (let blockName of blockNames)
      if (parentBlock.getInput(blockName)) {
        var inputConnection = parentBlock.getInput(blockName).connection;
        var childBlock = inputConnection && inputConnection.targetBlock();
        if (childBlock) {
          if (childBlock.type == 'entity_conditions') {
            childBlock.setFieldValue('NANENTITY', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, ['E', 'CONDITIONS']);
          } else if (childBlock.type == 'get_query') {
            childBlock.setFieldValue('NANENTITY', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, [
              'EC',
              'E',
              'CONDITIONS',
            ]);
          }  else if (childBlock.type == 'entity_set_string_in_conditions') {
            childBlock.setFieldValue('NANENTITYSTRING', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, [
              'SIBILING_CONDITIONS',
            ]);
          }  else if (childBlock.type == 'entity_set_time_stamp_in_conditions') {
            childBlock.setFieldValue('NANENTITYTIMESTAMP', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, [
              'SIBILING_CONDITIONS',
            ]);
          } else if (childBlock.type == 'entity_set_number_in_conditions') {
            childBlock.setFieldValue('NANENTITYNUMBER', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, [
              'SIBILING_CONDITIONS',
            ]);
          } else if (childBlock.type == 'entity_set_boolean_in_conditions') {
            childBlock.setFieldValue('NANENTITYBOOLEAN', 'VAR');
            this.setChildBlockToDefaultQuery(childBlock, [
              'SIBILING_CONDITIONS',
            ]);
          } else if (childBlock.type == 'entity_or_conditions') {
            this.setChildBlockToDefaultQuery(childBlock, [
              'CHILD_CONDITIONS',
              'SIBILING_CONDITIONS',
            ]);
          } else if (childBlock.type == 'get_entity_variable') {
            childBlock.setFieldValue('NAN_ENTITY_VARIABLE', 'VAR');
          } else if (
            childBlock.type == 'set_entity_variable_by_index_of_entity_list'
          ) {
            this.setChildBlockToDefaultQuery(childBlock, ['VALUE']);
          } else if (childBlock.type == 'get_entity_list_variable') {
            childBlock.setFieldValue('NANENTITY_LIST_VARIABLE', 'VAR');
          }
      }
    }
  }

  // end of entity for Array of Objects
  async createDefinitionsForFunctions() {
    // Body EXTRACTED (unchanged) to helpers/api-function-blocks.ts. The QUERY stays here because it
    // needs the injected service; the REGISTRATION is now data-in, so a headless runner can do the
    // same with a list it fetched any way it likes. Delegation, not removal.
    const funcs = await this.functionsService.query(500);
    registerApiFunctionBlocks(funcs as any[]);
  }

  /* → moved to helpers/misc-block-definitions.ts:
     customBlockForCustomLoop, customBlockForRegexMatchString,
     customBlockForStringContains, customBlockForFormatIsoDate,
     customBlockForParseDateToIso, customBlockForNumberToWords,
     customBlockForTrimDecimals, customBlockForAndConditionInQuery */

  // for searchable dropdown on functions
  dropdownOpen = false;
searchText = "";

toggleDropdown() {
  this.dropdownOpen = !this.dropdownOpen;
}

selectFunction(name: string) {
  this.dropdownOpen = false;
  this.selectedFunctionName = name;
  this.functionSelected();

  this.forgeFunctionName = this.functionName;
  this.forgePrompt = '';
  this.forgeMessages = [];
  this.forgeError = '';
  this.forgeLoadResult = null;
  this.forgeChatState = 'ready';

  this.updateLivePlan();
}

filteredFunctions() {
  const list = this.editorMode === 'conditional'
    ? (this.pageData.conditionalFunctions ?? [])
    : this.editorMode === 'value'
      ? (this.pageData.valueFunctions ?? [])
      : this.pageData.functions;
  if (!this.searchText) return list;
  return list.filter((fn: any) =>
    fn.name.toLowerCase().includes(this.searchText.toLowerCase())
  );
}
// for searchable dropdown on functions end

// REMOVED 2026-07-30 — the ref-link walk now lives on VariableRegistryService.entityReferenceCrudBaseNames
// (the token-enumeration authority), so both the click-time picker (its one caller, above) and the init-time
// `refLink` registry lane call ONE implementation and enumerate byte-identical tokens. This wrapper added
// nothing but an alias, so the caller references the service method directly. Kept commented per
// comment-don't-delete; the original inline walk is preserved verbatim inside.
// getEntityReferenceVariableNamesFromEntity(data: TypeData[] , parentName: string = '') {
//   var returnData : any[] = [];
//   for(let i=0 ; i < data.length ; i++) {
//      if(data[i].type == 'ENTITY_REFERENCE' && data[i].name !== 'parent_id' ) {
//       returnData.push(parentName + data[i].name + '%' + data[i].referenceEntityName);
//      }
//       if(data[i].type == 'ARRAY_OF_OBJECT' || data[i].type == 'ENTITY_REFERENCE') {
//       const innerData = this.getEntityReferenceVariableNamesFromEntity(data[i].data , parentName + data[i].name + '$');
//       returnData = [...returnData , ...innerData];
//   }
// }
// return returnData;
// }

// ── Forge (AI function generation) ──────────────────────────────────────

forgeState: 'idle' | 'prompt' | 'planning' | 'plan-review' | 'generating' | 'done' | 'error' = 'prompt';
forgePrompt = '';
forgeFunctionName = '';
forgePlan: ForgePlan | null = null;
forgeProgress = '';
forgeProgressPhase: ForgePhase = 'GENERATING';
forgeError = '';
forgeUsageMap: Map<string, VariableUsage> = new Map();
/** LOCAL vocabulary only — plan rendering, variable extraction, loadStepsToWorkspace. NEVER sent to the CF. */
private forgePageContext: PageContext | null = null;
/** Non-fatal outcomes of the Atlas selection: its own warnings, hydration notes, creates it could not make. */
forgeAtlasWarnings: string[] = [];
/** Set when Forge stopped to ask for variables the selection did not contain. Terminal for that run. */
forgeContextRequests: ForgeContextRequest[] = [];
forgeAtlasOpen = false;
forgeAtlasBrowseOnly = false;
forgeAtlasData: AtlasData | null = null;
get forgeAtlasEntities(): { id: string; name: string }[] {
  return (this.pageService.newEntityListForSelected || []).map((e: any) => ({ id: e.id, name: e.name }));
}
get forgeAtlasImportableEntities(): { id: string; name: string }[] {
  const imported = new Set(this.pageData?.entites || []);
  return (this.entityList || []).filter(e => !imported.has(e.id)).map(e => ({ id: e.id, name: e.name }));
}
forgeAtlasIcons: AtlasIconEntry[] = [];
forgeAtlasAllowedTypes: string[] = [];   // the swap slot's type(s) — filters the reconstruction enumerator
forgeAtlasSwapEncodedId: string | null = null;
forgeAtlasMode: 'reconstruct' = 'reconstruct';
forgeAtlasListItems: AtlasListItem[] = [];   // temp holder for the reference-AOB ref-entity list before reconFromListItems
forgeAtlasRefSegment: PlanBlockSegment | null = null;
forgeInlineEdit: { active: boolean; refKind: SegmentRefKind; oldValue: string; step: PlanBlockStep | null } = { active: false, refKind: 'stringLiteral', oldValue: '', step: null };
forgeInlineEditValue = '';
blockAtlasActive = false;
private blockAtlasField: 'VAR' | 'COLUMN' | 'AAOB_FIELD' | 'AOB_INNER' | 'PUSH_FIELD' = 'VAR';
/** Condition builder (Phase 3): field options for the active target, looked up by index on select. */
private conditionPickFields: FieldOption[] = [];
forgeLoadResult: LoadResult | null = null;
private forgeAbortController: AbortController | null = null;
forgeIsModify = false;
private forgeCurrentSteps: ForgeStep[] | null = null;
forgePanelOpen = true;
forgeView: 'forge' | 'blockly' = 'forge';
forgeLegendOpen = false;
forgeLegendHover = false;
private forgeLegendShown = false;

planCardMode: PlanCardMode = 'empty';
forgeMessages: ForgeMessage[] = [];
forgeChatState: 'ready' | 'sending' = 'ready';
chatExpanded = true;
forgeHistoryIndex = -1;
forgePlanVersion = 0;
// True when the user has manually edited the plan (variable swap / inline edit) but
// has NOT yet pushed those edits into the Blockly workspace. Drives the "Apply changes"
// button so manual plan edits aren't stranded. Cleared on forgeApply().
forgePlanDirty = false;
forgeContextCutIndex = -1;

openForge() {
  this.forgeState = 'prompt';
  this.forgePrompt = '';
  this.forgeFunctionName = '';
  this.forgePlan = null;
  this.forgeProgress = '';
  this.forgeError = '';
  this.forgeLoadResult = null;
  this.forgeIsModify = false;
  this.forgeCurrentSteps = null;
  this.planCardMode = 'empty';
  this.forgeMessages = [];
  this.forgeChatState = 'ready';
  this.resizeBlocklyWorkspace();
  if (!this.forgeLegendShown) {
    this.forgeLegendShown = true;
    this.forgeLegendOpen = true;
    setTimeout(() => { this.forgeLegendOpen = false; }, 4000);
  }
}

openForgeModify() {
  if (!this.functionName) return;
  this.forgeState = 'prompt';
  this.forgePrompt = '';
  this.forgeFunctionName = this.functionName;
  this.forgePlan = null;
  this.forgeProgress = '';
  this.forgeError = '';
  this.forgeLoadResult = null;
  this.forgeIsModify = true;
}

closeForge() {
  this.forgeCancel();
  if (this.standaloneMode) {
    this.standaloneMode = false;
    this.pageService.entryMode = 'spotlight';
    this.router.navigate(['/ui-editor']);
    return;
  }
  this.router.navigate(['/dashboard/pages']);
}

/**
 * Run Atlas selection and return the payload Forge takes (2026-08-05).
 *
 * WHY THIS EXISTS. Forge is v3: it takes an `atlasContext`, and the CF refuses a body carrying the old
 * `pageContext` BY NAME. This service kept sending `pageContext` for four days after that landed, so every
 * Forge run from the UI died at the door before a token was generated. A Forge run now BEGINS here.
 *
 * `null` means DO NOT CALL FORGE. `forgeError` carries the reason — EXCEPT on cancel, where it stays empty
 * because an abort is not a failure. The CALLER sets the state: this component drives two different Forge
 * surfaces (the plan flow on `forgeState`, the chat on `forgeChatState`) and only the caller knows which
 * vocabulary it is speaking. So the rule is: null + `forgeError` set → report it your way.
 *
 * There is deliberately no fallback: a run with no selection has nothing to send, and manufacturing an
 * empty AtlasContext would move the failure from a message the user can read into Forge's reasoning, where
 * it would surface as a bad plan instead of an explanation.
 *
 * NOTHING IS SWALLOWED. `AtlasRunResult` reports warnings, rejected creates and the round count precisely
 * so a caller does not have to guess; a create Atlas asked for and could not make is the difference between
 * a good plan and a wrong one, so it is shown even when the run succeeds.
 *
 * The ask-back loop is NOT here yet. When Forge replies `context_request`, this is the function its
 * requests come back through — they are already the shape `resolveCreates` consumes.
 * See docs/plans/forge-atlas-ask-back-loop.md §3.
 */
private async resolveAtlasContext(prompt: string, functionName?: string): Promise<AtlasContext | null> {
  this.forgeAtlasWarnings = [];
  this.forgeContextRequests = [];
  this.forgeProgressPhase = 'SELECTING';
  this.forgeProgress = 'Picking the variables this needs...';

  try {
    const run = await this.atlasAgentService.runSelection(this.pageData, prompt, {
      functionName,
      signal: this.forgeAbortController?.signal,
    });

    this.forgeAtlasWarnings = [
      ...run.warnings,
      ...run.rejected.map((r: any) => `Could not create "${r.purpose ?? r.name ?? 'a variable'}": ${r.reason}`),
    ];

    if (!run.context) {
      // Atlas ran and picked nothing. That is an ANSWER, not a crash — usually the prompt names something
      // the page does not have. Say so with its reasons rather than a bare failure.
      this.forgeError = this.forgeAtlasWarnings.length
        ? `Atlas found nothing to build this from:\n- ${this.forgeAtlasWarnings.join('\n- ')}`
        : 'Atlas found no variables or entities matching this request.';
      return null;
    }
    return run.context;
  } catch (e: any) {
    if (e?.name === 'AbortError') return null;   // cancel is not a failure — forgeError stays empty
    this.forgeError = `Atlas selection failed: ${e?.message ?? e}`;
    return null;
  }
}

async forgePlanRequest() {
  if (!this.forgePrompt.trim()) return;
  if (!this.forgeIsModify && !this.forgeFunctionName.trim()) return;

  this.forgeState = 'planning';
  this.forgeProgress = '';
  this.forgeError = '';
  this.forgeAbortController = new AbortController();

  this.forgeUsageMap = computeVariableUsage(this.pageData);

  const atlasContext = await this.resolveAtlasContext(this.forgePrompt, this.forgeFunctionName);
  if (!atlasContext) {
    if (this.forgeError) this.forgeState = 'error';   // empty forgeError = cancelled, not failed
    return;
  }
  // pageContext stays LOCAL — plan text, variable extraction, loadStepsToWorkspace. It is not sent.
  // Built AFTER the selection (2026-09-28): Atlas creates variables inside it, and a context taken before
  // did not know them — `forgeApprove` then compiled a new record variable's list as an unknown token, and
  // the delete that named it saved as NAN. (.scratch/forge-nested-list-delete, olympus-functions)
  const pageContext = this.buildForgePageContext();
  this.forgePageContext = pageContext;

  // The two events this component used to drop. context_request is TERMINAL — without this branch the UI
  // sat in 'planning' forever, with no plan and no error.
  const onContextRequest = (requests: ForgeContextRequest[]) => {
    this.forgeContextRequests = requests;
    this.forgeError = requests.length
      ? `Forge needs variables this page does not have:\n- ${requests.map(r => `${r.purpose} (${r.type}, ${r.scope}) — ${r.reason}`).join('\n- ')}`
      : 'Forge asked for missing context but did not say what.';
    this.forgeState = 'error';
  };
  const onValidation = (errors: string[], attempt: number) => {
    // NOT terminal — the CF repairs and retries, and a plan can still arrive.
    this.forgeProgressPhase = attempt === 0 ? 'VALIDATING' : 'FIXING';
    this.forgeProgress = errors.length
      ? `Fixing ${errors.length} validation issue${errors.length === 1 ? '' : 's'} (attempt ${attempt + 1})...`
      : 'Validating...';
  };

  if (this.forgeIsModify) {
    const currentSteps = decompileWorkspace(this.workspace, this.functionName);
    if (!currentSteps.length) {
      this.forgeError = 'No function loaded to modify';
      this.forgeState = 'error';
      return;
    }
    const currentSummary = formatPlanAsText(currentSteps, pageContext);
    this.forgeService.streamModify(
      { prompt: this.forgePrompt, currentSummary, atlasContext },
      {
        onContextRequest,
        onValidation,
        onProgress: (phase, message) => {
          this.forgeProgressPhase = phase;
          this.forgeProgress = message;
        },
        onPlan: (steps: ForgeStep[], warnings?: string[]) => {
          this.forgePlan = {
            functionName: this.forgeFunctionName,
            description: this.forgePrompt,
            steps,
            variablesUsed: extractVariablesFromSteps(steps, pageContext, this.forgeUsageMap),
            entitiesUsed: extractEntitiesFromSteps(steps, pageContext),
          };
          this.forgeState = 'plan-review';
        },
        onError: (message) => {
          this.forgeError = message;
          this.forgeState = 'error';
        },
      },
      this.forgeAbortController.signal
    );
  } else {
    this.forgeService.streamPlan(
      { prompt: this.forgePrompt, functionName: this.forgeFunctionName, atlasContext },
      {
        onContextRequest,
        onValidation,
        onProgress: (phase, message) => {
          this.forgeProgressPhase = phase;
          this.forgeProgress = message;
        },
        onPlan: (steps: ForgeStep[], warnings?: string[]) => {
          this.forgePlan = {
            functionName: this.forgeFunctionName,
            description: this.forgePrompt,
            steps,
            variablesUsed: extractVariablesFromSteps(steps, pageContext, this.forgeUsageMap),
            entitiesUsed: extractEntitiesFromSteps(steps, pageContext),
          };
          this.forgeState = 'plan-review';
        },
        onError: (message) => {
          this.forgeError = message;
          this.forgeState = 'error';
        },
      },
      this.forgeAbortController.signal
    );
  }
}

forgeApprove() {
  if (!this.forgePlan) return;

  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.clearWorkSpace();
  this.functionName = this.forgePlan.functionName;
  this.selectedFunctionName = '';

  const result = loadStepsToWorkspace(this.workspace, this.forgePlan.functionName, this.forgePlan.steps, pageContext);
  this.forgeLoadResult = result;
  this.updateCode();
  this.forgeState = 'done';
}

forgeCancel() {
  this.forgeAbortController?.abort();
  this.forgeAbortController = null;
}

forgeSave() {
  if (!this.functionName) return;
  this.addFunctinoToPage(this.forgePrompt || undefined);

  this.pageService.create(this.pageData);

  if (this.standaloneMode) {
    this.standaloneMode = false;
    this.pageService.entryMode = 'spotlight';
    this.router.navigate(['/ui-editor']);
    return;
  }

  this.selectedFunctionName = '';
  this.forgeFunctionName = '';
  this.forgePlan = null;
  this.forgeMessages = [];
  this.forgePrompt = '';
  this.planCardMode = 'empty';
  this.forgeView = 'forge';
}

forgeBackToPrompt() {
  this.forgeState = 'prompt';
  this.forgePlan = null;
}

forgeSwapVariable(oldId: string, newId: string) {
  if (!this.forgePlan || !this.forgePageContext) return;
  this.forgePlan.steps = swapVariableInSteps(this.forgePlan.steps, oldId, newId);
  this.forgePlan.blockSteps = summarizeForgeSteps(this.forgePlan.steps, this.forgePageContext);
  this.forgePlan.variablesUsed = extractVariablesFromSteps(
    this.forgePlan.steps, this.forgePageContext, this.forgeUsageMap
  );
  this.forgePlan.entitiesUsed = extractEntitiesFromSteps(
    this.forgePlan.steps, this.forgePageContext
  );
  this.forgePlanDirty = true;
}

getForgeUsageSummary(encodedId: string): string {
  const usage = this.forgeUsageMap.get(encodedId);
  if (!usage) return '';
  return formatUsageSummary(usage);
}

openAtlasForVar(encodedId: string | undefined, step?: PlanBlockStep) {
  if (!encodedId) return;
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  // Reconstruction SWAP picker (migrated off buildAtlasData). resolveVarType gives the slot's type(s); the
  // enumerator keeps only type-compatible options (a parent whose own type is filtered still drills through
  // to its matching leaves). Pick → onForgeAtlasSwap → forgeSwapVariable(oldId, encodedId), UNCHANGED.
  this.forgeAtlasAllowedTypes = this.resolveVarType(encodedId, pageContext);
  this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(
    this.enumerateForgeVarsForRecon(pageContext, { allowedTypes: this.forgeAtlasAllowedTypes, localOnly: this.isCurrentPageSandboxed() })
  ));
  this.forgeAtlasSwapEncodedId = encodedId;
  this.forgeAtlasMode = 'reconstruct';
  this.forgeAtlasListItems = [];
  this.forgeAtlasRefSegment = null;
  this.forgeAtlasOpen = true;
  this.refreshForgeAtlasIcons();
}

openAtlasBrowse() {
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  // Reconstruction BROWSE (migrated off buildAtlasData / atlas mode): the full variable tree, all types. No
  // swap context → picking a node just closes (view-only, as before). NOTE: the atlas-mode add-variable /
  // add-entity affordance does NOT exist in reconstruct mode, so it's dropped from browse (flagged — restoring
  // it would need reconstruct-mode add UI in the atlas component).
  this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(
    this.enumerateForgeVarsForRecon(pageContext, { localOnly: this.isCurrentPageSandboxed() })
  ));
  this.forgeAtlasSwapEncodedId = null;
  this.forgeAtlasAllowedTypes = [];
  this.forgeAtlasMode = 'reconstruct';
  this.forgeAtlasListItems = [];
  this.forgeAtlasRefSegment = null;
  this.forgeAtlasBrowseOnly = true;
  this.forgeAtlasOpen = true;
  this.refreshForgeAtlasIcons();
}

// (onForgeAtlasDataChanged + onForgeAtlasAddVariable REMOVED 2026-07-13 — they drove the atlas-mode
//  add-variable rebuild, which the reconstruction dropped along with the browse add UI.)

openAtlasForRef(segment: PlanBlockSegment, step?: PlanBlockStep) {
  if (!segment.refKind || segment.refValue === undefined) return;

  if (segment.refKind === 'stringLiteral' || segment.refKind === 'numberLiteral') {
    this.forgeInlineEdit = { active: true, refKind: segment.refKind, oldValue: segment.refValue, step: step || null };
    this.forgeInlineEditValue = segment.refValue;
    return;
  }

  const pageContext = this.forgePageContext || this.buildForgePageContext();
  // Reconstruction ref-swap picker (migrated off `list` mode). buildListItemsForRef stays the enumeration;
  // reconFromListItems renders it as the tree. Pick → onForgeAtlasSwap → handleRefSwap (refSegment set).
  this.forgeAtlasData = this.reconFromListItems(buildListItemsForRef(segment.refKind, segment.refValue, pageContext, segment.refEntity));
  this.forgeAtlasListItems = [];
  this.forgeAtlasMode = 'reconstruct';
  this.forgeAtlasRefSegment = segment;
  this.forgeAtlasSwapEncodedId = null;
  this.forgeAtlasOpen = true;
}

openAtlasForBlock() {
  if (!this.selectedBlock) return;

  // Deprecated typed scalar blocks (superseded by the unified set_variable/get_variable): clicking the VAR
  // does NOTHING — no Atlas opens, so they can't be re-picked; they only render saved XML. (cleared 2026-07-12)
  const DEPRECATED_TYPED_SCALAR_BLOCKS = new Set([
    'get_string_variable', 'set_string_variable', 'get_number_variable', 'set_number_variable',
    'get_boolean_variable', 'set_boolean_variable', 'get_time_stamp_variable', 'set_time_stamp_variable',
  ]);
  // Also the deprecated TYPED AOB scalar blocks (get_/set_reference_entity_variable_for_AOB_variable_*),
  // superseded by set_aob_variable/get_aob_variable — same do-nothing treatment. (cleared 2026-07-12)
  if (DEPRECATED_TYPED_SCALAR_BLOCKS.has(this.selectedBlock.type)
      || this.selectedBlock.type.includes('_reference_entity_variable_for_AOB_variable_')) {
    this.blockAtlasActive = false; return;
  }

  const allowedType = this.variableToBeAdded.allowedType;

  // allowedType 'NAN' = the block's narrowing found NO compatible target (e.g. set_reference_entity_variable_for_AOB's
  // Target List when no AOB list matches the selected Reference Entity — the onchange sets 'NAN' at its empty check).
  // It MUST show nothing. Before this guard 'NAN' was a no-op: it merely failed to match any picker branch, so
  // openAtlasForBlock fell through without refreshing/closing the overlay — leaving the previously shown list
  // (typically the Reference-Entity picker's FULL AOB list) on screen = "it shows a lot". Close instead.
  if (allowedType === 'NAN') {
    this.blockAtlasActive = false;
    this.forgeAtlasOpen = false;
    // Subtle, non-blocking feedback at click time (replaces the old blocking alert in the AOB onchange).
    this.toastService.showWarning('Top-level AOBs are not available for this block.');
    return;
  }

  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.blockAtlasActive = true;
  this.forgeAtlasRefSegment = null;
  this.forgeAtlasSwapEncodedId = null;
  this.refreshForgeAtlasIcons();

  // ── Atlas reconstruction — entity / list variable pickers ──────────────────────────────────────────
  // Replaces the flat bucket/pick view with the flat-string reconstruction so reference-descended entries
  // (entity → entityReference → field/AOB, N-deep) appear like main. Covers get_entity_list under push
  // (via isPushGetList) + every ENTITY_LIST_VARIABLE / ENTITY_VARIABLE block. Enumeration +
  // encoding reuse the existing helpers; Atlas only shows + picks. See docs/blockly/atlas-reconstruction.md.
  const reconParentType = this.selectedBlock.getParent && this.selectedBlock.getParent() ? this.selectedBlock.getParent().type : undefined;
  const isPushGetList = this.selectedBlock.type === 'get_entity_list_variable' && reconParentType === 'push_new_element_to_array';
  // zavo_oct_06: get entity list now accepts AAOB lists (reads). The other list WRITE parents must still not target
  // an AAOB (it would bypass the systemKey/systemType upsert) — exclude AAOB here, without push's LOCAL-only rule.
  const isListWriteGet = this.selectedBlock.type === 'get_entity_list_variable'
    && (reconParentType === 'delete_from_entity_list' || reconParentType === 'set_function_entity_list_at_index');
  if (isPushGetList || allowedType === 'ENTITY_LIST_VARIABLE' || allowedType === 'ENTITY_VARIABLE') {
    const typeFilter = allowedType === 'ENTITY_VARIABLE' ? 'ENTITY_VARIABLE' : 'ENTITY_LIST_VARIABLE';
    const reconOptions = this.enumerateVariablesForRecon({
      types: [typeFilter],
      listSuffix: typeFilter === 'ENTITY_LIST_VARIABLE',
      narrow: true, // main's ENTITY_LIST / ENTITY_VARIABLE panels apply the entityIdForGetEntity same-entity gate
      // System-word vars hidden in write blocks EXCEPT SYSTEM_SET_VISIBLE_BLOCKS — here set_entity_variable /
      // set_entity_list_variable (+ the reference-setter) show + set them (stripped on the create/update write).
      hideSystem: this.hideSystemForWritePicker(),
      excludeAaob: isPushGetList, // push targets exclude AAOB lists (restores A4); get_* reads are unaffected
      excludeAaobFields: isListWriteGet, // delete / set-at-index targets exclude AAOB lists too (zavo_oct_06)
      excludeSystemEntityVariables: reconParentType === 'update_entity',
      excludeSystemEntityFields: this.isSetContextBlock() || isPushGetList || isListWriteGet,
    });
    // AN EMPTY PANEL MUST SAY WHY. Hiding a global whose entity this page never imported is correct —
    // the workspace has no token for it — but silence here reads as "Atlas is broken", and the remedy
    // (import the entity, which this same overlay can do) is not guessable. Only when the panel is
    // EMPTY because of the gate: a partial list explains itself.
    if (!reconOptions.length && this.reconWithheldByImport.count > 0) {
      const names = [...this.reconWithheldByImport.entityIds]
        .map((id) => this.entityList?.find((e: any) => e.id === id)?.name || id);
      this.toastService.showWarning(
        `${this.reconWithheldByImport.count} variable(s) hold a ${names.join(' / ')} record, but that entity is not imported on this page. Import it to use them.`,
      );
    }
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(reconOptions));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  // ── Atlas reconstruction — SCALAR (unified set_variable / get_variable) ─────────────────────────────
  // set_variable → all scalar types; get_variable → all scalars, UNLESS it is plugged into a set_variable,
  // then only that set's picked type (get follows the connected set — a zavo unified-block behaviour, cleared
  // 2026-07-12). Scalar fields inside reference-descended entities appear via newEntityTransformedListForSelected.
  if (allowedType === 'SCALAR') {
    let types = [...SCALAR_PICKER_TYPES];
    if (this.selectedBlock.type === 'get_variable' && reconParentType === 'set_variable') {
      const par: any = this.selectedBlock.getParent();
      const setVar: any = par && this.workspace.getVariableById(par.getFieldValue('VAR'));
      const t = setVar && setVar.type; // 'String' | 'Number' | 'Boolean' | 'TimeStamp'
      if (t && NARROWABLE_BLOCKLY_TYPES.includes(t)) types = [String(t).toUpperCase()];
    }
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(this.enumerateVariablesForRecon({
      types, listSuffix: false, narrow: false,
      // set_variable (page/local scalar) is NOT in SYSTEM_SET_VISIBLE_BLOCKS → still hides system-word (a set
      // block); get_variable shows them (get). Only the entity setters were relaxed, not this scalar setter.
      hideSystem: this.hideSystemForWritePicker(),
      excludeSystemEntityFields: this.isSetContextBlock(),
    })));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  // ── Atlas reconstruction — AOB SCALAR (unified set_aob_variable / get_aob_variable) ─────────────────
  // Two-step (AOB ref → scalar field) flattened to entity_reference_for_AOB$<refId>$$<field> strings.
  // AOBToBeAddedAllowedType is 'SCALAR' for these, so all four scalar types. See enumerateAobScalarForRecon.
  if (allowedType === 'REFERENCE_ENTITY_VARIABLE_AOB'
      && (this.selectedBlock.type === 'set_aob_variable' || this.selectedBlock.type === 'get_aob_variable')) {
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(this.enumerateAobScalarForRecon(['STRING', 'NUMBER', 'BOOLEAN', 'TIMESTAMP', 'GEOMETRY'])));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  // REMOVED (2026-07-13): the old `buildAtlasData` + `pick`-mode block-picker fallback (typeMap →
  // ENTITY_VARIABLE / ENTITY_LIST_VARIABLE / SCALAR / primitives) + its same-entity `holdsEntity` re-arm.
  // DEAD — every one of those allowedTypes is handled by a reconstruct branch above (early return) or comes
  // only from a deprecated do-nothing block, so control never reached here; narrowing now lives in
  // enumerateVariablesForRecon (narrow:true). Deleted as part of retiring buildAtlasData. (git history if needed.)

  if (allowedType === 'PUSH_FIELD') {
    // Fields the value-carrying push can set on the new row: the target element's SCALAR fields,
    // plus scalars one level into each entity-reference field. System fields + already-selected hidden.
    const items: AtlasListItem[] = [];
    const used = new Set(((this.selectedBlock as any)?.fieldRows_ || []).map((r: any) => r.key));
    this.pushFieldTypes = {};
    const targetId = this.variableToBeAdded.variable;
    const refEnt = (this.pageService.entityListReferenceEntity || []).find((e: any) => e.id === targetId); // AOB element: data is an object map
    const topEnt = (this.pageService.newEntityListForSelected || []).find((e: any) => e.id === targetId);  // top-level entity: data is an array
    const isScalar = (t: string) => t === 'STRING' || t === 'NUMBER' || t === 'BOOLEAN' || t === 'TIMESTAMP' || t === 'GEOMETRY';
    const blkType = (t: string) => (t === 'STRING' ? 'String' : t === 'NUMBER' ? 'Number' : t === 'BOOLEAN' ? 'Boolean' : t === 'GEOMETRY' ? 'GEOMETRY' : 'TimeStamp');
    const add = (key: string, type: string, label: string, group: string) => {
      if (used.has(key)) return;
      if (this.isSystemVariableKey && this.isSystemVariableKey(key)) return;
      items.push({ label, value: key, group });
      this.pushFieldTypes[key] = type;
    };
    if (refEnt) {
      for (const key of Object.keys(refEnt.data || {})) {
        if (key === 'id' || key === 'id_auto_increment' || key === 'parent_id') continue;
        const ft = this.getType(refEnt.data[key]);
        if (isScalar(ft)) add(key, blkType(ft), key, refEnt.name);
        else if (ft === 'ENTITY_VARIABLE' && typeof refEnt.data[key] === 'object') {
          for (const inner of Object.keys(refEnt.data[key])) {
            const it = this.getType(refEnt.data[key][inner]);
            if (isScalar(it)) add(key + '.' + inner, blkType(it), key + ' — ' + inner, refEnt.name);
          }
        }
      }
    } else if (topEnt) {
      // From the full entity list, so a reference offers its target's fields as they are now, imported or not.
      for (const f of pushFieldsOf(topEnt.id, this.entityList)) add(f.key, blkType(f.type), f.label, topEnt.name);
    }
    // Render the SAME enumeration as a drill tree (REUSE buildReconTree; list mode retired). Each field →
    // one option: value = the dotted key `$`-encoded (own field, or reference/entity-var sub-field drills one
    // level), key = the dotted key VERBATIM (the pick + pushFieldTypes are keyed on it, UNCHANGED). Enumeration
    // (items/pushFieldTypes) is untouched — a faithful list→tree render of the existing working picker.
    const fieldOpts: ReconOption[] = items.map((it) => {
      const raw = String(it.value);
      return { value: raw.split('.').join('$'), key: raw, label: (raw.split('.').pop() || raw), type: this.pushFieldTypes[raw] || '' };
    });
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(fieldOpts));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'PUSH_FIELD';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'CONDITION_TARGET') {
    // Step 2 — FIELD drill tree of the chosen target (read entityKey back from block.data).
    if (this.blockAtlasField === 'COLUMN') {
      // Import gate = synced entity ids (buildFields only expands imported, buildable references). Used for BOTH
      // the target lookup and the field walk so the two stay symmetric (see docs/plans/condition-target-driven-routing.md).
      const importedIds = new Set((this.pageService.newEntityListForSelected || []).filter((e: any) => e.status !== 'LOCAL').map((e: any) => e.id));
      let targetData: any[] = [];
      try {
        const d = JSON.parse(this.selectedBlock.data || '{}');
        const targets = enumerateConditionTargets(this.pageService.newEntityListForSelected || [], importedIds, this.pageService.entityListForShapes);
        targetData = (targets.find((t: any) => t.key === d.entityKey) || {}).data || [];
      } catch { /* no target yet → empty field list */ }
      // add_order: no AOB-drill (you cannot order_by through an array relationship — sort an AOB by TARGETING it).
      const isOrder = this.selectedBlock.type === 'add_order';
      this.conditionPickFields = enumerateTargetFields(targetData, { importedIds, drillAob: !isOrder });
      // zavo_Sep_22: prepend "This record's ID" for CONDITIONS on an ENTITY target only — never order-by, never
      // an AOB target. Real `id` stays filtered inside enumerateTargetFields; this is the sole marked carve-out.
      // Prepend so it sits at the TOP of the field list; the index below stays the pick key.
      let selfTargetIsAob = false;
      try { selfTargetIsAob = !!JSON.parse(this.selectedBlock.data || '{}').targetIsAob; } catch { /* unconfigured */ }
      if (!isOrder && !selfTargetIsAob) this.conditionPickFields.unshift(selfIdFieldOption());
      // REUSE buildReconTree (same as the target step) — the field drill is exactly a `$`/`%`-style tree. Each
      // FieldOption → one option: value = the relative field PATH (`$`-encoded so it tokenizes into
      // own-field / reference-drill / AOB-drill), key = the INDEX into conditionPickFields (the pick handler
      // keys on it, UNCHANGED). buildReconTree merges shared prefixes: a reference is a DUAL node (pickable as
      // compare-by-id AND drillable into its sub-fields); an AOB is drillable-only (you filter a field inside
      // it, never the AOB itself), matching enumerateTargetFields, which pushes references+scalars but only
      // recurses into AOBs. AAOB fields carry type 'aaob' (the pick then reveals the systemKey/type rows).
      const fieldOpts: ReconOption[] = this.conditionPickFields.map((f, i) => ({
        value: (f.path && f.path.length ? f.path : [f.label]).join('$'),
        key: String(i),
        label: f.source === 'self' ? f.label : (f.path && f.path.length ? f.path[f.path.length - 1] : f.label),
        type: f.source === 'aaob' ? 'aaob' : '',
      }));
      this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(fieldOpts));
      this.forgeAtlasListItems = [];
      this.forgeAtlasMode = 'reconstruct';
      this.forgeAtlasOpen = true;
      return;
    }
    // Step 1 — TARGET drill tree (reconstruction). Synced entities only (LOCAL have no DB table). Shows the
    // top-level entities with their nested AOBs drilled INSIDE (product owner 2026-07-12: "condition_target
    // should show top entities, there inside AOB, for filtration"). Every node is a pickable filter target
    // carrying its own routing key: entity.id → filter that entity's rows (WHERE); AOB field-instance id →
    // trim that AOB's rows. This SUPERSEDES the earlier add_condition top-level-only restriction — AOBs are
    // now nested, selectable targets for every condition block. The field step (COLUMN) still enumerates the
    // fields relative to whichever target was chosen, so an AOB target lists the AOB's own fields.
    const syncedEntities = (this.pageService.newEntityListForSelected || []).filter((e: any) => e.status !== 'LOCAL');
    const importedIds = new Set(syncedEntities.map((e: any) => e.id)); // ref-drill gate (symmetric with the FIELD walk + buildFields)
    const targets = enumerateConditionTargets(syncedEntities, importedIds, this.pageService.entityListForShapes);
    // REUSE the query/variable pickers' tree-builder (buildReconTree) — do NOT reinvent. Feed ONE option per
    // target node (entity + every nested AOB, which enumerateConditionTargets already emits) with the dot-path
    // re-encoded as `$` so buildReconTree tokenizes it into the drill, plus the opaque routing `key`
    // (entity.id / AOB field-instance id) the pick handler keys on. Every node is pickable because it is its
    // own option's leaf; shared prefixes merge, so an entity becomes a dual node (pickable filter target AND
    // drillable into its AOBs). buildReconTree groups these DB-entity strings under the synthetic "New Entry"
    // column-0 bucket (same as the variable pickers). Note: the DATA stays enumerateConditionTargets (synced-
    // only, AAOBs excluded, depth preserved) — the query's entityNameFlatWithParent/innerEntites is the wrong
    // data for filtering (it includes AAOBs with no flag to drop them, and flattens depth).
    const targetOpts: ReconOption[] = targets.map((t) => ({
      value: t.path.split('.').join('$'),
      key: t.key,
      label: (t.path.split('.').pop() || t.label),
    }));
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(targetOpts));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'ENTITYNAME') {
    // ENTITYNAME picker for get_query + create_entity. Lists ONLY the top-level entities
    // (entityNameFlatWithParent — main's data, one entry per newEntityListForSelected entity).
    //
    // CLEARED DEVIATION FROM MAIN (product owner, 2026-07-12): main additionally showed each entity's
    // `innerEntites` as a cascaded "Sub Entity" dropdown here. That sub-entity/nested selection existed for
    // the OLD architecture (a get_query hung under another get_query via the EC/entity_conditions connection
    // to add where-conditions). Where-conditions has since been REWORKED into the imperative condition
    // builder (add_condition / condition_group / add_order — get_query no longer has condition sockets); the
    // deprecated entity_conditions/ENTITYCHILDREN path survives only for saved XML. Nested/N-level filtering
    // now lives in add_condition's CONDITION_TARGET picker (target = any tree node; field = drill path via
    // enumerateTargetFields, PARENT/TRIM toggle). So the query/create entity picker shows top-level ONLY.
    //
    // No entity/variable on the option → the pick falls through to the ENTITYNAME write-back in
    // onBlockAtlasSelect (sets VAR = selection.encodedId + setChildBlockToDefaultQuery).
    const opts: { value: string; label: string }[] = [];
    for (const entity of this.entityNameFlatWithParent || []) {
      // Built-in entities are never written by a page (zavo_bug_oct_18) — not offered to CREATE. The list is
      // shared with get_query, which keeps offering them (reads), so filter here and not where it is built.
      if (this.selectedBlock?.type === 'create_entity' && isSystemEntityRef(entity.id)) continue;
      opts.push({ value: entity.id, label: entity.name });
    }
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(opts));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'ENTITYCHILDREN') {
    // Transcribes main's ENTITYCHILDREN panel: childrenEntiesofSelectedEntity (main's data) → tree
    // (label = child name, value = child id). No entity/variable on the option → the pick falls through to
    // main's toBeAddedEntityVariableChangedForCondition in onBlockAtlasSelect (keyed on selection.encodedId).
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(
      (this.childrenEntiesofSelectedEntity || []).map((e: any) => ({ value: e.id, label: e.name }))
    ));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'ENTITYREFERENCEFORCRUD') {
    // Transcribes main's ENTITYREFERENCEFORCRUD panel: the `$`-separated reference names in
    // `entityReferenceSelectionListForCRUD` (main's data), reconstructed into a tree (split on `$`). The
    // option carries no entity/variable, so the pick falls through the recon branch to main's own handler
    // (`toBeAddedEntityVariableChanged`, keyed on selection.encodedId) in onBlockAtlasSelect.
    // Orphan guard: a nested reference is only offered when its parent reference is already configured as
    // Build inline (a linked parent carries its own children). Filter tokens via isReferenceOrphaned against
    // the current chain's modes. Breadcrumb labels come from the recon tree's own `$`-split; referenceCrumbLabel
    // stays available for flat surfaces. See docs/superpowers/specs/2026-09-08-crud-reference-fields-redesign.md §D.
    const crudChain = this.readCrudReferenceChain(this.selectedBlock);
    this.forgeAtlasData = reconTreeToAtlasData(buildReconTree(
      (this.entityReferenceSelectionListForCRUD || [])
        .filter((e: string) => !isReferenceOrphaned(e, crudChain))
        .map((e: string) => ({ value: e }))
    ));
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'AAOB_FILTER') {
    if (!this.aaobParentEntityId) return;
    const items: AtlasListItem[] = [];
    for (const entity of this.pageService.newEntityListForSelected) {
      if (entity.id === this.aaobParentEntityId) {
        for (const field of entity.data || []) {
          if (field.type === 'ARRAY_OF_OBJECT' && field.extendedAttributes === true) {
            items.push({ label: `${entity.name}.${field.name}`, value: field.name, group: entity.name });
          }
        }
      }
    }
    this.forgeAtlasData = this.reconFromListItems(items);
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'AGGREGATE_ENTITY') {
    if (this.blockAtlasField === 'COLUMN') {
      const items: AtlasListItem[] = [];
      for (const entity of this.pageService.newEntityListForSelected) {
        if (entity.id === this.variableToBeAdded.variable) {
          for (const field of entity.data || []) {
            if (field.type === 'NUMBER') {
              items.push({ label: field.name, value: entity.id + '$$' + field.name, group: entity.name });
            }
          }
        }
      }
      this.forgeAtlasData = this.reconFromListItems(items, 'flatLeaf');
      this.forgeAtlasListItems = [];
      this.forgeAtlasMode = 'reconstruct';
      this.forgeAtlasOpen = true;
      return;
    }
    const aggEntItems = (this.pageService.newEntityListForSelected || []).map(
      (e: any) => ({ label: e.name, value: e.id, group: 'Entities' })
    );
    this.forgeAtlasData = this.reconFromListItems(aggEntItems);
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'AAOB_AGGREGATE') {
    if (this.blockAtlasField === 'AAOB_FIELD') {
      const items: AtlasListItem[] = [];
      for (const entity of this.pageService.newEntityListForSelected) {
        if (entity.id === this.variableToBeAdded.variable) {
          for (const field of entity.data || []) {
            if (field.type === 'ARRAY_OF_OBJECT' && field.extendedAttributes === true) {
              items.push({ label: field.name, value: field.name, group: entity.name });
            }
          }
        }
      }
      this.forgeAtlasData = this.reconFromListItems(items);
      this.forgeAtlasListItems = [];
      this.forgeAtlasMode = 'reconstruct';
      this.forgeAtlasOpen = true;
      return;
    }
    const aaobAggEntItems = (this.pageService.newEntityListForSelected || []).map(
      (e: any) => ({ label: e.name, value: e.id, group: 'Entities' })
    );
    this.forgeAtlasData = this.reconFromListItems(aaobAggEntItems);
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'AAOB_UPSERT') {
    // AAOB key/value upsert target: two-step pick — entity, then its AAOB (extendedAttributes) field.
    // The entity may be a Database entity OR a Local/Global PAGE entity variable; the resolved
    // (entity, field) feed upsertAaobKeyValue().
    if (this.blockAtlasField === 'AAOB_FIELD') {
      const items: AtlasListItem[] = [];
      // Resolve the entity behind the picked target: a raw Database entity id, OR a local/global page
      // variable (local_variable%<var> / global_variable%<var>) -> its bound entityId.
      const picked = this.variableToBeAdded.variable || '';
      let entId = picked;
      if (picked.startsWith('local_variable%')) {
        entId = (this.pageData?.localVariable?.[picked.slice('local_variable%'.length)])?.entityId || '';
      } else if (picked.startsWith('global_variable%')) {
        entId = (this.globalVariablesService?.globalVariable?.[picked.slice('global_variable%'.length)])?.entityId || '';
      }
      const ent = (this.pageService.newEntityListForSelected || []).find((e: any) => e.id === entId);
      if (ent) {
        for (const field of ent.data || []) {
          if (field.type === 'ARRAY_OF_OBJECT' && field.extendedAttributes === true) {
            items.push({ label: field.name, value: field.name, group: ent.name });
          }
        }
      }
      this.forgeAtlasData = this.reconFromListItems(items);
      this.forgeAtlasListItems = [];
      this.forgeAtlasMode = 'reconstruct';
      this.forgeAtlasOpen = true;
      return;
    }
    // Step 1 targets: Database entities (raw id) + Local/Global PAGE entity variables bound to an entity
    // that has an AAOB field. Local/global encode as local_variable%<var> / global_variable%<var> so the
    // runtime resolves the in-memory data (matches saved AOB writes, e.g. local_variable%<X> var=<field>).
    const entityItems: AtlasListItem[] = (this.pageService.newEntityListForSelected || []).map(
      (e: any) => ({ label: e.name, value: e.id, group: 'Database entities' })
    );
    const hasAaob = (entId: string) => {
      const e = (this.pageService.newEntityListForSelected || []).find((x: any) => x.id === entId);
      return !!(e && (e.data || []).some((f: any) => f.type === 'ARRAY_OF_OBJECT' && f.extendedAttributes === true));
    };
    const addVarEntities = (map: any, prefix: string, group: string) => {
      for (const key of this.getKeys(map || {})) {
        const vv = map[key];
        if (!vv?.entityId || !hasAaob(vv.entityId)) continue;
        const e = (this.pageService.newEntityListForSelected || []).find((x: any) => x.id === vv.entityId);
        entityItems.push({ label: key + (e ? ' — ' + e.name : ''), value: prefix + key, group });
      }
    };
    addVarEntities(this.pageData?.localVariable, 'local_variable%', 'Local page entities');
    addVarEntities(this.globalVariablesService?.globalVariable, 'global_variable%', 'Global page entities');
    this.forgeAtlasData = this.reconFromListItems(entityItems);
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }

  if (allowedType === 'REFERENCE_ENTITY_VARIABLE_AOB') {
    // "Inner Variable" step = the scalar-field-inside-the-AOB picker. Gate it on 'SCALAR' specifically (not any
    // truthy AOBToBeAddedAllowedType) so the ENTITY_LIST_VARIABLE case (the reference/Target-List picker) can
    // never enter this scalar step — mirrors main, where "Inner Variable" is the scalar path only.
    if (this.blockAtlasField === 'AOB_INNER' && this.AOBToBeAddedAllowedType === 'SCALAR') {
      const items: AtlasListItem[] = [];
      for (const entity of this.pageService.entityListReferenceEntity) {
        if (entity.id === this.variableToBeAdded.variable) {
          for (const key of Object.keys(entity.data || {})) {
            if (key === 'id' || key === 'id_auto_increment') continue;
            if (this.hideSystemVariablesForSelectedAobSetBlock() && this.isSystemVariableKey(key)) continue;
            const fieldType = this.getType(entity.data[key]);
            if (fieldType === this.AOBToBeAddedAllowedType ||
                (this.AOBToBeAddedAllowedType === 'SCALAR' &&
                 (fieldType === 'STRING' || fieldType === 'NUMBER' || fieldType === 'BOOLEAN' || fieldType === 'TIMESTAMP' || fieldType === 'GEOMETRY'))) {
              items.push({ label: key, value: key, group: entity.name });
            }
            if (fieldType === 'ENTITY_VARIABLE' && typeof entity.data[key] === 'object') {
              for (const innerKey of Object.keys(entity.data[key])) {
                if (this.hideSystemVariablesForSelectedAobSetBlock() && (this.isSystemVariableKey(key) || this.isSystemVariableKey(innerKey))) continue;
                const _innerT = this.getType(entity.data[key][innerKey]);
                if (_innerT === this.AOBToBeAddedAllowedType ||
                    (this.AOBToBeAddedAllowedType === 'SCALAR' &&
                     (_innerT === 'STRING' || _innerT === 'NUMBER' || _innerT === 'BOOLEAN' || _innerT === 'TIMESTAMP' || _innerT === 'GEOMETRY'))) {
                  items.push({ label: `${key} -- ${innerKey}`, value: key + '.' + innerKey, group: entity.name });
                }
              }
            }
          }
        }
      }
      this.forgeAtlasData = this.reconFromListItems(items, 'dotDrill');
      this.forgeAtlasListItems = [];
      this.forgeAtlasMode = 'reconstruct';
      this.forgeAtlasOpen = true;
      return;
    }
    if (this.AOBToBeAddedEntity.length > 0) {
      this.forgeAtlasListItems = this.AOBToBeAddedEntity.map(
        (e: any) => ({ label: e.name, value: e.id, group: 'AOB References' })
      );
    } else {
      const items: AtlasListItem[] = [];
      const aaobBlock = this.isAaobBlock();
      // AAOB is READ-ONLY in general AOB blocks (docs/blockly/aaob-model.md / A4): a READER (any get_* AOB
      // block reaching this REFERENCE_ENTITY_VARIABLE_AOB picker) MAY select an AAOB reference (to read a
      // column's value); the WRITERS (set_*) EXCLUDE AAOB — writes go only via set_key_value_in_AOB_reference.
      // EXCEPTION: set_reference_entity_variable_for_AOB may target AAOB too — it sets a REFERENCE entity for
      // the AOB (plain OR AAOB), not the systemKey/value columns, so it does not bypass the upsert.
      const allowsBothAob = !!this.selectedBlock?.type?.startsWith('get_')
        || this.selectedBlock?.type === 'set_reference_entity_variable_for_AOB';
      for (const entity of this.pageService.entityListReferenceEntity) {
        // Dedicated AAOB blocks see ONLY AAOB; readers see both; writers exclude AAOB.
        const isAaobEntity = entity.extendedAttributes === true;
        if (!allowsBothAob && (aaobBlock ? !isAaobEntity : isAaobEntity)) continue;
        items.push({ label: entity.name, value: entity.id, group: 'AOB References' });
      }
      this.forgeAtlasListItems = items;
    }
    // Render whichever ref-entity list the branch above built (AOBToBeAddedEntity stack, or the filtered
    // entityListReferenceEntity) as the tree. Stateful 2-step (VAR → AOB_INNER) unchanged.
    this.forgeAtlasData = this.reconFromListItems(this.forgeAtlasListItems);
    this.forgeAtlasListItems = [];
    this.forgeAtlasMode = 'reconstruct';
    this.blockAtlasField = 'VAR';
    this.forgeAtlasOpen = true;
    return;
  }
}

private onBlockAtlasSelect(selection: AtlasSelection) {
  if (!this.selectedBlock) return;

  // ── Atlas reconstruction pick ──────────────────────────────────────────────────────────────────────
  // selection.value is the verbatim ReconOption. Replay its (entity, variable) into the EXISTING encoder/
  // pick handler — exactly as main's HTML (change)="toBeAddedVaraibleChanged()" — so the encoding + field
  // write are never re-invented. See docs/blockly/atlas-reconstruction.md.
  const recon = selection.value;
  if (recon && typeof recon === 'object' && typeof recon.entity === 'string' && typeof recon.variable === 'string') {
    this.variableToBeAdded.entity = recon.entity;
    this.variableToBeAdded.variable = recon.variable;
    if (typeof recon.aobVar === 'string') this.AOBToBeAddedVariable = recon.aobVar; // AOB scalar third part
    this.toBeAddedVaraibleChanged();
    // Push target changed → clear the parent push block's now-stale field rows (mirrors the old picker).
    const par: any = this.selectedBlock.getParent && this.selectedBlock.getParent();
    if (par && par.type === 'push_new_element_to_array' && par.fieldRows_ && par.fieldRows_.length && par.removeFieldRow_) {
      for (const r of par.fieldRows_.slice()) par.removeFieldRow_(r.key);
      if (par.rendered) par.render();
    }
    this.presentBlockVariable = recon.value;
    return;
  }

  const allowedType = this.variableToBeAdded.allowedType;

  const variableTypes = ['BOOLEAN', 'STRING', 'NUMBER', 'TIMESTAMP', 'ENTITY_VARIABLE', 'ENTITY_LIST_VARIABLE', 'SCALAR', 'GEOMETRY'];
  if (variableTypes.includes(allowedType)) {
    this.checkVariableandCreate(selection.encodedId, selection.type);
    this.selectedBlock.setFieldValue(selection.encodedId, 'VAR');
    if (['set_entity_list_variable', 'set_entity_variable', 'push_to_entity_list',
         'set_variable_entity', 'get_entity_length'].includes(this.selectedBlock.type)) {
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['VALUE']);
    }
    this.presentBlockVariable = selection.encodedId;
    return;
  }

  if (allowedType === 'PUSH_FIELD') {
    // Atlas field selection → auto-add a typed row to the push block (no explicit "add" button).
    const key = selection.encodedId;
    const type = (this.pushFieldTypes && this.pushFieldTypes[key]) || 'String';
    const blk: any = this.selectedBlock;
    if (blk && blk.addFieldRow_) {
      blk.addFieldRow_(key, type, key.includes('.') ? key.replace('.', ' — ') : key, false);
      if (blk.rendered) blk.render();
    }
    this.presentBlockVariable = key;
    return;
  }

  if (allowedType === 'CONDITION_TARGET') {
    const readBlockData = () => { try { return JSON.parse(this.selectedBlock.data || '{}'); } catch { return {}; } };

    if (this.blockAtlasField === 'VAR') {
      const importedIds = new Set((this.pageService.newEntityListForSelected || []).filter((e: any) => e.status !== 'LOCAL').map((e: any) => e.id));
      const targets = enumerateConditionTargets(this.pageService.newEntityListForSelected || [], importedIds, this.pageService.entityListForShapes);
      const picked = targets.find((t: any) => t.key === selection.encodedId);
      const label = picked ? picked.label : selection.encodedId;
      const d = readBlockData();
      this.selectedBlock.setFieldValue(label, 'TARGET');
      if (this.selectedBlock.type === 'add_condition' || this.selectedBlock.type === 'add_order') {
        // add_condition / add_order: store the picked TARGET; the field's relative path completes the routing.
        // targetIsAob drives the effect line (trim vs filter) — the TARGET is the PARENT/TRIM disambiguator now.
        d.entityKey = selection.encodedId;
        d.entityPath = picked ? picked.path : selection.encodedId;
        d.targetIsAob = picked ? picked.kind === 'aob' : false;
        this.selectedBlock.data = JSON.stringify(d);
        this.blockAtlasField = 'COLUMN';
        this.blockAtlasActive = false;
        this.forgeAtlasOpen = false;
        setTimeout(() => this.openAtlasForBlock(), 150);
        return;
      }
      // condition_group / clear_conditions / clear_orders — target a routing key directly (entity or AOB node).
      d.targetKey = selection.encodedId;
      d.targetLabel = label;
      d.onPath = picked ? picked.path : '';
      this.selectedBlock.data = JSON.stringify(d);
      this.presentBlockVariable = selection.encodedId;
      return;
    }

    if (this.blockAtlasField === 'COLUMN') {
      // Field chosen. The TARGET (stored as entityKey) IS the routing key; the field's path is relative to it.
      // Target the entity → an AOB field yields a nested path (PARENT: filter which parents return); target the
      // AOB → the field is its own scalar (TRIM: trim that list). No intent — the target disambiguates.
      const f: FieldOption | undefined = (this.conditionPickFields || [])[Number(selection.encodedId)];
      if (!f) return;
      const d = readBlockData();
      d.source = f.source;
      d.dataType = f.dataType;
      d.aaobField = f.aaobField || '';
      d.targetKey = d.entityKey;  // the picked target's key (entity id OR AOB field-instance id)
      d.onPath = d.entityPath;    // Forge-fallback only; hand blocks route via targetKey
      d.path = f.path;            // field path relative to the target
      d.fullPath = f.path;        // retained for any back-compat reader
      d.label = f.label;          // zavo_Sep_22: keep the display label in block.data so a future reload-time
                                  // re-match can special-case source:'self' (real `id` is filtered from enumeration)
      this.selectedBlock.data = JSON.stringify(d);
      this.selectedBlock.setFieldValue(f.label, 'FIELD');
      // AAOB key + type rows (both block types)
      const keyRow = this.selectedBlock.getInput('KEY'); // AAOB systemKey value socket
      if (keyRow) keyRow.setVisible(f.source === 'aaob');
      const typeRow = this.selectedBlock.getInput('TYPEROW'); // AAOB value-type row
      if (typeRow) typeRow.setVisible(f.source === 'aaob');
      if (f.source === 'aaob') this.selectedBlock.setFieldValue('String', 'AAOBTYPE'); // default type; user picks the real one
      const isOrder = this.selectedBlock.type === 'add_order';
      if (isOrder) {
        // add_order has no op / value / effect — routing is targetKey + path.
        if (this.selectedBlock.rendered) this.selectedBlock.render();
        this.presentBlockVariable = d.targetKey || '';
        return;
      }
      const eff = this.selectedBlock.getField('EFFECT');
      if (eff) eff.setValue(conditionEffectSentence(d));
      if (this.selectedBlock.rendered) this.selectedBlock.render();
      // Type-driven value socket: set the check, then pop any attached value block that no longer matches.
      const valInput = this.selectedBlock.getInput('VALUE');
      if (valInput) {
        const check = VALUE_CHECK_FOR_TYPE[f.dataType] || null;
        valInput.setCheck(check);
        const tb = valInput.connection && valInput.connection.targetBlock && valInput.connection.targetBlock();
        if (tb && check) {
          const outCheck = tb.outputConnection && tb.outputConnection.getCheck && tb.outputConnection.getCheck();
          const ok = !outCheck || outCheck.some((t: string) => check.includes(t));
          if (!ok) tb.unplug(true); // bump the now-incompatible value block, heal the stack
        }
      }
      this.selectedBlock.setFieldValue('_eq', 'OP');
      this.presentBlockVariable = d.targetKey || '';
      return;
    }
  }

  if (allowedType === 'ENTITYNAME') {
    const entity = this.entityNameFlatWithParent.find(e => e.id === selection.encodedId);
    if (entity && entity.innerEntites?.length > 0) {
      this.variableToBeAdded.entity = selection.encodedId;
      this.checkVariableandCreate(selection.encodedId, 'ENTITYNAME');
      this.selectedBlock.setFieldValue(selection.encodedId, 'VAR');
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['EC', 'E', 'CONDITIONS', 'SIBILING_CONDITIONS']);
    } else {
      this.variableToBeAdded.variable = selection.encodedId;
      this.variableToBeAdded.entity = selection.encodedId;
      this.checkVariableandCreate(selection.encodedId, 'ENTITYNAME');
      this.selectedBlock.setFieldValue(selection.encodedId, 'VAR');
      this.setChildBlockToDefaultQuery(this.selectedBlock, ['EC', 'E', 'CONDITIONS', 'SIBILING_CONDITIONS']);
    }
    this.presentBlockVariable = selection.encodedId;
    return;
  }

  if (allowedType === 'ENTITYCHILDREN') {
    this.variableToBeAdded.variable = selection.encodedId;
    this.toBeAddedEntityVariableChangedForCondition();
    this.presentBlockVariable = this.selectedBlock.getFieldValue('VAR') || selection.encodedId;
    return;
  }

  if (allowedType === 'ENTITYREFERENCEFORCRUD') {
    this.variableToBeAdded.variable = selection.encodedId;
    this.toBeAddedEntityVariableChanged();
    this.presentBlockVariable = selection.encodedId;
    return;
  }

  if (allowedType === 'AAOB_FILTER') {
    this.variableToBeAdded.variable = selection.encodedId;
    this.toBeAddedVaraibleChanged();
    this.presentBlockVariable = selection.encodedId;
    return;
  }

  if (allowedType === 'AGGREGATE_ENTITY') {
    if (this.blockAtlasField === 'VAR') {
      this.variableToBeAdded.variable = selection.encodedId;
      this.aggregateBlockChanged(true);
      this.blockAtlasField = 'COLUMN';
      this.blockAtlasActive = false;
      this.forgeAtlasOpen = false;
      setTimeout(() => this.openAtlasForBlock(), 150);
      return;
    }
    if (this.blockAtlasField === 'COLUMN') {
      this.aggregateColumnVar = selection.encodedId;
      this.aggregateBlockChanged(false);
      this.presentBlockVariable = this.variableToBeAdded.variable;
      return;
    }
  }

  if (allowedType === 'AAOB_AGGREGATE') {
    if (this.blockAtlasField === 'VAR') {
      this.variableToBeAdded.variable = selection.encodedId;
      this.aaobAggregateBlockChanged(true);
      this.blockAtlasField = 'AAOB_FIELD';
      this.blockAtlasActive = false;
      this.forgeAtlasOpen = false;
      setTimeout(() => this.openAtlasForBlock(), 150);
      return;
    }
    if (this.blockAtlasField === 'AAOB_FIELD') {
      this.aaobAggregateField = selection.encodedId;
      this.aaobAggregateBlockChanged(false);
      this.presentBlockVariable = this.variableToBeAdded.variable;
      return;
    }
  }

  if (allowedType === 'AAOB_UPSERT') {
    // Step 1: entity picked → write VAR, clear the AAOB field, cascade to the AAOB-field step.
    if (this.blockAtlasField === 'VAR') {
      this.variableToBeAdded.variable = selection.encodedId;
      this.checkVariableandCreate(selection.encodedId, 'ENTITYNAME');
      this.selectedBlock.setFieldValue(selection.encodedId, 'VAR');
      this.aaobUpsertField = '';
      this.selectedBlock.setFieldValue('NANAAOB_FIELD', 'AAOB_FIELD'); // entity changed → reset field
      this.blockAtlasField = 'AAOB_FIELD';
      this.blockAtlasActive = false;
      this.forgeAtlasOpen = false;
      setTimeout(() => this.openAtlasForBlock(), 150);
      return;
    }
    // Step 2: AAOB field picked → write AAOB_FIELD.
    if (this.blockAtlasField === 'AAOB_FIELD') {
      this.aaobUpsertField = selection.encodedId;
      this.checkVariableandCreate(selection.encodedId, 'AAOB_FIELD');
      this.selectedBlock.setFieldValue(selection.encodedId, 'AAOB_FIELD');
      this.presentBlockVariable = this.variableToBeAdded.variable;
      return;
    }
  }

  if (allowedType === 'REFERENCE_ENTITY_VARIABLE_AOB') {
    if (this.blockAtlasField === 'AOB_INNER') {
      this.AOBToBeAddedVariable = selection.encodedId;
      // unified AOB block (SCALAR): resolve the picked field's real type so the variable —
      // and hence the block's check/output/colour/codegen — are typed correctly.
      if (this.AOBToBeAddedAllowedType === 'SCALAR') {
        const refEntity = this.pageService.entityListReferenceEntity.find(
          (e: any) => e.id === this.variableToBeAdded.variable
        );
        let val: any = refEntity ? refEntity.data : null;
        for (const seg of String(selection.encodedId).split('.')) { val = val == null ? val : val[seg]; }
        const ft = (val !== undefined && val !== null) ? this.getType(val) : '';
        if (ft === 'STRING' || ft === 'NUMBER' || ft === 'BOOLEAN' || ft === 'TIMESTAMP' || ft === 'GEOMETRY') {
          this.AOBToBeAddedAllowedType = ft;
        }
      }
      this.toBeAddedVaraibleChanged();
      this.presentBlockVariable = this.selectedBlock.getFieldValue('VAR') || '';
      return;
    }
    this.variableToBeAdded.variable = selection.encodedId;
    this.toBeAddedVaraibleChanged();
    if (this.AOBToBeAddedAllowedType && this.AOBToBeAddedEntity.length === 0) {
      this.blockAtlasField = 'AOB_INNER';
      this.blockAtlasActive = false;
      this.forgeAtlasOpen = false;
      setTimeout(() => this.openAtlasForBlock(), 150);
      return;
    }
    this.presentBlockVariable = this.selectedBlock.getFieldValue('VAR') || '';
    return;
  }
}

/* → buildListItemsForRef moved to helpers/forge-plan-helpers.ts */

// ── On-attach same-entity guard (2026-07-10) — see docs/blockly/blocks/_VERIFICATION.md ──────────
// The guard's INSTALLER lives here (moved out of entity-variable-healper.ts, 2026-07-10) so all the
// guard logic is centralized in this component. Block inits (in the helper files) trigger it via
// `AtlasFieldBridge.attachEntityGuard`, keeping the block defs free of any component/helper coupling.
// Sets a setOnChange that, on load (FINISHED_LOADING) or a live connect (a block moved INTO this block),
// runs validateEntityChildMatch. Never throws into Blockly's change loop.
installEntityGuard(block: any): void {
  block.setOnChange((event: any) => {
    try {
      if (!block || block.isInFlyout || !block.workspace) return;
      const T: any = (Blockly as any).Events;
      const relevant = !!event && (
        event.type === T.FINISHED_LOADING ||
        (event.type === T.BLOCK_MOVE && event.newParentId === block.id)
      );
      if (!relevant) return;
      this.validateEntityChildMatch(block);
    } catch { /* never throw into Blockly's change loop */ }
  });
}

// Called from the entity setters' setOnChange (FINISHED_LOADING + a live connect, when resolution is
// reliable). Unplugs a CONFIGURED child in the VALUE socket whose entity ≠ the setter's target entity,
// or whose entity can't be resolved. Unconfigured children (VAR = NAN/'') are left so they can be
// configured; if the setter's own target entity can't be resolved we skip (can't judge). No loop:
// after the unplug the child is disconnected, so the follow-up change finds no child and no-ops.
validateEntityChildMatch(setBlock: any): void {
  try {
    // Different blocks carry the "target entity" differently:
    //  - entity setters (A2): VALUE socket + a plain entity-var VAR → resolveEntityIdOfVarField.
    //  - entity_reference_selection (A3/#13): ENTITY socket + VAR encodes `…%<referenceEntityName>_for_CRUD`.
    //  - create_entity (A6/#1): ENTITY socket ("Output Assign To Entity" destination) + VAR is the created
    //    entity TYPE (an ENTITYNAME id) directly — the destination child must hold that same type.
    const isRefSel = setBlock?.type === 'entity_reference_selection';
    const isCreate = setBlock?.type === 'create_entity';
    const child = setBlock?.getInput?.(isRefSel || isCreate ? 'ENTITY' : 'VALUE')?.connection?.targetBlock?.();
    if (!child) return;
    const childVar = child.getFieldValue?.('VAR');
    if (!childVar || childVar === 'NAN' || childVar === 'NAN_ENTITY_VARIABLE') return; // unconfigured child → leave it
    const parentVar = setBlock.getFieldValue?.('VAR');
    if (!parentVar || parentVar === 'NAN' || parentVar === 'NAN_ENTITY_VARIABLE') return; // setter not configured → nothing to match
    // Canonicalise both sides to a stable entity id before comparing. A plain-entity var resolves to
    // `.entityId`, the AOB reference setter to `referenceEntityName`, entity_reference_selection parses it
    // from the CRUD VAR, and create_entity's VAR is already an ENTITYNAME id — any of these may be a name
    // rather than an id, so comparing raw would false-unplug a valid child. Canonicalisation only merges
    // identifiers that name the SAME entity, so it can only REDUCE unplugs (safe direction).
    // See docs/blockly/fix-plan.md A2 (#10) / A3 (#13) / A6 (#1).
    const parentRaw = isRefSel
      ? this.parseReferenceEntityFromCrudVar(parentVar)
      : isCreate
        ? parentVar // create_entity VAR is the created entity TYPE (ENTITYNAME id) directly
        : this.resolveEntityIdOfVarField(parentVar);
    const parentEid = this.canonicalEntityId(parentRaw);
    if (parentEid == null) return; // can't determine target entity → don't judge (parent-side uncertainty)
    const childEid = this.canonicalEntityId(this.resolveEntityIdOfVarField(childVar));
    if (childEid == null || childEid !== parentEid) {
      child.unplug?.();
      console.warn(
        `[entity-guard] unplugged ${child.type} (entity "${childEid ?? '?'}") from ${setBlock.type} ` +
        `targeting entity "${parentEid}" — entity mismatch/unresolvable.`
      );
    }
  } catch { /* never throw into Blockly's change loop */ }
}

/** entity_reference_selection's VAR encodes its target as `…%<referenceEntityName>_for_CRUD`. */
private parseReferenceEntityFromCrudVar(v: any): string | null {
  if (!v || typeof v !== 'string') return null;
  const m = /%([^%]+?)_for_CRUD$/.exec(v);
  return m ? m[1] : null;
}

/**
 * Canonicalise an entity identifier (an `entityId`, an entity `name`, or a reference field's
 * `referenceEntityName`) to a stable entity id, so cross-encoding comparisons don't false-mismatch on
 * id-vs-name. Returns the matched entity's `id`; falls back to the input when nothing matches (so an
 * unknown identifier still compares equal to itself). Null-safe passthrough.
 */
private canonicalEntityId(x: string | null): string | null {
  if (!x) return x;
  try {
    const pool = [
      ...((this.entityList as any[]) || []),
      ...(((this.pageService as any)?.newEntityListForSelected as any[]) || []),
    ];
    const hit = pool.find(
      (e: any) => e && (e.id === x || e.name === x || e.referenceEntityName === x)
    );
    return hit?.id ?? x;
  } catch { return x; }
}

/** Resolve the entity id a block's encoded VAR field points at (null if unconfigured/unresolvable). */
private resolveEntityIdOfVarField(v: any): string | null {
  if (!v || typeof v !== 'string' || v === 'NAN' || v === 'NAN_ENTITY_VARIABLE') return null;
  try {
    if (v.startsWith('localVariable$'))
      return ((this.pageData as any)?.localVariable?.[v.slice('localVariable$'.length).split(/[.$]/)[0]]?.entityId) ?? null;
    if (v.startsWith('globalVariable$'))
      return ((this.globalVariablesService as any)?.globalVariable?.[v.slice('globalVariable$'.length).split(/[.$]/)[0]]?.entityId) ?? null;
    if (v.startsWith('local_variable%')) {
      const rest = v.slice('local_variable%'.length);
      const rootKey = rest.split(/[.$%]/)[0];
      const rootEid = (this.pageData as any)?.localVariable?.[rootKey]?.entityId ?? null;
      return this.drillReferenceFields(rootEid, rest.slice(rootKey.length)); // follow $field/%field drill (e.g. …$territoryId)
    }
    if (v.startsWith('global_variable%')) {
      const rest = v.slice('global_variable%'.length);
      const rootKey = rest.split(/[.$%]/)[0];
      const rootEid = (this.globalVariablesService as any)?.globalVariable?.[rootKey]?.entityId ?? null;
      return this.drillReferenceFields(rootEid, rest.slice(rootKey.length));
    }
    // Database-entity AOB field (<entityId>$$field) + nested-AOB forms → REUSE the existing resolver
    // (its else-branch already does the exact entity-id-token match + nested drilling). Don't re-implement.
    return this.resolveEntityIdByEntityAndVariable(v) || null;
  } catch { return null; }
}

/**
 * Follow a reference-field drill from a root entity id to the final referenced entity id, so a get on a
 * reference-descended field (e.g. `global_variable%X$territoryId`) resolves to that field's
 * `referenceEntityName` — NOT the root variable's entity. Without this the entity-guard compared the ROOT
 * entity (X) against the setter's target and false-unplugged a valid same-entity child. Stops at any
 * non-reference segment (scalar / `.data`), keeping the last resolved id. Safe direction (only reduces unplugs).
 */
private drillReferenceFields(eid: string | null, drill: string): string | null {
  if (!eid || !drill) return eid;
  try {
    for (const seg of drill.split(/[.$%]/).filter(Boolean)) {
      const ent: any = (this.entityList as any[]).find((e: any) => e && e.id === eid);
      const field: any = ent?.data?.find?.((f: any) => f.name === seg);
      if (field?.referenceEntityName) eid = field.referenceEntityName;
    }
  } catch { /* keep best-effort eid */ }
  return eid;
}

private resolveVarType(encodedId: string, ctx: PageContext): string[] {
  for (const v of [...ctx.localVariables, ...ctx.globalVariables]) {
    if (v.encodedId === encodedId) {
      return v.type ? [v.type] : [];
    }
    if (v.entityFields) {
      const f = v.entityFields.find(ef => ef.encodedId === encodedId);
      if (f) return f.type ? [f.type] : [];
    }
  }
  for (const e of [...ctx.entities, ...ctx.entityListReferences]) {
    for (const f of e.fields) {
      if (f.encodedId === encodedId) return f.type ? [f.type] : [];
    }
  }
  return [];
}

onForgeAtlasSwap(selection: AtlasSelection) {
  if (this.blockAtlasActive) {
    this.onBlockAtlasSelect(selection);
    this.blockAtlasActive = false;
    this.forgeAtlasOpen = false;
    this.forgeAtlasSwapEncodedId = null;
    this.forgeAtlasRefSegment = null;
    this.forgeAtlasMode = 'reconstruct';
    this.forgeAtlasListItems = [];
    return;
  }

  if (this.forgeAtlasRefSegment) {
    this.handleRefSwap(selection);
  } else {
    const oldId = this.forgeAtlasSwapEncodedId;
    if (!oldId) { this.forgeAtlasOpen = false; return; }

    if (this.forgePlan) {
      this.forgeSwapVariable(oldId, selection.encodedId);
    }
  }

  this.forgeAtlasOpen = false;
  this.forgeAtlasSwapEncodedId = null;
  this.forgeAtlasRefSegment = null;
  this.forgeAtlasMode = 'reconstruct';
  this.forgeAtlasListItems = [];
  this.forgeAtlasBrowseOnly = false;
}

onAtlasAddVariable(event: AtlasAddVariableEvent) {
  const BLOCKLY_TYPE: Record<string, string> = {
    'BOOLEAN': 'Boolean', 'STRING': 'String', 'NUMBER': 'Number',
    'TIMESTAMP': 'TimeStamp', 'ENTITY': 'ENTITY_VARIABLE', 'ARRAY_OF_OBJECT': 'ENTITY_LIST_VARIABLE',
    'GEOMETRY': 'GEOMETRY',
  };
  const prefix = event.scope === 'local' ? 'localVariable$' : 'globalVariable$';
  // Was (2026-09-20, follow-ups plan 4 — L9): let varName = event.name;
  // Was (2026-09-20, follow-ups plan 4 — L9): if (event.type === 'ARRAY_OF_OBJECT') varName = event.name + '.data';
  // Was (2026-09-20, follow-ups plan 4 — L9): const encodedId = prefix + varName;
  // ⚠ THE ENCODED ID USES plan.name, NOT event.name — L9. `planVariableCreate` can rename a collision (it
  // once appended a char); Blockly's registration must match the name actually written to the store, so
  // this is built AFTER the plan below, from `plan.name`, not up here from the raw event.
  // AOB list variables use the `.data` suffix in their encodedId (matches extractVariables in page-context-builder)

  // Was (2026-09-16, data binding plan 1): hydrated ENTITY `data` from newEntityTransformedListForSelected
  // inline, and checked each store with `!== undefined` inline. Both now come from variable-create.util.ts.
  // // For ENTITY/AOB variables, hydrate `data` from the entity's actual schema so that
  // // buildPageContext → extractVariables produces entityFields for drill-down in Atlas.
  // // Without this, value.data stays {} → the variable appears but has no drillable fields
  // // and disappears on context rebuild until a full pageSelected() runs.
  // if ((event.type === 'ENTITY' || event.type === 'ARRAY_OF_OBJECT') && event.value?.entityId) {
  //   const entity = (this.pageService.newEntityTransformedListForSelected || [])
  //     .find((e: any) => e.id === event.value.entityId || e.name === event.value.entityName);
  //   if (entity?.data) {
  //     if (event.type === 'ENTITY' && typeof entity.data === 'object' && !Array.isArray(entity.data)) {
  //       // ENTITY variables carry a field→defaultValue map: { fieldName: defaultValue, ... }
  //       const dataObj: Record<string, any> = {};
  //       for (const [key, val] of Object.entries(entity.data)) {
  //         dataObj[key] = val ?? '';
  //       }
  //       event.value.data = dataObj;
  //     }
  //     // AOB (ARRAY_OF_OBJECT) keeps data as [] — the entity's schema is in entityListReferenceEntity,
  //     // not in the variable's data. No hydration needed for AOB; the fields come from entity refs.
  //   }
  // }
  const value = withEntityFields(event.type, event.value, this.pageService.newEntityTransformedListForSelected || []);

  // Was (2026-09-16, data binding plan 1):
  // if (event.scope === 'local') {
  //   // Duplicate check (verbatim from page-data)
  //   if (this.pageData.localVariable[event.name] !== undefined) {
  //     this.toastService.showWarning('Variable name already exists'); return;
  //   }
  //   this.pageData.localVariable[event.name] = event.value;
  //   // Save the page (the user's warning: "creating a local variable saves all changes")
  //   this.pageService.create(this.pageData);
  // } else {
  //   const globals = this.globalVariablesService.globalVariable;
  //   if (globals[event.name] !== undefined) {
  //     this.toastService.showWarning('Variable name already exists'); return;
  //   }
  //   globals[event.name] = event.value;
  //   this.globalVariablesService.create(globals);
  // }
  // ⚠ ONE MESSAGE PER REFUSAL REASON — L8: `planVariableCreate` can refuse for four different reasons, but
  // only `name-taken` is reachable today (the form screens the rest); every reason now reads its own
  // sentence instead of `name-taken`'s wording standing in for all of them.
  const REFUSAL_TOAST: Record<string, string> = {
    'empty-name': 'Give the variable a name.',
    'unknown-type': 'Pick a type for the variable.',
    'needs-entity': 'Choose the entity this list holds.',
    'name-taken': 'Variable name already exists',
  };
  /**
   * ⚠ A REASON WITH NO SENTENCE MUST NOT TOAST `undefined` — N36. The map covers the four reasons
   * `planVariableCreate` can return today; a fifth added later would reach `showWarning(undefined)`
   * and show an empty box. The fallback is deliberately vague rather than silent: a refusal the
   * builder cannot read is still better than a refusal they cannot see.
   */
  const refusalToast = (reason: string): string =>
    REFUSAL_TOAST[reason] ?? 'That variable could not be made — check its name and type.';
  let encodedId: string;
  if (event.scope === 'local') {
    // Duplicate check (verbatim reaction from page-data)
    const plan = planVariableCreate({ name: event.name, type: event.type, value }, this.pageData.localVariable);
    // Was (2026-09-20, follow-ups plan 4 — L8): if (!plan.ok) { this.toastService.showWarning('Variable name already exists'); return; }
    if (!plan.ok) { this.toastService.showWarning(refusalToast(plan.reason)); return; }
    this.pageData.localVariable[plan.name] = plan.value;
    // Save the page (the user's warning: "creating a local variable saves all changes")
    this.pageService.create(this.pageData);
    encodedId = prefix + (event.type === 'ARRAY_OF_OBJECT' ? `${plan.name}.data` : plan.name);
  } else {
    const globals = this.globalVariablesService.globalVariable;
    const plan = planVariableCreate({ name: event.name, type: event.type, value }, globals);
    // Was (2026-09-20, follow-ups plan 4 — L8): if (!plan.ok) { this.toastService.showWarning('Variable name already exists'); return; }
    if (!plan.ok) { this.toastService.showWarning(refusalToast(plan.reason)); return; }
    globals[plan.name] = plan.value;
    this.globalVariablesService.create(globals);
    encodedId = prefix + (event.type === 'ARRAY_OF_OBJECT' ? `${plan.name}.data` : plan.name);
  }

  // Register in Blockly workspace so it's usable in blocks immediately
  // intentionalCreate = true: this is a brand-new variable, not a missing pre-registration bug
  this.checkVariableandCreate(encodedId, BLOCKLY_TYPE[event.type] || event.type, true);
  // Invalidate cached page context so the next Atlas open picks up the new variable
  this.forgePageContext = null;
  // Refresh Atlas data so the new variable appears
  this.openAtlasBrowse();
}

async onAtlasImportEntity(event: AtlasImportEntityEvent) {
  if (!event.entityId || !this.pageData) return;
  if (this.pageData.entites.includes(event.entityId)) {
    this.toastService.showWarning('Entity already imported'); return;
  }

  // 1. Add the entity AND its full ENTITY_REFERENCE chain to page.entites.
  //    A manual import of `farm` should also import `salesExecutive` if farm has a reference to it —
  //    the same transitive walk the pipeline's `entitiesForPage` does (blueprint-context.ts). Without
  //    this, a global holding a referenced entity stays invisible to Atlas (droppedUnimported §1b).
  const allEntities = this.entityList;
  const toImport = this.collectEntityChain(event.entityId, allEntities, new Set(this.pageData.entites));
  for (const id of toImport) this.pageData.entites.push(id);
  this.pageService.create(this.pageData);

  // 2. Resolve every newly imported entity into pageService lists (replicating the
  //    relevant portion of ui-editor's pageSelected() for each entity in the chain)
  for (const importedId of toImport) {
    const newEntity = allEntities.find(e => e.id === importedId);
    if (!newEntity) continue;
    // The page's imported entities hold their resolved shape, as the page load builds them.
    // Was (2026-10-29): this.pageService.newEntityListForSelected.push(newEntity);
    this.pageService.newEntityListForSelected.push(this.pageService.getEntityById(newEntity.id, allEntities));

    const transformedData = await this.pageService.createEntityData(
      this.pageService.getEntityById(newEntity.id, allEntities).data, [], allEntities
    );
    const data = { entityId: newEntity.id, id: newEntity.id, name: newEntity.name, data: transformedData };
    this.pageService.newEntityTransformedListForSelected.push(data);

    // Recursive reference-entity resolution (verbatim from ui-editor's addEntityToEntityListRecursively)
    const refEntities = this.addEntityToEntityListRecursivelyForImport(
      newEntity.data, structuredClone(this.pageService.newEntityTransformedListForSelected), true, 0, allEntities
    );
    this.pageService.entityListReferenceEntity.push(...refEntities);
    this.pageService.newEntityTransformedListForSelected = this.addEntityToEntityListRecursivelyForImport(
      newEntity.data, structuredClone(this.pageService.newEntityTransformedListForSelected), false, 0, allEntities
    );
  }

  // 3. Invalidate and refresh Atlas
  this.forgePageContext = null;
  this.openAtlasBrowse();
}

/**
 * Walk ENTITY_REFERENCE fields transitively from a root entity and return every entity ID
 * not already in `existing`. Cycle-safe: visited set prevents infinite loops (A→B→C→A).
 *
 * Same walk as the pipeline's `entitiesForPage` (blueprint-context.ts) — when a builder manually
 * imports an entity, the full reference chain is imported automatically so globals holding
 * referenced entities are visible to Atlas.
 */
private collectEntityChain(rootId: string, allEntities: Entity[], existing: Set<string>): string[] {
  const visited = new Set<string>();
  const result: string[] = [];
  const walk = (id: string) => {
    if (visited.has(id)) return;
    visited.add(id);
    if (!existing.has(id)) result.push(id);
    const entity = allEntities.find(e => e.id === id);
    if (!entity?.data) return;
    for (const f of entity.data) {
      if (f.type !== 'ENTITY_REFERENCE' || !f.referenceEntityName) continue;
      // referenceEntityName can be a name or an id — try both
      const target = allEntities.find(e => e.id === f.referenceEntityName || e.name === f.referenceEntityName);
      if (target) walk(target.id);
    }
  };
  walk(rootId);
  return result;
}

// ── Icons for Atlas gallery ──

private async refreshForgeAtlasIcons(): Promise<void> {
  // Was (2026-10-16): this.iconService.query(100);
  // Was (2026-10-16): Wait a tick for the query to populate $values
  // The icon service reads the library itself now; $values is already (or soon) populated.
  await new Promise<void>(resolve => {
    // take(1) auto-unsubscribes after the first emission. iconService.$values is a ReplaySubject, so once it
    // has a cached value .subscribe() fires SYNCHRONOUSLY — the old `const sub = …subscribe(cb)` with
    // `sub.unsubscribe()` inside cb then hit the TDZ "Cannot access 'sub' before initialization" on the 2nd+
    // Atlas open (cb ran before `sub` was assigned). take(1) removes the self-reference entirely.
    this.iconService.$values.pipe(take(1)).subscribe(icons => {
      // Was (2026-10-16): const entries: AtlasIconEntry[] = [];
      // Was (2026-10-16): const resolveAll = async () => {
      // Was (2026-10-16):   for (const icon of icons || []) {
      // Was (2026-10-16):     let thumbUrl: string | undefined;
      // Was (2026-10-16):     if (icon.path) {
      // Was (2026-10-16):       try { thumbUrl = await this.storageUrlCache.getDownloadURL(icon.path); } catch {}
      // Was (2026-10-16):     }
      // Was (2026-10-16):     entries.push({ name: icon.name, path: icon.path, thumbUrl });
      // Was (2026-10-16):   }
      // Was (2026-10-16):   this.forgeAtlasIcons = entries;
      // Was (2026-10-16): };
      // Was (2026-10-16): resolveAll();
      // A pick carries the library reference; the thumbnail is the icon itself.
      void atlasIconEntries(icons || [], (p) => this.storageUrlCache.getDownloadURL(p)).then((entries) => { this.forgeAtlasIcons = entries; });
      resolve();
    });
  });
}

async onAtlasUploadIcon(event: AtlasUploadIconEvent): Promise<void> {
  try {
    // Was (2026-10-16): const base = event.file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
    // Was (2026-10-16): const suffix = Math.random().toString(36).slice(2, 7);
    // Was (2026-10-16): const name = `${base || 'icon'}_${suffix}`;
    // Was (2026-10-16): await this.iconService.uploadIcon(event.file, name);
    // Cancelled in the preview: nothing was stored, the gallery just stops waiting.
    if (!(await this.iconService.add(event.file))) { event.done?.(); return; }
    await this.refreshForgeAtlasIcons();
    event.done?.();
  } catch (e: any) {
    console.error('[Forge] icon upload failed', e);
    event.done?.(e?.message || 'Upload failed');
  }
}


// Verbatim from ui-editor's addEntityToEntityListRecursively — needed for entity import
// since blockly-interface is a separate route and cannot access ui-editor instance.
private addEntityToEntityListRecursivelyForImport(
  entityData: TypeData[], list: { name: string; id: string; data: any }[],
  arrayOfObjectsFlag: boolean = false, indexForConsideringList: number = 0, allEntities: Entity[] = [], refPath: string[] = []
) {
  const firstList = structuredClone(list[list.length - 1]);
  let entityListReferenceEntity: any;
  if (indexForConsideringList == 0) {
    entityListReferenceEntity = [];
  } else {
    entityListReferenceEntity = list;
  }
  indexForConsideringList++;
  for (let i = 0; i < entityData.length; i++) {
    if (!arrayOfObjectsFlag && entityData[i].type == 'ENTITY_REFERENCE') {
      // One level (reference-path.util.ts) — createEntityData stopped here, so there is no data below.
      if (!followsReference(refPath, entityData[i].referenceEntityName)) continue;
      list.push({ name: firstList.name + '-' + entityData[i].name, id: firstList.id + '%' + entityData[i].name, data: firstList.data[entityData[i].name] });
      const refEntity = allEntities.find(e => e.id === entityData[i].referenceEntityName);
      // A reference to an entity that no longer exists has no fields; its stored copy is never read.
      // Was (2026-10-29): const refData = refEntity ? refEntity.data : entityData[i].data;
      const refData = refEntity ? refEntity.data : [];
      // Was (2026-10-16, self-reference): list = this.addEntityToEntityListRecursivelyForImport(refData, list, false, 0, allEntities);
      list = this.addEntityToEntityListRecursivelyForImport(refData, list, false, 0, allEntities, [...refPath, entityData[i].referenceEntityName]);
    } else if (arrayOfObjectsFlag && entityData[i].type == 'ARRAY_OF_OBJECT') {
      entityListReferenceEntity.push({
        name: list[list.length - 1].name + '-' + entityData[i].name,
        id: list[list.length - 1].id + '%' + entityData[i].name,
        data: list[list.length - 1].data[entityData[i].name][0],
        extendedAttributes: entityData[i].extendedAttributes ? true : false
      });
      entityListReferenceEntity = this.addEntityToEntityListRecursivelyForImport(
        entityData[i].data, entityListReferenceEntity, true, indexForConsideringList, allEntities, refPath
      );
    }
  }
  if (arrayOfObjectsFlag) {
    return entityListReferenceEntity;
  } else {
    return list;
  }
}

private handleRefSwap(selection: AtlasSelection) {
  if (!this.forgeAtlasRefSegment || !this.forgePlan) return;
  const seg = this.forgeAtlasRefSegment;
  const newValue = selection.encodedId;
  const refKind = seg.refKind as SegmentRefKind;

  this.forgePlan.steps = swapInSteps(this.forgePlan.steps, refKind, seg.refValue!, newValue);

  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.forgePlan.blockSteps = summarizeForgeSteps(this.forgePlan.steps, pageContext);
  this.forgePlan.variablesUsed = extractVariablesFromSteps(this.forgePlan.steps, pageContext, this.forgeUsageMap);
  this.forgePlan.entitiesUsed = extractEntitiesFromSteps(this.forgePlan.steps, pageContext);
  this.forgePlanDirty = true;
}

submitInlineEdit() {
  if (!this.forgeInlineEdit.active || !this.forgePlan) return;
  const { refKind, oldValue } = this.forgeInlineEdit;
  let newValue = this.forgeInlineEditValue;
  if (refKind === 'numberLiteral' && isNaN(Number(newValue))) return;
  if (oldValue === newValue) { this.forgeInlineEdit.active = false; return; }

  this.forgePlan.steps = swapInSteps(this.forgePlan.steps, refKind, oldValue, newValue);
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.forgePlan.blockSteps = summarizeForgeSteps(this.forgePlan.steps, pageContext);
  this.forgePlan.variablesUsed = extractVariablesFromSteps(this.forgePlan.steps, pageContext, this.forgeUsageMap);
  this.forgePlan.entitiesUsed = extractEntitiesFromSteps(this.forgePlan.steps, pageContext);
  this.forgeInlineEdit.active = false;
  this.forgePlanDirty = true;
}

cancelInlineEdit() {
  this.forgeInlineEdit.active = false;
}

getForgeStepSummary(step: ForgeStep): string {
  switch (step.action) {
    case 'SET': return `${step.target} = ${this.expressionLabel(step.value)}`;
    case 'IF': return `${step.branches.length} branch(es)${step.else ? ' + else' : ''}`;
    case 'LOOP_RANGE': return `${step.indexVar} from ${this.expressionLabel(step.from)} to ${this.expressionLabel(step.to)}`;
    case 'LOOP_FOREACH': return `each item in list`;
    case 'QUERY': return `${step.entity} → ${step.destination}`;
    case 'AGGREGATE_QUERY': return `${step.op}(${step.entity}.${step.column || '*'}) → ${step.destination}`;
    case 'CREATE_ENTITY': return `create ${step.entity}`;
    case 'UPDATE_ENTITY': return `update ${step.entityVar}`;
    case 'SET_ENTITY_FROM_LIST': return `${step.target} = ${step.listVar}[${step.indexVar}]`;
    case 'SET_AOB_REF': return `${step.target} = ${step.listVar}[${step.indexVar}]`;
    case 'CALL_FUNCTION': return step.name;
    case 'CALL_WORKFLOW': return step.name;
    case 'ASYNC': return `group ${step.group}`;
    case 'RESOLVE_ASYNC': return `wait for group ${step.group}`;
    case 'TOAST': return `${step.type}: ${this.expressionLabel(step.message)}`;
    case 'NAVIGATE': return step.page;
    case 'LOADING': return step.show ? 'show' : 'hide';
    case 'RETURN': return '';
    case 'BREAK': return '';
    case 'CONTINUE': return '';
    case 'GET_CURRENT_USER': return `→ ${step.destination}`;
    case 'LOGOUT': return '';
    default: return '';
  }
}

private expressionLabel(expr: any): string {
  if (expr === null || expr === undefined) return 'null';
  if (typeof expr === 'string') return `"${expr}"`;
  if (typeof expr === 'number' || typeof expr === 'boolean') return String(expr);
  if (expr.var) return expr.var;
  if (expr.fn) return `${expr.fn}(...)`;
  if (expr.op) return `(${expr.op})`;
  return '...';
}


// ── Phase 7: Conversation-driven plan card ─────────────────────────────

updateLivePlan() {
  // The plan is being rebuilt from the actual workspace — it now matches Blockly, so any
  // pending manual edits are either applied or discarded. Clear the dirty flag.
  this.forgePlanDirty = false;
  if (!this.functionName) {
    this.planCardMode = 'empty';
    this.forgePlan = null;
    return;
  }
  const steps = decompileWorkspace(this.workspace, this.functionName);
  if (steps.length === 0) {
    this.planCardMode = 'empty';
    this.forgePlan = null;
    return;
  }
  this.forgeUsageMap = computeVariableUsage(this.pageData);
  const pageContext = this.buildForgePageContext();
  this.forgePageContext = pageContext;
  this.forgeCurrentSteps = steps;
  this.forgePlan = {
    functionName: this.functionName,
    description: '',
    steps,
    variablesUsed: extractVariablesFromSteps(steps, pageContext, this.forgeUsageMap),
    entitiesUsed: extractEntitiesFromSteps(steps, pageContext),
    blockSteps: summarizeForgeSteps(steps, pageContext),
  };
  this.planCardMode = 'live';
}

async forgeSendMessage() {
  const prompt = this.forgePrompt.trim();
  if (!prompt) return;
  const fnName = this.functionName || this.forgeFunctionName;
  if (!fnName) return;

  this.forgeMessages.push({ role: 'user', text: prompt });
  this.forgePrompt = '';
  this.forgeChatState = 'sending';
  this.forgeProgress = '';
  this.forgeError = '';
  this.forgeAbortController = new AbortController();

  this.forgeUsageMap = computeVariableUsage(this.pageData);
  // Assigned AFTER the Atlas selection below — Atlas creates variables inside it, and a context taken
  // before would not know them (see forgeGenerate). The callbacks only run once a plan streams back.
  let pageContext!: PageContext;

  const onPlan = (steps: ForgeStep[], warnings?: string[]) => {
    this.forgePlanVersion++;
    const assistantMsg: ForgeMessage = {
      role: 'assistant',
      text: warnings?.length ? `Plan ready (${warnings.length} warning${warnings.length > 1 ? 's' : ''})` : 'Plan ready',
      steps: [...steps],
      version: this.forgePlanVersion,
    };
    this.forgeMessages.push(assistantMsg);
    this.forgeContextCutIndex = this.forgeMessages.length;
    this.forgeChatState = 'ready';

    this.forgePlan = {
      functionName: fnName,
      description: prompt,
      steps,
      variablesUsed: extractVariablesFromSteps(steps, pageContext, this.forgeUsageMap),
      entitiesUsed: extractEntitiesFromSteps(steps, pageContext),
      blockSteps: summarizeForgeSteps(steps, pageContext),
    };
    this.forgeLoadResult = warnings?.length ? { warnings, errors: [] } as any : null;
    this.planCardMode = 'preview';
    this.forgeHistoryIndex = -1;
    this.forgePlanDirty = false;
  };

  const onError = (message: string) => {
    this.forgeMessages.push({ role: 'assistant', text: `Error: ${message}` });
    this.forgeChatState = 'ready';
    this.forgeError = message;
  };

  const onProgress = (phase: ForgePhase, message: string) => {
    this.forgeProgressPhase = phase;
    this.forgeProgress = message;
  };

  // Forge is v3 — the run begins with an Atlas selection, and `pageContext` is never sent. This chat is
  // where the dropped `context_request` hurt most: forgeChatState went to 'sending' and, with no plan and
  // no error to move it, stayed there. Every branch below leaves it 'ready'.
  const onContextRequest = (requests: ForgeContextRequest[]) => {
    this.forgeContextRequests = requests;
    this.forgeMessages.push({
      role: 'assistant',
      text: requests.length
        ? `I need variables this page doesn't have:\n${requests.map(r => `• ${r.purpose} (${r.type}, ${r.scope}) — ${r.reason}`).join('\n')}`
        : 'I asked for missing context but could not say what.',
    });
    this.forgeChatState = 'ready';
  };
  const onValidation = (errors: string[], attempt: number) => {
    this.forgeProgressPhase = attempt === 0 ? 'VALIDATING' : 'FIXING';
    this.forgeProgress = errors.length
      ? `Fixing ${errors.length} validation issue${errors.length === 1 ? '' : 's'} (attempt ${attempt + 1})...`
      : 'Validating...';
  };

  const atlasContext = await this.resolveAtlasContext(prompt, fnName);
  if (!atlasContext) {
    if (this.forgeError) this.forgeMessages.push({ role: 'assistant', text: this.forgeError });
    this.forgeChatState = 'ready';
    return;
  }
  pageContext = this.buildForgePageContext();
  this.forgePageContext = pageContext;
  if (this.forgeAtlasWarnings.length) {
    this.forgeMessages.push({ role: 'assistant', text: `Note:\n${this.forgeAtlasWarnings.map(w => `• ${w}`).join('\n')}` });
  }

  const latestPlanMsg = [...this.forgeMessages].reverse().find(m => m.steps && !m.discarded);
  const currentSteps = latestPlanMsg?.steps || decompileWorkspace(this.workspace, fnName);
  if (currentSteps.length > 0) {
    const currentSummary = formatPlanAsText(currentSteps, pageContext);
    this.forgeService.streamModify(
      { prompt, currentSummary, atlasContext },
      { onProgress, onPlan, onError, onContextRequest, onValidation },
      this.forgeAbortController.signal
    );
  } else {
    this.forgeService.streamPlan(
      { prompt, functionName: fnName, atlasContext },
      { onProgress, onPlan, onError, onContextRequest, onValidation },
      this.forgeAbortController.signal
    );
  }
}

forgeDiscard(index: number) {
  const msg = this.forgeMessages[index];
  if (msg?.steps) {
    msg.discarded = true;
  }
  this.planCardMode = 'live';
  this.forgeHistoryIndex = -1;
  this.forgeContextCutIndex = this.forgeMessages.length;
  this.updateLivePlan();
}

forgeRevise() {
  const input = document.querySelector<HTMLInputElement>('.forge-chat-input');
  if (input) input.focus();
}

forgeAcceptVersion(index: number) {
  const msg = this.forgeMessages[index];
  if (!msg?.steps) return;
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.forgePlan = {
    functionName: this.functionName || this.forgeFunctionName,
    description: '',
    steps: [...msg.steps],
    variablesUsed: extractVariablesFromSteps(msg.steps, pageContext, this.forgeUsageMap),
    entitiesUsed: extractEntitiesFromSteps(msg.steps, pageContext),
    blockSteps: summarizeForgeSteps(msg.steps, pageContext),
  };
  this.planCardMode = 'preview';
  this.forgeApply();
}

forgeApply() {
  if (!this.forgePlan) return;
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.clearWorkSpace();
  this.functionName = this.forgePlan.functionName;
  this.selectedFunctionName = this.forgePlan.functionName;

  const result = loadStepsToWorkspace(this.workspace, this.forgePlan.functionName, this.forgePlan.steps, pageContext);
  this.forgeLoadResult = result;
  this.updateCode();
  this.updateLivePlan();
  this.forgePlanDirty = false;
}

forgeClickMessage(msg: ForgeMessage, index: number) {
  if (!msg.steps || msg.role !== 'assistant') return;
  const pageContext = this.forgePageContext || this.buildForgePageContext();
  this.forgeHistoryIndex = index;
  this.forgePlan = {
    functionName: this.functionName || this.forgeFunctionName,
    description: '',
    steps: [...msg.steps],
    variablesUsed: extractVariablesFromSteps(msg.steps, pageContext, this.forgeUsageMap),
    entitiesUsed: extractEntitiesFromSteps(msg.steps, pageContext),
    blockSteps: summarizeForgeSteps(msg.steps, pageContext),
  };
  this.planCardMode = 'history';
  this.forgePlanDirty = false;
}

switchPlanMode(mode: PlanCardMode) {
  if (mode === 'live') {
    this.updateLivePlan();
  }
  this.planCardMode = mode;
}

private buildForgePageContext() {
  const ctx = buildPageContext({
    localVariable: this.pageData.localVariable,
    globalVariable: this.globalVariablesService.globalVariable,
    newEntityTransformedListForSelected: this.pageService.newEntityTransformedListForSelected,
    localEntityList: this.pageService.localEntityList,
    globalEntityList: this.pageService.globalEntityList,
    entityListReferenceEntity: this.pageService.entityListReferenceEntity,
    newEntityListForSelected: this.pageService.newEntityListForSelected,
    entities: this.pageService.entityListForShapes,
    functionList: this.functionList,
    pageList: this.pageList,
    localMap: (this.pageData as any)?.atlasVariableMap,
    globalMap: (this.globalVariablesService.globalVariable as any)?.['atlasMeta']?.map,
  });
  (ctx as any).existingFunctions = (ctx as any).existingFunctions || [];
  return ctx;
}

private resizeBlocklyWorkspace() {
  setTimeout(() => {
    if (this.workspace) {
      Blockly.svgResize(this.workspace);
    }
  }, 50);
}

switchToBlockly() {
  this.forgeView = 'blockly';
  this.resizeBlocklyWorkspace();
}

/** Focus the Forge chat input (from the empty-state "Chat with Forge" button). */
focusForgeChat() {
  setTimeout(() => {
    const el = document.querySelector('.forge-chat-input') as HTMLInputElement | null;
    el?.focus();
  });
}

switchToForge() {
  this.forgeView = 'forge';
  if (this.workspace && this.functionName) {
    const steps = decompileWorkspace(this.workspace, this.functionName);
    if (steps.length > 0) {
      this.forgeUsageMap = computeVariableUsage(this.pageData);
      const pageContext = this.buildForgePageContext();
      this.forgePageContext = pageContext;
      this.forgePlan = {
        functionName: this.functionName,
        description: '',
        steps,
        variablesUsed: extractVariablesFromSteps(steps, pageContext, this.forgeUsageMap),
        entitiesUsed: extractEntitiesFromSteps(steps, pageContext),
        blockSteps: summarizeForgeSteps(steps, pageContext),
      };
      this.planCardMode = 'live';
    }
  }
}
}
